import React, { useState, useEffect, useRef } from 'react'
import { 
  // CogIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  CreditCardIcon,
  BellIcon,
  ShieldCheckIcon,
  CloudIcon,
  // PrinterIcon,
  // GlobeAltIcon,
  // DocumentTextIcon,
  CheckIcon,
  XMarkIcon,
  UserIcon,
  ComputerDesktopIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  BriefcaseIcon,
  CalendarIcon,
  CalculatorIcon,
  // StarIcon,
  CubeIcon,
  ExclamationTriangleIcon,
  BeakerIcon
} from '@heroicons/react/24/outline'
import { apiService, resolveImageUrl } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import ReceiptLayoutDesigner from '../components/Receipt/ReceiptLayoutDesigner'

// Canonical list of app pages and the permission required to access each (must match App.tsx routes)
const PAGE_PERMISSIONS: { path: string; label: string; requiredPermission: string }[] = [
  { path: '/', label: 'Dashboard', requiredPermission: 'reports.sales.read' },
  { path: '/pos', label: 'POS', requiredPermission: 'pos.orders.create' },
  { path: '/orders', label: 'Orders', requiredPermission: 'pos.orders.read' },
  { path: '/kds', label: 'Kitchen Display', requiredPermission: 'kds.view' },
  { path: '/menu', label: 'Menu Management', requiredPermission: 'menu.items.read' },
  { path: '/categories', label: 'Categories', requiredPermission: 'menu.categories.read' },
  { path: '/tables', label: 'Table Management', requiredPermission: 'tables.read' },
  { path: '/reservations', label: 'Reservations', requiredPermission: 'tables.read' },
  { path: '/waiter', label: 'Waiter Module', requiredPermission: 'waiter.orders.create' },
  { path: '/inventory', label: 'Inventory', requiredPermission: 'inventory.read' },
  { path: '/reports', label: 'Reports', requiredPermission: 'reports.sales.read' },
  { path: '/customers', label: 'Customers', requiredPermission: 'customers.read' },
  { path: '/accounting', label: 'Accounting', requiredPermission: 'accounting.accounts.read' },
  { path: '/invoices', label: 'Invoices', requiredPermission: 'accounting.invoices.manage' },
  { path: '/payments', label: 'Payments', requiredPermission: 'pos.orders.read' },
  { path: '/hr', label: 'HR Management', requiredPermission: 'hr.employees.read' },
  { path: '/settings', label: 'Settings', requiredPermission: 'settings.restaurant.read' }
]

interface RestaurantSettings {
  restaurant_name: string
  restaurant_name_ar?: string
  phone_number: string
  address: string
  address_ar?: string
  restaurant_logo_url?: string
  customer_frontend_logo_url?: string
  /** Favicon used on customer frontend, admin panel, and waiter module. */
  app_favicon_url?: string
  /** Loading icon shown while apps load (customer menu, admin, waiter). */
  app_loading_icon_url?: string
  email: string
  website: string
  google_maps_review_url?: string
  vat_number: string
  currency: string
  currency_symbol: string
  currency_display_type: 'text' | 'icon'
  currency_display_position: 'left' | 'right'
  currency_icon_url: string
  timezone: string
  language: string
  tax_rate: number
  service_charge: number
  allowance_calculation_based_on_attendance?: boolean
  waiter_order_flow?: 'direct_to_kitchen' | 'requires_approval'
  waiter_additional_items_flow?: 'direct_to_kitchen' | 'requires_approval'
  requires_approval_order_modification?: boolean
  waiter_require_customer_count?: boolean
  takeaway_floor_plan_id?: string
  catering_floor_plan_id?: string
  dual_invoicing_enabled?: boolean
  zatca_phase1_enabled?: boolean
  qr_menu_order_placing_enabled?: boolean
  /** Reservation: slot duration in minutes (15, 30, 60). Default 30. */
  reservation_timeslot_duration_minutes?: number
  /** Reservation: minimum hours in advance to book. Default 2. */
  reservation_minimum_booking_hours?: number
  /** Global: per-day schedule. Keys 0–6 (Sun–Sat), value { open, close, closed }. */
  restaurant_schedule?: Record<string, { open: string; close: string; closed: boolean }>
  /** Global: holiday dates (closed) as YYYY-MM-DD[]. */
  restaurant_holidays?: string[]
  /** Business day start (HH:mm). E.g. 16:00 = 4pm. Used for register sessions and "today" in reports. */
  business_day_start_time?: string
  /** Business day end (HH:mm). E.g. 03:00 = 3am next day. When before start, day crosses midnight. */
  business_day_end_time?: string
}

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

function normalizeRestaurantSchedule(
  raw: unknown
): Record<string, { open: string; close: string; closed: boolean }> {
  const defaultSlot = { open: '16:00', close: '22:00', closed: false }
  const out: Record<string, { open: string; close: string; closed: boolean }> = {}
  for (let i = 0; i <= 6; i++) {
    const key = String(i)
    const slot = raw && typeof raw === 'object' && key in (raw as object)
      ? (raw as Record<string, unknown>)[key]
      : null
    if (slot && typeof slot === 'object' && slot !== null && 'open' in slot && 'close' in slot) {
      const s = slot as { open?: unknown; close?: unknown; closed?: unknown }
      out[key] = {
        open: typeof s.open === 'string' ? s.open : defaultSlot.open,
        close: typeof s.close === 'string' ? s.close : defaultSlot.close,
        closed: s.closed === true || s.closed === 'true'
      }
    } else {
      out[key] = { ...defaultSlot }
    }
  }
  return out
}

interface UserSettings {
  notifications: {
    email: boolean
    sms: boolean
    push: boolean
    lowStock: boolean
    newOrders: boolean
    payments: boolean
  }
  theme: 'light' | 'dark' | 'auto'
  language: string
  timezone: string
}

interface PaymentSettings {
  methods: {
    cash: boolean
    card: boolean
    digital: boolean
  }
  taxInclusive: boolean
  autoPrintReceipt: boolean
  requireCustomerInfo: boolean
}

// interface Customer {
//   id: string
//   mobileNumber: string
//   firstName?: string
//   lastName?: string
//   email?: string
//   age?: number
//   gender?: string
//   address?: string
//   city?: string
//   state?: string
//   postalCode?: string
//   country?: string
//   dateOfBirth?: string
//   notes?: string
//   isActive: boolean
//   createdAt: string
//   updatedAt: string
// }

interface CustomerField {
  id: string
  fieldName: string
  fieldLabel: string
  fieldType: 'text' | 'email' | 'number' | 'date' | 'select' | 'textarea'
  isRequired: boolean
  isActive: boolean
  options?: string[]
  validationRules?: any
  sortOrder: number
}

interface POSSettings {
  guestCustomerName: string
  guestCustomerStartingNumber: string
  enableGuestOrdering: boolean
  autoIncrementGuestNumber: boolean
  requireMobileNumber: boolean
  enableDiscountSystem: boolean
  maxDiscountPercentage: number
  maxDiscountAmount: number
  returnDurationHours: number
  linkInventoryWithProducts: boolean
  enableQrMenu: boolean
  qrMenuOrderPlacingEnabled?: boolean
  enableWaiterModule?: boolean
  allowZeroCashRegisterOpening?: boolean
  allowDineInFromPOS?: boolean
  allowCateringOrders?: boolean
  enableTokenSystem?: boolean
  tokenStartNumber?: number
  tokenResetFrequency?: 'never' | 'daily'
  tokenMode?: 'shared' | 'separate' | 'pos_only' | 'waiter_only'
  enableAutoPrint?: boolean
  printerType?: 'tcp_ip' | 'usb' | 'network' | 'system'
  printerName?: string
  printerIp?: string
  printerPort?: number
  printerModel?: string
  printerWidth?: number
  receiptLogoUrl?: string
  receiptLayout?: any[]
  preventCancelWhenPreparing?: boolean
  kdsBumpingMode?: 'item_wise' | 'order_wise'
  kdsStartPreparingMode?: 'item_wise' | 'order_wise'
  kdsColorThresholdGreen?: number // minutes
  kdsColorThresholdYellow?: number // minutes
  zatcaPhase1Enabled?: boolean
  // Invoice Printer Settings
  invoicePrinterIp?: string
  invoicePrinterPort?: number
  invoicePrinterName?: string
  invoicePrinterWidth?: number
  invoiceEnableAutoPrint?: boolean
  // Duplicate Invoice Printer Settings
  duplicateInvoicePrinterIp?: string
  duplicateInvoicePrinterPort?: number
  duplicateInvoicePrinterName?: string
  duplicateInvoicePrinterWidth?: number
  duplicateInvoiceEnableAutoPrint?: boolean
  // Final Invoice Printer Settings
  finalInvoicePrinterIp?: string
  finalInvoicePrinterPort?: number
  finalInvoicePrinterName?: string
  finalInvoicePrinterWidth?: number
  finalInvoiceEnableAutoPrint?: boolean
  // Bill Request Printer Settings
  billRequestPrinterIp?: string
  billRequestPrinterPort?: number
  billRequestPrinterName?: string
  billRequestPrinterWidth?: number
  billRequestEnableAutoPrint?: boolean
  // Android Print Service Settings
  androidPrintServiceEnabled?: boolean
  androidPrintServiceIp?: string
  androidPrintServicePort?: number
  // KDS new order sound (only when user is on KDS screen)
  kdsNewOrderSoundEnabled?: boolean
  kdsNewOrderSoundVolume?: number
  kdsNewOrderSoundUrl?: string
  /** When true, full (100%) discounts require Credit Dinner C/o + customer name and enable reporting */
  creditDinnerEnabled?: boolean
}

interface KDSStation {
  id: string
  name: string
  description?: string
  color: string
  is_active: boolean
  sort_order: number
  printer_ip?: string
  printer_port?: number
  printer_name?: string
  enable_auto_print?: boolean
  printer_width?: number
  created_at: string
  updated_at: string
}

interface LeaveType {
  id: string
  leave_type_name: string
  max_days_per_year: number
  is_paid: boolean
  requires_approval: boolean
  carry_forward: boolean
  gender_specific?: string | null
  description?: string | null
  is_active: boolean
  created_at: string
}

interface Location {
  id: string
  name: string
  description?: string
  color: string
  sort_order: number
  is_active: boolean
  created_at: string
  updated_at: string
}

interface FloorPlan {
  id: string
  name: string
  level: number
  description?: string
  width: number
  height: number
  is_active: boolean
  sort_order: number
  created_at: string
  updated_at: string
}

const Settings: React.FC = () => {
  const { user } = useAuth()
  const [activeTab, setActiveTab] = useState<'restaurant' | 'users' | 'payments' | 'notifications' | 'integrations' | 'security' | 'pos' | 'customers' | 'kds' | 'hr' | 'inventory' | 'accounting' | 'allergens' | 'data-management'>('restaurant')
  
  // Users & Roles Management State
  const [users, setUsers] = useState<any[]>([])
  const [roles, setRoles] = useState<any[]>([])
  const [allPermissions, setPermissions] = useState<any[]>([])
  const [groupedPermissions, setGroupedPermissions] = useState<any>({})
  const [permissionsTabSelectedRoleId, setPermissionsTabSelectedRoleId] = useState<string | null>(null)
  const [permissionsTabRole, setPermissionsTabRole] = useState<any>(null)
  const [usersLoading, setUsersLoading] = useState(false)
  const [rolesLoading, setRolesLoading] = useState(false)
  const [showUserModal, setShowUserModal] = useState(false)
  const [showRoleModal, setShowRoleModal] = useState(false)
  const [showRolePermissionsModal, setShowRolePermissionsModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState<any>(null)
  const [selectedRole, setSelectedRole] = useState<any>(null)
  const [userFormData, setUserFormData] = useState({
    email: '',
    password: '',
    name: '',
    phone: '',
    role_ids: [] as string[],
    default_floor_plan_id: '' as string,
    kds_can_start_order: true,
    kds_can_mark_ready: true,
    kds_can_mark_complete: true,
    kds_station_ids: [] as string[]
  })
  const [roleFormData, setRoleFormData] = useState({
    name: '',
    description: ''
  })
  const [activeUsersSubTab, setActiveUsersSubTab] = useState<'users' | 'roles' | 'permissions'>('users')
  
  // Table Locations management state
  const [tableLocations, setTableLocations] = useState<Location[]>([])
  const [showTableLocationModal, setShowTableLocationModal] = useState(false)
  const [selectedTableLocation, setSelectedTableLocation] = useState<Location | null>(null)
  const [newTableLocation, setNewTableLocation] = useState<Partial<Location>>({ name: '', description: '', color: '#3B82F6', sort_order: 0, is_active: true })
  
  // Floor plans management state
  const [floorPlans, setFloorPlans] = useState<FloorPlan[]>([])
  const [showFloorPlanModal, setShowFloorPlanModal] = useState(false)
  const [selectedFloorPlan, setSelectedFloorPlan] = useState<FloorPlan | null>(null)
  const [newFloorPlan, setNewFloorPlan] = useState<Partial<FloorPlan>>({ name: '', level: 0, description: '', width: 2000, height: 1500, sort_order: 0, is_active: true })
  const [floorPlanKDSAssignments, setFloorPlanKDSAssignments] = useState<Record<string, any[]>>({})
  const [expandedFloorPlans, setExpandedFloorPlans] = useState<Set<string>>(new Set())
  const [categories, setCategories] = useState<Array<{ id: string; name: string; name_ar?: string; is_active: boolean }>>([])
  const [showNotificationModal, setShowNotificationModal] = useState(false)
  const [notificationMessage, setNotificationMessage] = useState({ type: 'success' as 'success' | 'error', message: '' })
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [confirmModalConfig, setConfirmModalConfig] = useState<{ message: string; onConfirm: () => void; confirmButtonText?: string; confirmButtonColor?: string } | null>(null)

  // Allergens management state
  const [allergensList, setAllergensList] = useState<Array<{ id: string; name: string; icon: string | null; sort_order: number; created_at: string; updated_at: string }>>([])
  const [showAllergenModal, setShowAllergenModal] = useState(false)
  const [editingAllergen, setEditingAllergen] = useState<{ id: string; name: string; icon: string | null; sort_order: number } | null>(null)
  const [allergenForm, setAllergenForm] = useState({ name: '', icon: '' as string | null, sort_order: 0 })
  const [allergenSaving, setAllergenSaving] = useState(false)
  
  // TOTP Authenticator State (Discount)
  const [totpStatus, setTotpStatus] = useState<{ isConfigured: boolean; authenticators: any[]; count: number } | null>(null)
  const [showTOTPSetup, setShowTOTPSetup] = useState(false)
  const [totpQRCode, setTotpQRCode] = useState<string>('')
  const [totpSecretId, setTotpSecretId] = useState<string>('')
  const [totpBackupCodes, setTotpBackupCodes] = useState<string[]>([])
  const [totpVerificationCode, setTotpVerificationCode] = useState('')
  const [isVerifyingTOTP, setIsVerifyingTOTP] = useState(false)
  const [isGeneratingTOTP, setIsGeneratingTOTP] = useState(false)
  const [showBackupCodes, setShowBackupCodes] = useState(false)
  const [newAuthenticatorName, setNewAuthenticatorName] = useState('')
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false)
  const [deletingSecretId, setDeletingSecretId] = useState<string | null>(null)
  const [showDeleteSuccessModal, setShowDeleteSuccessModal] = useState(false)
  
  // TOTP Authenticator State (Refund)
  const [refundTotpStatus, setRefundTotpStatus] = useState<{ isConfigured: boolean; authenticators: any[]; count: number } | null>(null)
  const [showRefundTOTPSetup, setShowRefundTOTPSetup] = useState(false)
  const [refundTotpQRCode, setRefundTotpQRCode] = useState<string>('')
  const [refundTotpSecretId, setRefundTotpSecretId] = useState<string>('')
  const [refundTotpBackupCodes, setRefundTotpBackupCodes] = useState<string[]>([])
  const [refundTotpVerificationCode, setRefundTotpVerificationCode] = useState('')
  const [isVerifyingRefundTOTP, setIsVerifyingRefundTOTP] = useState(false)
  const [isGeneratingRefundTOTP, setIsGeneratingRefundTOTP] = useState(false)
  const [showRefundBackupCodes, setShowRefundBackupCodes] = useState(false)
  const [newRefundAuthenticatorName, setNewRefundAuthenticatorName] = useState('')
  const [showRefundSuccessModal, setShowRefundSuccessModal] = useState(false)
  const [showRefundDeleteConfirmModal, setShowRefundDeleteConfirmModal] = useState(false)
  const [deletingRefundSecretId, setDeletingRefundSecretId] = useState<string | null>(null)
  const [showRefundDeleteSuccessModal, setShowRefundDeleteSuccessModal] = useState(false)
  
  // HR Management State
  const [leaveTypes, setLeaveTypes] = useState<LeaveType[]>([])
  const [showLeaveTypeModal, setShowLeaveTypeModal] = useState(false)
  const [editingLeaveType, setEditingLeaveType] = useState<LeaveType | null>(null)
  const [leaveTypeFormData, setLeaveTypeFormData] = useState<Partial<LeaveType>>({
    leave_type_name: '',
    max_days_per_year: 0,
    is_paid: true,
    requires_approval: true,
    carry_forward: false,
    gender_specific: null,
    description: '',
    is_active: true
  })
  const [restaurantSettings, setRestaurantSettings] = useState<RestaurantSettings>({
    restaurant_name: 'Mufaza Restaurant',
    restaurant_name_ar: '',
    phone_number: '+966 11 123 4567',
    address: '123 King Fahd Road, Riyadh, Saudi Arabia',
    address_ar: '',
    restaurant_logo_url: '',
    customer_frontend_logo_url: '',
    app_favicon_url: '',
    app_loading_icon_url: '',
    email: 'info@mufaza.com',
    website: 'www.mufaza.com',
    google_maps_review_url: '',
    vat_number: '',
    currency: 'SAR',
    currency_symbol: 'ر.س',
    currency_display_type: 'text',
    currency_display_position: 'left',
    currency_icon_url: '',
    timezone: 'Asia/Riyadh',
    language: 'English',
    tax_rate: 15,
    service_charge: 10,
    allowance_calculation_based_on_attendance: true,
    waiter_order_flow: 'direct_to_kitchen',
    waiter_additional_items_flow: 'direct_to_kitchen',
    requires_approval_order_modification: true,
    waiter_require_customer_count: false,
    dual_invoicing_enabled: false,
    zatca_phase1_enabled: false,
    takeaway_floor_plan_id: undefined,
    catering_floor_plan_id: undefined,
    business_day_start_time: '00:00',
    business_day_end_time: '23:59'
  })
  const [userSettings, setUserSettings] = useState<UserSettings>({
    notifications: {
      email: true,
      sms: false,
      push: true,
      lowStock: true,
      newOrders: true,
      payments: false
    },
    theme: 'auto',
    language: 'en',
    timezone: 'Asia/Riyadh'
  })
  const [paymentSettings, setPaymentSettings] = useState<PaymentSettings>({
    methods: {
      cash: true,
      card: true,
      digital: true
    },
    taxInclusive: true,
    autoPrintReceipt: true,
    requireCustomerInfo: false
  })
  const [saving, setSaving] = useState(false)

  // Customer Management State
  const [customerFields, setCustomerFields] = useState<CustomerField[]>([])
  // Track ZATCA toggle state separately to avoid race conditions
  const [zatcaToggleValue, setZatcaToggleValue] = useState<boolean>(false)
  
  const [posSettings, setPOSSettings] = useState<POSSettings>({
    guestCustomerName: 'Guest',
    guestCustomerStartingNumber: '10001',
    enableGuestOrdering: true,
    autoIncrementGuestNumber: true,
    requireMobileNumber: true,
    enableDiscountSystem: true,
    maxDiscountPercentage: 50,
    maxDiscountAmount: 100,
    creditDinnerEnabled: false,
    returnDurationHours: 24,
    linkInventoryWithProducts: false,
    enableQrMenu: false,
    qrMenuOrderPlacingEnabled: true,
    enableWaiterModule: true,
    allowZeroCashRegisterOpening: false,
    allowDineInFromPOS: false,
    enableTokenSystem: false,
    tokenStartNumber: 1,
    tokenResetFrequency: 'never',
    tokenMode: 'shared',
    enableAutoPrint: false,
    printerType: 'tcp_ip',
    printerName: '',
    printerIp: '',
    printerPort: 9100,
    preventCancelWhenPreparing: true,
    kdsBumpingMode: 'item_wise',
    kdsStartPreparingMode: 'item_wise',
    kdsColorThresholdGreen: 10,
    kdsColorThresholdYellow: 15,
    zatcaPhase1Enabled: false,
    printerModel: '',
    printerWidth: 80,
    // Invoice Printer Settings
    invoicePrinterIp: '',
    invoicePrinterPort: 9100,
    invoicePrinterName: '',
    invoicePrinterWidth: 80,
    invoiceEnableAutoPrint: false,
    // Duplicate Invoice Printer Settings
    duplicateInvoicePrinterIp: '',
    duplicateInvoicePrinterPort: 9100,
    duplicateInvoicePrinterName: '',
    duplicateInvoicePrinterWidth: 80,
    duplicateInvoiceEnableAutoPrint: false,
    // Final Invoice Printer Settings
    finalInvoicePrinterIp: '',
    finalInvoicePrinterPort: 9100,
    finalInvoicePrinterName: '',
    finalInvoicePrinterWidth: 80,
    finalInvoiceEnableAutoPrint: false,
    // Bill Request Printer Settings
    billRequestPrinterIp: '',
    billRequestPrinterPort: 9100,
    billRequestPrinterName: '',
    billRequestPrinterWidth: 80,
    billRequestEnableAutoPrint: false,
    receiptLogoUrl: '',
    // Android Print Service Settings
    androidPrintServiceEnabled: false,
    androidPrintServiceIp: '',
    androidPrintServicePort: 3001,
    // KDS new order sound (only when user is on KDS screen)
    kdsNewOrderSoundEnabled: false,
    kdsNewOrderSoundVolume: 70,
    kdsNewOrderSoundUrl: ''
  })
  const [showFieldModal, setShowFieldModal] = useState(false)
  const [editingField, setEditingField] = useState<CustomerField | null>(null)
  const [fieldFormData, setFieldFormData] = useState<Partial<CustomerField>>({})
  const [_loading, setLoading] = useState(false)
  
  // KDS Management State
  const [kdsStations, setKdsStations] = useState<KDSStation[]>([])
  const [showKDSModal, setShowKDSModal] = useState(false)
  const [editingKDS, setEditingKDS] = useState<KDSStation | null>(null)
  const [kdsFormData, setKdsFormData] = useState<Partial<KDSStation>>({})
  
  // Receipt Layout Designer State
  const [showLayoutDesigner, setShowLayoutDesigner] = useState(false)
  
  // Visit-Based Offers Management State
  const [visitBasedOffers, setVisitBasedOffers] = useState<any[]>([])
  const [showVisitOfferModal, setShowVisitOfferModal] = useState(false)
  const [editingVisitOffer, setEditingVisitOffer] = useState<any | null>(null)
  const [visitOfferFormData, setVisitOfferFormData] = useState<any>({
    name: '',
    description: '',
    visit_count: 1,
    offer_type: 'free_item',
    discount_value: 0,
    free_item_id: null,
    is_active: true,
    max_uses_per_customer: null,
    start_date: '',
    end_date: '',
    min_order_amount: 0
  })
  const [products, setProducts] = useState<any[]>([])
  
  // Accounting - Expense Categories Management State
  const [expenseCategories, setExpenseCategories] = useState<any[]>([])
  const [showExpenseCategoryModal, setShowExpenseCategoryModal] = useState(false)
  const [editingExpenseCategory, setEditingExpenseCategory] = useState<any | null>(null)
  const [expenseCategoryFormData, setExpenseCategoryFormData] = useState<any>({
    category_name: '',
    account_id: null,
    description: '',
    is_active: true
  })
  const [chartOfAccounts, setChartOfAccounts] = useState<any[]>([])
  
  // Inventory - Locations Management State
  const [inventoryLocations, setInventoryLocations] = useState<any[]>([])
  const [showLocationModal, setShowLocationModal] = useState(false)
  const [editingLocation, setEditingLocation] = useState<any | null>(null)
  const [locationFormData, setLocationFormData] = useState<any>({
    name: '',
    parent_id: null,
    description: '',
    is_active: true,
    sort_order: 0
  })
  
  // Inventory - Categories Management State
  const [inventoryCategories, setInventoryCategories] = useState<any[]>([])
  const [showCategoryModal, setShowCategoryModal] = useState(false)
  const [editingCategory, setEditingCategory] = useState<any | null>(null)
  const [categoryFormData, setCategoryFormData] = useState<any>({
    name: '',
    description: '',
    is_active: true,
    sort_order: 0
  })

  const tabs = [
    { key: 'restaurant', label: 'Restaurant', icon: BuildingOfficeIcon },
    { key: 'users', label: 'Users & Roles', icon: UserGroupIcon },
    { key: 'payments', label: 'Payments', icon: CreditCardIcon },
    { key: 'pos', label: 'POS Settings', icon: ComputerDesktopIcon },
    { key: 'customers', label: 'Customer Fields', icon: UserIcon },
    { key: 'kds', label: 'Kitchen Display', icon: ComputerDesktopIcon },
    { key: 'hr', label: 'HR Management', icon: BriefcaseIcon },
    { key: 'accounting', label: 'Accounting', icon: CalculatorIcon },
    { key: 'inventory', label: 'Inventory', icon: CubeIcon },
    { key: 'allergens', label: 'Allergens', icon: BeakerIcon },
    { key: 'notifications', label: 'Notifications', icon: BellIcon },
    { key: 'integrations', label: 'Integrations', icon: CloudIcon },
    { key: 'security', label: 'Security', icon: ShieldCheckIcon },
    ...(user?.roles?.some(r => r.name === 'Super Admin' || r.name === 'Admin') ? [{ key: 'data-management', label: 'Data Management', icon: TrashIcon }] : [])
  ]

  // Track ZATCA toggle state separately to avoid race conditions
  // Declare refs before useEffects to ensure hooks are called in order
  const zatcaSyncedRef = useRef(false)
  const lastZatcaValueRef = useRef<boolean | undefined>(undefined)

  useEffect(() => {
    loadData()
  }, [])

  // Sync ZATCA setting from restaurant settings to POS settings for display
  // Only sync when restaurant settings are first loaded or when explicitly changed
  // This prevents overwriting user edits to other POS settings
  
  useEffect(() => {
    const currentZatcaValue = restaurantSettings.zatca_phase1_enabled
    // Only sync if:
    // 1. Value is defined
    // 2. Either we haven't synced yet, OR the value actually changed
    if (currentZatcaValue !== undefined) {
      if (!zatcaSyncedRef.current || lastZatcaValueRef.current !== currentZatcaValue) {
        const zatcaValue = currentZatcaValue ?? false
        // Only update if the toggle value doesn't match (to avoid unnecessary updates)
        if (zatcaToggleValue !== zatcaValue) {
          setPOSSettings(prev => ({
            ...prev,
            zatcaPhase1Enabled: zatcaValue
          }))
          setZatcaToggleValue(zatcaValue)
          lastZatcaValueRef.current = currentZatcaValue
          zatcaSyncedRef.current = true

        }
      }
    }
  }, [restaurantSettings.zatca_phase1_enabled, zatcaToggleValue])

  useEffect(() => {
    if (activeTab === 'security' && user?.id) {
      loadTOTPStatus()
      loadRefundTOTPStatus()
    }
    if (activeTab === 'inventory') {
      loadInventoryLocations()
      loadInventoryCategories()
    }
    if (activeTab === 'allergens') {
      loadAllergens()
    }
    if (activeTab === 'users') {
      loadUsers()
      loadRoles()
      loadPermissions()
    }
  }, [activeTab, user?.id])

  useEffect(() => {
    if (permissionsTabSelectedRoleId) {
      loadRoleForPermissionsTab(permissionsTabSelectedRoleId)
    } else {
      setPermissionsTabRole(null)
    }
  }, [permissionsTabSelectedRoleId])

  const loadTOTPStatus = async () => {
    if (!user?.id) return
    
    try {
      const response = await apiService.getTOTPStatus(user.id, 'discount')
      if (response.success && response.data) {
        setTotpStatus(response.data as { isConfigured: boolean; authenticators: any[]; count: number })
      }
    } catch (error) {
      console.error('Error loading TOTP status:', error)
    }
  }

  const loadRefundTOTPStatus = async () => {
    if (!user?.id) return
    
    try {
      const response = await apiService.getTOTPStatus(user.id, 'refund')
      if (response.success && response.data) {
        setRefundTotpStatus(response.data as { isConfigured: boolean; authenticators: any[]; count: number })
      }
    } catch (error) {
      console.error('Error loading refund TOTP status:', error)
    }
  }

  const handleGenerateTOTP = async () => {
    if (!user?.id) {
      alert('User not authenticated')
      return
    }

    if (!newAuthenticatorName.trim()) {
      alert('Please enter a name for this authenticator')
      return
    }

    setIsGeneratingTOTP(true)
    try {
      const response = await apiService.generateTOTPSecret(user.id, newAuthenticatorName.trim(), 'discount')
      if (response.success && response.data) {
        const data = response.data as any
        setTotpQRCode(data.qrCodeUrl)
        setTotpSecretId(data.id)
        setTotpBackupCodes(data.backupCodes)
        setShowTOTPSetup(true)
        setShowBackupCodes(true)
      } else {
        alert(response.error || 'Failed to generate TOTP secret')
      }
    } catch (error) {
      console.error('Error generating TOTP:', error)
      alert('Failed to generate TOTP secret')
    } finally {
      setIsGeneratingTOTP(false)
    }
  }

  const handleGenerateRefundTOTP = async () => {
    if (!user?.id) {
      alert('User not authenticated')
      return
    }

    if (!newRefundAuthenticatorName.trim()) {
      alert('Please enter a name for this authenticator')
      return
    }

    setIsGeneratingRefundTOTP(true)
    try {
      const response = await apiService.generateTOTPSecret(user.id, newRefundAuthenticatorName.trim(), 'refund')
      if (response.success && response.data) {
        const data = response.data as any
        setRefundTotpQRCode(data.qrCodeUrl)
        setRefundTotpSecretId(data.id)
        setRefundTotpBackupCodes(data.backupCodes)
        setShowRefundTOTPSetup(true)
        setShowRefundBackupCodes(true)
      } else {
        alert(response.error || 'Failed to generate TOTP secret')
      }
    } catch (error) {
      console.error('Error generating refund TOTP:', error)
      alert('Failed to generate TOTP secret')
    } finally {
      setIsGeneratingRefundTOTP(false)
    }
  }

  const handleDeleteAuthenticator = (secretId: string) => {
    setDeletingSecretId(secretId)
    setShowDeleteConfirmModal(true)
  }

  const confirmDeleteAuthenticator = async () => {
    if (!deletingSecretId) return

    try {
      const response = await apiService.disableTOTP(deletingSecretId)
      if (response.success) {
        setShowDeleteConfirmModal(false)
        setShowDeleteSuccessModal(true)
        await loadTOTPStatus()
        setDeletingSecretId(null)
      } else {
        alert(response.error || 'Failed to delete authenticator')
        setShowDeleteConfirmModal(false)
        setDeletingSecretId(null)
      }
    } catch (error) {
      console.error('Error deleting authenticator:', error)
      alert('Failed to delete authenticator')
      setShowDeleteConfirmModal(false)
      setDeletingSecretId(null)
    }
  }

  const handleDeleteRefundAuthenticator = (secretId: string) => {
    setDeletingRefundSecretId(secretId)
    setShowRefundDeleteConfirmModal(true)
  }

  const confirmDeleteRefundAuthenticator = async () => {
    if (!deletingRefundSecretId) return

    try {
      const response = await apiService.disableTOTP(deletingRefundSecretId)
      if (response.success) {
        setShowRefundDeleteConfirmModal(false)
        setShowRefundDeleteSuccessModal(true)
        await loadRefundTOTPStatus()
        setDeletingRefundSecretId(null)
      } else {
        alert(response.error || 'Failed to delete authenticator')
        setShowRefundDeleteConfirmModal(false)
        setDeletingRefundSecretId(null)
      }
    } catch (error) {
      console.error('Error deleting refund authenticator:', error)
      alert('Failed to delete authenticator')
      setShowRefundDeleteConfirmModal(false)
      setDeletingRefundSecretId(null)
    }
  }

  const handleVerifyRefundTOTP = async () => {
    if (!user?.id || !refundTotpVerificationCode || refundTotpVerificationCode.length !== 6) {
      alert('Please enter a 6-digit verification code')
      return
    }

    setIsVerifyingRefundTOTP(true)
    try {
      const response = await apiService.verifyTOTPCode(user.id, refundTotpVerificationCode, refundTotpSecretId)

      if (response.success && (response as any).verified) {
        // Close modal and reset state first
        setShowRefundTOTPSetup(false)
        setRefundTotpVerificationCode('')
        setRefundTotpQRCode('')
        setRefundTotpSecretId('')
        setRefundTotpBackupCodes([])
        setShowRefundBackupCodes(false)
        
        // Reload TOTP status to show the new authenticator
        await loadRefundTOTPStatus()
        
        // Clear the authenticator name input after successful setup
        setNewRefundAuthenticatorName('')
        
        // Show success modal
        setShowRefundSuccessModal(true)
      } else {
        alert(response.error || 'Invalid verification code')
        setRefundTotpVerificationCode('')
      }
    } catch (error) {
      console.error('Error verifying refund TOTP:', error)
      alert('Failed to verify code')
      setRefundTotpVerificationCode('')
    } finally {
      setIsVerifyingRefundTOTP(false)
    }
  }

  const handleVerifyTOTP = async () => {
    if (!user?.id || !totpVerificationCode || totpVerificationCode.length !== 6) {
      alert('Please enter a 6-digit verification code')
      return
    }

    setIsVerifyingTOTP(true)
    try {
      const response = await apiService.verifyTOTPCode(user.id, totpVerificationCode, totpSecretId)

      if (response.success && (response as any).verified) {
        // Close modal and reset state first
        setShowTOTPSetup(false)
        setTotpVerificationCode('')
        setTotpQRCode('')
        setTotpSecretId('')
        setTotpBackupCodes([])
        setShowBackupCodes(false)
        
        // Reload TOTP status to show the new authenticator
        await loadTOTPStatus()
        
        // Clear the authenticator name input after successful setup
        setNewAuthenticatorName('')
        
        // Show success modal
        setShowSuccessModal(true)
      } else {
        alert(response.error || 'Invalid verification code')
        setTotpVerificationCode('')
      }
    } catch (error) {
      console.error('Error verifying TOTP:', error)
      alert('Failed to verify code')
      setTotpVerificationCode('')
    } finally {
      setIsVerifyingTOTP(false)
    }
  }

  const loadData = async () => {
    setLoading(true)
    try {
      await Promise.all([
        loadRestaurantSettings(),
        loadCustomerFields(),
        loadPOSSettings(),
        loadKDSStations(),
        loadCategories(),
        loadLeaveTypes(),
        loadVisitBasedOffers(),
        loadProducts(),
        loadExpenseCategories(),
        loadTableLocations(),
        loadFloorPlans(),
        loadChartOfAccounts()
      ])
    } catch (error) {
      console.error('Error loading settings data:', error)
    } finally {
      setLoading(false)
    }
  }

  // Load users, roles, and permissions
  const loadUsers = async () => {
    setUsersLoading(true)
    try {
      const response = await apiService.getUsers()
      if (response.success && response.data) {
        setUsers(Array.isArray(response.data) ? response.data : [])
      }
    } catch (error) {
      console.error('Error loading users:', error)
    } finally {
      setUsersLoading(false)
    }
  }

  const loadRoles = async () => {
    setRolesLoading(true)
    try {
      const response = await apiService.getRoles()
      if (response.success && response.data) {
        setRoles(Array.isArray(response.data) ? response.data : [])
      }
    } catch (error) {
      console.error('Error loading roles:', error)
    } finally {
      setRolesLoading(false)
    }
  }

  const loadPermissions = async () => {
    try {
      const response = await apiService.getPermissionsGrouped()
      if (response.success && response.data) {
        setGroupedPermissions(response.data || {})
      }
      
      const allPermsResponse = await apiService.getPermissions()
      if (allPermsResponse.success && allPermsResponse.data) {
        setPermissions(Array.isArray(allPermsResponse.data) ? allPermsResponse.data : [])
      }
    } catch (error) {
      console.error('Error loading permissions:', error)
    }
  }

  const loadRoleWithPermissions = async (roleId: string) => {
    try {
      const response = await apiService.getRole(roleId)
      if (response.success && response.data) {
        setSelectedRole(response.data)
        return response.data
      }
    } catch (error) {
      console.error('Error loading role:', error)
    }
    return null
  }

  const loadRoleForPermissionsTab = async (roleId: string) => {
    try {
      const response = await apiService.getRole(roleId)
      if (response.success && response.data) {
        setPermissionsTabRole(response.data)
        return response.data
      }
    } catch (error) {
      console.error('Error loading role for permissions tab:', error)
    }
    setPermissionsTabRole(null)
    return null
  }

  // Load KDS stations from database
  const loadKDSStations = async () => {
    try {
      const response = await apiService.getKDSStations()
      if (response.success && response.data) {
        setKdsStations(Array.isArray(response.data) ? response.data : [])
      }
    } catch (error) {
      console.error('Error loading KDS stations:', error)
    }
  }

  // Handle KDS station operations
  const handleAddKDS = () => {
    setEditingKDS(null)
    setKdsFormData({
      name: '',
      description: '',
      color: '#3B82F6',
      is_active: true,
      sort_order: kdsStations.length,
      printer_ip: '',
      printer_port: 9100,
      printer_name: '',
      enable_auto_print: false,
      printer_width: 80
    })
    setShowKDSModal(true)
  }

  const handleEditKDS = (station: KDSStation) => {
    setEditingKDS(station)
    setKdsFormData({
      name: station.name,
      description: station.description,
      color: station.color,
      is_active: station.is_active,
      sort_order: station.sort_order,
      printer_ip: station.printer_ip || '',
      printer_port: station.printer_port || 9100,
      printer_name: station.printer_name || '',
      enable_auto_print: station.enable_auto_print || false,
      printer_width: station.printer_width || 80
    })
    setShowKDSModal(true)
  }

  const handleSaveKDS = async () => {
    try {
      if (editingKDS) {
        const response = await apiService.updateKDSStation(editingKDS.id, kdsFormData)
        if (response.success) {
          await loadKDSStations()
          setShowKDSModal(false)
          setNotificationMessage({ type: 'success', message: 'KDS station updated successfully!' })
          setShowNotificationModal(true)
        } else {
          setNotificationMessage({ type: 'error', message: response.error || 'Error updating KDS station' })
          setShowNotificationModal(true)
        }
      } else {
        const response = await apiService.createKDSStation(kdsFormData)
        if (response.success) {
          await loadKDSStations()
          setShowKDSModal(false)
          setNotificationMessage({ type: 'success', message: 'KDS station created successfully!' })
          setShowNotificationModal(true)
        } else {
          setNotificationMessage({ type: 'error', message: response.error || 'Error creating KDS station' })
          setShowNotificationModal(true)
        }
      }
    } catch (error: any) {
      console.error('Error saving KDS station:', error)
      setNotificationMessage({ type: 'error', message: error.message || 'Error saving KDS station' })
      setShowNotificationModal(true)
    }
  }

  const handleDeleteKDS = async (station: KDSStation) => {
    setConfirmModalConfig({
      message: `Are you sure you want to delete "${station.name}"?`,
      onConfirm: async () => {
        setShowConfirmModal(false)
        try {
          const response = await apiService.deleteKDSStation(station.id)
          if (response.success) {
            await loadKDSStations()
            setNotificationMessage({ type: 'success', message: 'KDS station deleted successfully!' })
          } else {
            setNotificationMessage({ type: 'error', message: response.error || 'Error deleting KDS station' })
          }
        } catch (error) {
          console.error('Error deleting KDS station:', error)
          setNotificationMessage({ type: 'error', message: 'Error deleting KDS station' })
        }
      }
    })
    setShowConfirmModal(true)
  }

  // Load Leave Types
  const loadLeaveTypes = async () => {
    try {
      const response = await apiService.getLeaveTypes(false)
      if (response.success && response.data) {
        setLeaveTypes(Array.isArray(response.data) ? response.data : [])
      }
    } catch (error) {
      console.error('Error loading leave types:', error)
    }
  }

  // Handle Leave Type operations
  const handleAddLeaveType = () => {
    setEditingLeaveType(null)
    setLeaveTypeFormData({
      leave_type_name: '',
      max_days_per_year: 0,
      is_paid: true,
      requires_approval: true,
      carry_forward: false,
      gender_specific: null,
      description: '',
      is_active: true
    })
    setShowLeaveTypeModal(true)
  }

  const handleEditLeaveType = (leaveType: LeaveType) => {
    setEditingLeaveType(leaveType)
    setLeaveTypeFormData({
      leave_type_name: leaveType.leave_type_name,
      max_days_per_year: leaveType.max_days_per_year,
      is_paid: leaveType.is_paid,
      requires_approval: leaveType.requires_approval,
      carry_forward: leaveType.carry_forward,
      gender_specific: leaveType.gender_specific,
      description: leaveType.description || '',
      is_active: leaveType.is_active
    })
    setShowLeaveTypeModal(true)
  }

  const handleSaveLeaveType = async () => {
    if (!leaveTypeFormData.leave_type_name || !leaveTypeFormData.max_days_per_year) {
      setNotificationMessage({ type: 'error', message: 'Please fill in all required fields' })
      setShowNotificationModal(true)
      return
    }

    try {
      if (editingLeaveType) {
        const response = await apiService.updateLeaveType(editingLeaveType.id, leaveTypeFormData)
        if (response.success) {
          await loadLeaveTypes()
          setShowLeaveTypeModal(false)
          setNotificationMessage({ type: 'success', message: 'Leave type updated successfully!' })
          setShowNotificationModal(true)
        } else {
          setNotificationMessage({ type: 'error', message: response.error || 'Error updating leave type' })
          setShowNotificationModal(true)
        }
      } else {
        const response = await apiService.createLeaveType(leaveTypeFormData)
        if (response.success) {
          await loadLeaveTypes()
          setShowLeaveTypeModal(false)
          setNotificationMessage({ type: 'success', message: 'Leave type created successfully!' })
          setShowNotificationModal(true)
        } else {
          setNotificationMessage({ type: 'error', message: response.error || 'Error creating leave type' })
          setShowNotificationModal(true)
        }
      }
    } catch (error: any) {
      console.error('Error saving leave type:', error)
      setNotificationMessage({ type: 'error', message: error.message || 'Error saving leave type' })
      setShowNotificationModal(true)
    }
  }

  const handleDeleteLeaveType = async (leaveType: LeaveType) => {
    setConfirmModalConfig({
      message: `Are you sure you want to ${leaveType.is_active ? 'deactivate' : 'delete'} "${leaveType.leave_type_name}"?`,
      onConfirm: async () => {
        setShowConfirmModal(false)
        try {
          const response = await apiService.deleteLeaveType(leaveType.id)
          if (response.success) {
            await loadLeaveTypes()
            setNotificationMessage({ type: 'success', message: (response as any).message || 'Leave type deleted successfully!' })
          } else {
            setNotificationMessage({ type: 'error', message: response.error || 'Error deleting leave type' })
          }
        } catch (error: any) {
          console.error('Error deleting leave type:', error)
          setNotificationMessage({ type: 'error', message: error.message || 'Error deleting leave type' })
        }
      }
    })
    setShowConfirmModal(true)
  }

  // Load restaurant settings from database
  const loadRestaurantSettings = async () => {
    try {
      const response = await apiService.getRestaurantSettings()
      if (response.success && response.data) {
        // Ensure waiter_order_flow has a default value if not set
        const data = response.data as any


        
        // Helper function to convert to boolean
        const toBoolean = (value: any): boolean => {
          if (typeof value === 'boolean') return value
          if (typeof value === 'string') return value === 'true' || value === '1'
          return false
        }
        
        const settings: RestaurantSettings = {
          restaurant_name: data.restaurant_name || '',
          restaurant_name_ar: data.restaurant_name_ar || '',
          phone_number: data.phone_number || '',
          address: data.address || '',
          address_ar: data.address_ar || '',
          restaurant_logo_url: data.restaurant_logo_url || '',
          customer_frontend_logo_url: data.customer_frontend_logo_url || '',
          app_favicon_url: data.app_favicon_url || '',
          app_loading_icon_url: data.app_loading_icon_url || '',
          email: data.email || '',
          website: data.website || '',
          google_maps_review_url: data.google_maps_review_url || '',
          vat_number: data.vat_number || '',
          currency: data.currency || 'SAR',
          currency_symbol: data.currency_symbol || 'ر.س',
          currency_display_type: data.currency_display_type || 'text',
          currency_display_position: data.currency_display_position || 'left',
          currency_icon_url: data.currency_icon_url || '',
          timezone: data.timezone || 'Asia/Riyadh',
          language: data.language || 'English',
          tax_rate: (data.tax_rate !== undefined && data.tax_rate !== null) ? Number(data.tax_rate) : 15,
          service_charge: (data.service_charge !== undefined && data.service_charge !== null) ? Number(data.service_charge) : 10,
          allowance_calculation_based_on_attendance: toBoolean(data.allowance_calculation_based_on_attendance),
          waiter_order_flow: data.waiter_order_flow || 'direct_to_kitchen',
          waiter_additional_items_flow: data.waiter_additional_items_flow || 'direct_to_kitchen',
          requires_approval_order_modification: toBoolean(data.requires_approval_order_modification !== false),
          waiter_require_customer_count: toBoolean(data.waiter_require_customer_count),
          dual_invoicing_enabled: toBoolean(data.dual_invoicing_enabled),
          zatca_phase1_enabled: toBoolean(data.zatca_phase1_enabled),
          qr_menu_order_placing_enabled: toBoolean(data.qr_menu_order_placing_enabled),
          reservation_timeslot_duration_minutes: typeof data.reservation_timeslot_duration_minutes === 'number' ? data.reservation_timeslot_duration_minutes : (data.reservation_timeslot_duration_minutes != null ? Number(data.reservation_timeslot_duration_minutes) : 30),
          reservation_minimum_booking_hours: typeof data.reservation_minimum_booking_hours === 'number' ? data.reservation_minimum_booking_hours : (data.reservation_minimum_booking_hours != null ? Number(data.reservation_minimum_booking_hours) : 2),
          restaurant_schedule: normalizeRestaurantSchedule(data.restaurant_schedule),
          restaurant_holidays: Array.isArray(data.restaurant_holidays) ? data.restaurant_holidays.filter((d: unknown): d is string => typeof d === 'string') : [],
          takeaway_floor_plan_id: data.takeaway_floor_plan_id || undefined,
          catering_floor_plan_id: data.catering_floor_plan_id || undefined,
          business_day_start_time: data.business_day_start_time || '00:00',
          business_day_end_time: data.business_day_end_time || '23:59'
        }
        

        
        // Sync qr_menu_order_placing_enabled to POS settings
        if (settings.qr_menu_order_placing_enabled !== undefined) {
          setPOSSettings(prev => ({
            ...prev,
            qrMenuOrderPlacingEnabled: settings.qr_menu_order_placing_enabled
          }))
        }
        setRestaurantSettings(settings)
        // Reset ZATCA sync ref when we reload restaurant settings if value changed
        if (zatcaSyncedRef.current && lastZatcaValueRef.current !== settings.zatca_phase1_enabled) {
          zatcaSyncedRef.current = false
        }
      }
    } catch (error) {
      console.error('Error loading restaurant settings:', error)
    }
  }

  // Save restaurant settings to database
  const handleSaveRestaurantSettings = async () => {
    setSaving(true)
    try {
      // Ensure all settings are included; explicitly set numeric/boolean so they are never omitted by JSON.stringify (undefined keys are stripped)
      const settingsToSave = {
        ...restaurantSettings,
        tax_rate: Number(restaurantSettings.tax_rate) || 0,
        service_charge: Number(restaurantSettings.service_charge) ?? 0,
        zatca_phase1_enabled: restaurantSettings.zatca_phase1_enabled ?? false,
        dual_invoicing_enabled: restaurantSettings.dual_invoicing_enabled ?? false,
        requires_approval_order_modification: restaurantSettings.requires_approval_order_modification !== false
      }
      


      
      const response = await apiService.updateRestaurantSettings(settingsToSave)
      if (response.success) {
        setNotificationMessage({ type: 'success', message: 'Restaurant settings saved successfully!' })
        setShowNotificationModal(true)
        // Clear cache so reload fetches fresh data from server
        apiService.clearCache('/restaurant-settings')
        await loadRestaurantSettings()
      } else {
        console.error('Save failed:', response.error)
        setNotificationMessage({ type: 'error', message: `Error saving restaurant settings: ${response.error || 'Unknown error'}` })
        setShowNotificationModal(true)
      }
    } catch (error) {
      console.error('Error saving restaurant settings:', error)
      const errMsg = error instanceof Error ? error.message : 'Error saving restaurant settings'
      setNotificationMessage({ type: 'error', message: errMsg })
      setShowNotificationModal(true)
    } finally {
      setSaving(false)
    }
  }

  const loadCustomerFields = async () => {
    try {
      const response = await apiService.getCustomerFields()
      if (response.success && response.data && Array.isArray(response.data)) {
        setCustomerFields(response.data as CustomerField[])
      }
    } catch (error) {
      console.error('Error loading customer fields:', error)
    }
  }

  const loadPOSSettings = async () => {
    try {
      const response = await apiService.getPOSSettings()

      if (response.success && response.data) {
        const data = response.data as any
        
        // Try to get values from either camelCase or snake_case
        const getValue = (camelKey: string, snakeKey: string) => {
          if (camelKey in data && data[camelKey] !== undefined) {
            return data[camelKey]
          }
          if (snakeKey in data && data[snakeKey] !== undefined) {
            return data[snakeKey]
          }
          return undefined
        }
        
        // Helper to safely convert to number - only use default if truly missing
        const toNumber = (value: any, defaultValue: number, _keyName: string): number => {
          // Check if value exists in the data object (even if it's 0, null, or empty string)
          if (value === undefined) {

            return defaultValue
          }
          // 0 is a valid value, so don't treat it as missing
          if (value === null || value === '') {

            return defaultValue
          }
          const num = typeof value === 'string' ? parseFloat(value) : Number(value)
          if (isNaN(num)) {
            return defaultValue
          }
          return num
        }
        
        // Log what we're about to set - try both camelCase and snake_case
        const maxDiscountPercentageRaw = getValue('maxDiscountPercentage', 'max_discount_percentage')
        const maxDiscountAmountRaw = getValue('maxDiscountAmount', 'max_discount_amount')
        const returnDurationHoursRaw = getValue('returnDurationHours', 'return_duration_hours')
        
        const maxDiscountPercentage = toNumber(maxDiscountPercentageRaw, 50, 'maxDiscountPercentage')
        const maxDiscountAmount = toNumber(maxDiscountAmountRaw, 100, 'maxDiscountAmount')
        const returnDurationHours = toNumber(returnDurationHoursRaw, 24, 'returnDurationHours')
        
        
        // CRITICAL: Only warn if values are truly missing (undefined), not if they just happen to equal defaults
        const isMaxDiscountPercentageMissing = maxDiscountPercentageRaw === undefined
        const isMaxDiscountAmountMissing = maxDiscountAmountRaw === undefined
        const isReturnDurationHoursMissing = returnDurationHoursRaw === undefined
        
        if (isMaxDiscountPercentageMissing || isMaxDiscountAmountMissing || isReturnDurationHoursMissing) {
          console.warn('⚠️ Some settings values were not found in the data object and default values were used.')
          if (isMaxDiscountPercentageMissing) console.warn('  - maxDiscountPercentage/max_discount_percentage not found')
          if (isMaxDiscountAmountMissing) console.warn('  - maxDiscountAmount/max_discount_amount not found')
          if (isReturnDurationHoursMissing) console.warn('  - returnDurationHours/return_duration_hours not found')
          console.warn('Available keys in data:', Object.keys(data))
        }
        
        setPOSSettings({
          guestCustomerName: data.guestCustomerName || 'Guest',
          guestCustomerStartingNumber: data.guestCustomerStartingNumber || '10001',
          enableGuestOrdering: data.enableGuestOrdering ?? true,
          autoIncrementGuestNumber: data.autoIncrementGuestNumber ?? true,
          requireMobileNumber: data.requireMobileNumber ?? false,
          enableDiscountSystem: data.enableDiscountSystem ?? true,
          maxDiscountPercentage,
          maxDiscountAmount,
          returnDurationHours,
          linkInventoryWithProducts: data.linkInventoryWithProducts ?? false,
          enableQrMenu: data.enableQrMenu ?? false,
          qrMenuOrderPlacingEnabled: data.qrMenuOrderPlacingEnabled ?? true,
      enableWaiterModule: data.enableWaiterModule ?? true,
          allowZeroCashRegisterOpening: data.allowZeroCashRegisterOpening ?? false,
          allowDineInFromPOS: getValue('allowDineInFromPOS', 'allow_dine_in_from_pos') ?? false,
          allowCateringOrders: getValue('allowCateringOrders', 'allow_catering_orders') ?? false,
          enableTokenSystem: data.enableTokenSystem ?? false,
          tokenStartNumber: data.tokenStartNumber ?? 1,
          tokenResetFrequency: data.tokenResetFrequency || 'never',
          tokenMode: data.tokenMode || 'shared',
          enableAutoPrint: data.enableAutoPrint ?? false,
          printerType: data.printerType || 'tcp_ip',
          printerName: data.printerName || '',
          printerIp: data.printerIp || '',
          printerPort: data.printerPort || 9100,
          printerModel: data.printerModel || '',
          printerWidth: data.printerWidth || 80,
          receiptLogoUrl: data.receiptLogoUrl || '',
          receiptLayout: data.receiptLayout || [],
          preventCancelWhenPreparing: data.preventCancelWhenPreparing ?? true,
          kdsBumpingMode: data.kdsBumpingMode || 'item_wise',
          kdsStartPreparingMode: data.kdsStartPreparingMode || 'item_wise',
          kdsColorThresholdGreen: data.kdsColorThresholdGreen ?? 10,
          kdsColorThresholdYellow: data.kdsColorThresholdYellow ?? 15,
          // ZATCA is stored in restaurant_settings, so we'll load it from there
          zatcaPhase1Enabled: false, // Will be set from restaurant settings
          // Invoice Printer Settings
          invoicePrinterIp: data.invoicePrinterIp || '',
          invoicePrinterPort: data.invoicePrinterPort || 9100,
          invoicePrinterName: data.invoicePrinterName || '',
          invoicePrinterWidth: data.invoicePrinterWidth || 80,
          invoiceEnableAutoPrint: data.invoiceEnableAutoPrint ?? false,
          // Duplicate Invoice Printer Settings
          duplicateInvoicePrinterIp: data.duplicateInvoicePrinterIp || '',
          duplicateInvoicePrinterPort: data.duplicateInvoicePrinterPort || 9100,
          duplicateInvoicePrinterName: data.duplicateInvoicePrinterName || '',
          duplicateInvoicePrinterWidth: data.duplicateInvoicePrinterWidth || 80,
          duplicateInvoiceEnableAutoPrint: data.duplicateInvoiceEnableAutoPrint ?? false,
          // Final Invoice Printer Settings
          finalInvoicePrinterIp: data.finalInvoicePrinterIp || '',
          finalInvoicePrinterPort: data.finalInvoicePrinterPort || 9100,
          finalInvoicePrinterName: data.finalInvoicePrinterName || '',
          finalInvoicePrinterWidth: data.finalInvoicePrinterWidth || 80,
          finalInvoiceEnableAutoPrint: data.finalInvoiceEnableAutoPrint ?? false,
          // Bill Request Printer Settings
          billRequestPrinterIp: data.billRequestPrinterIp || '',
          billRequestPrinterPort: data.billRequestPrinterPort || 9100,
          billRequestPrinterName: data.billRequestPrinterName || '',
          billRequestPrinterWidth: data.billRequestPrinterWidth || 80,
          billRequestEnableAutoPrint: data.billRequestEnableAutoPrint ?? false,
          // Android Print Service Settings
          androidPrintServiceEnabled: data.androidPrintServiceEnabled ?? false,
          androidPrintServiceIp: data.androidPrintServiceIp || '',
          androidPrintServicePort: data.androidPrintServicePort || 3001,
          // KDS new order sound
          kdsNewOrderSoundEnabled: data.kdsNewOrderSoundEnabled ?? false,
          kdsNewOrderSoundVolume: data.kdsNewOrderSoundVolume ?? 70,
          kdsNewOrderSoundUrl: data.kdsNewOrderSoundUrl || '',
          creditDinnerEnabled: data.creditDinnerEnabled ?? false
        })
      }
    } catch (error) {
      console.error('Error loading POS settings:', error)
    }
  }

  // Load visit-based offers
  const loadVisitBasedOffers = async () => {
    try {
      const response = await apiService.getVisitBasedOffers()
      if (response.success && response.data) {
        setVisitBasedOffers(Array.isArray(response.data) ? response.data : [])
      }
    } catch (error) {
      console.error('Error loading visit-based offers:', error)
    }
  }

  // Load products for free item selection
  const loadProducts = async () => {
    try {
      const response = await apiService.getMenuItems()
      if (response.success && response.data) {
        setProducts(Array.isArray(response.data) ? response.data : [])
      }
    } catch (error) {
      console.error('Error loading products:', error)
    }
  }

  // Handle visit-based offer operations
  const handleAddVisitOffer = () => {
    setEditingVisitOffer(null)
    setVisitOfferFormData({
      name: '',
      description: '',
      visit_count: 1,
      offer_type: 'free_item',
      discount_value: 0,
      free_item_id: null,
      is_active: true,
      max_uses_per_customer: null,
      start_date: '',
      end_date: '',
      min_order_amount: 0
    })
    setShowVisitOfferModal(true)
  }

  const handleEditVisitOffer = (offer: any) => {
    setEditingVisitOffer(offer)
    setVisitOfferFormData({
      name: offer.name || '',
      description: offer.description || '',
      visit_count: offer.visit_count || 1,
      offer_type: offer.offer_type || 'free_item',
      discount_value: offer.discount_value || 0,
      free_item_id: offer.free_item_id || null,
      is_active: offer.is_active !== undefined ? offer.is_active : true,
      max_uses_per_customer: offer.max_uses_per_customer || null,
      start_date: offer.start_date || '',
      end_date: offer.end_date || '',
      min_order_amount: offer.min_order_amount || 0
    })
    setShowVisitOfferModal(true)
  }

  const handleSaveVisitOffer = async () => {
    try {
      if (editingVisitOffer) {
        const response = await apiService.updateVisitBasedOffer(editingVisitOffer.id, visitOfferFormData)
        if (response.success) {
          await loadVisitBasedOffers()
          setShowVisitOfferModal(false)
          alert('Visit-based offer updated successfully!')
        }
      } else {
        const response = await apiService.createVisitBasedOffer(visitOfferFormData)
        if (response.success) {
          await loadVisitBasedOffers()
          setShowVisitOfferModal(false)
          alert('Visit-based offer created successfully!')
        }
      }
    } catch (error) {
      console.error('Error saving visit-based offer:', error)
      alert('Error saving visit-based offer')
    }
  }

  const handleDeleteVisitOffer = async (id: string) => {
    setConfirmModalConfig({
      message: 'Are you sure you want to delete this visit-based offer?',
      onConfirm: async () => {
        setShowConfirmModal(false)
        try {
          const response = await apiService.deleteVisitBasedOffer(id)
          if (response.success) {
            await loadVisitBasedOffers()
            setNotificationMessage({ type: 'success', message: 'Visit-based offer deleted successfully!' })
          } else {
            setNotificationMessage({ type: 'error', message: response.error || 'Error deleting visit-based offer' })
          }
        } catch (error) {
          console.error('Error deleting visit-based offer:', error)
          setNotificationMessage({ type: 'error', message: 'Error deleting visit-based offer' })
        }
      }
    })
    setShowConfirmModal(true)
  }

  // Load expense categories
  const loadExpenseCategories = async () => {
    try {
      const response = await apiService.getExpenseCategories()
      if (response.success && response.data) {
        setExpenseCategories(Array.isArray(response.data) ? response.data : [])
      }
    } catch (error) {
      console.error('Error loading expense categories:', error)
    }
  }

  // Load chart of accounts for expense category dropdown
  const loadChartOfAccounts = async () => {
    try {
      const response = await apiService.getChartOfAccounts()
      if (response.success && response.data) {
        setChartOfAccounts(Array.isArray(response.data) ? response.data : [])
      }
    } catch (error) {
      console.error('Error loading chart of accounts:', error)
    }
  }

  // Table Locations management functions
  const loadTableLocations = async () => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
      const response = await fetch(`${apiUrl}/locations/all`)
      const data = await response.json()
      if (data.success) {
        setTableLocations(data.data || [])
      }
    } catch (error) {
      console.error('Error loading table locations:', error)
    }
  }

  const handleSaveTableLocation = async () => {
    if (!newTableLocation.name) {
      alert('Please enter a location name')
      return
    }

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
      const url = selectedTableLocation 
        ? `${apiUrl}/locations/${selectedTableLocation.id}`
        : `${apiUrl}/locations`
      const method = selectedTableLocation ? 'PUT' : 'POST'

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newTableLocation)
      })

      const data = await response.json()
      if (data.success) {
        await loadTableLocations()
        setShowTableLocationModal(false)
        setSelectedTableLocation(null)
        setNewTableLocation({ name: '', description: '', color: '#3B82F6', sort_order: 0, is_active: true })
        setNotificationMessage({ type: 'success', message: selectedTableLocation ? 'Location updated successfully!' : 'Location added successfully!' })
        setShowNotificationModal(true)
      } else {
        setNotificationMessage({ type: 'error', message: data.error || 'Failed to save location' })
        setShowNotificationModal(true)
      }
    } catch (error) {
      console.error('Error saving table location:', error)
      setNotificationMessage({ type: 'error', message: 'Failed to save location' })
      setShowNotificationModal(true)
    }
  }

  const handleDeleteTableLocation = async (id: string) => {
    setConfirmModalConfig({
      message: 'Are you sure you want to delete this location?',
      onConfirm: async () => {
        setShowConfirmModal(false)
        try {
          const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
          const response = await fetch(`${apiUrl}/locations/${id}`, { method: 'DELETE' })
          const data = await response.json()
          
          if (data.success) {
            await loadTableLocations()
            setNotificationMessage({ type: 'success', message: 'Location deleted successfully!' })
          } else {
            setNotificationMessage({ type: 'error', message: data.error || 'Failed to delete location' })
          }
        } catch (error) {
          console.error('Error deleting table location:', error)
          setNotificationMessage({ type: 'error', message: 'Failed to delete location' })
        }
      }
    })
    setShowConfirmModal(true)
  }

  // Floor plans management functions
  const loadFloorPlans = async () => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
      const response = await fetch(`${apiUrl}/floor-plans/all`)
      const data = await response.json()
      if (data.success) {
        setFloorPlans(data.data || [])
      }
    } catch (error) {
      console.error('Error loading floor plans:', error)
    }
  }

  const handleSaveFloorPlan = async () => {
    if (!newFloorPlan.name) {
      setNotificationMessage({ type: 'error', message: 'Please enter a floor plan name' })
      setShowNotificationModal(true)
      return
    }

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
      const url = selectedFloorPlan 
        ? `${apiUrl}/floor-plans/${selectedFloorPlan.id}`
        : `${apiUrl}/floor-plans`
      const method = selectedFloorPlan ? 'PUT' : 'POST'

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newFloorPlan)
      })

      const data = await response.json()
      if (data.success) {
        await loadFloorPlans()
        setShowFloorPlanModal(false)
        setSelectedFloorPlan(null)
        setNewFloorPlan({ name: '', level: 0, description: '', width: 2000, height: 1500, sort_order: 0, is_active: true })
        setNotificationMessage({ type: 'success', message: selectedFloorPlan ? 'Floor plan updated successfully!' : 'Floor plan added successfully!' })
        setShowNotificationModal(true)
      } else {
        setNotificationMessage({ type: 'error', message: data.error || 'Failed to save floor plan' })
        setShowNotificationModal(true)
      }
    } catch (error) {
      console.error('Error saving floor plan:', error)
      setNotificationMessage({ type: 'error', message: 'Failed to save floor plan' })
      setShowNotificationModal(true)
    }
  }

  const handleDeleteFloorPlan = async (id: string) => {
    setConfirmModalConfig({
      message: 'Are you sure you want to delete this floor plan?',
      onConfirm: async () => {
        setShowConfirmModal(false)
        try {
          const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api'
          const response = await fetch(`${apiUrl}/floor-plans/${id}`, { method: 'DELETE' })
          const data = await response.json()
          
          if (data.success) {
            await loadFloorPlans()
            // Remove from assignments
            const newAssignments = { ...floorPlanKDSAssignments }
            delete newAssignments[id]
            setFloorPlanKDSAssignments(newAssignments)
            setNotificationMessage({ type: 'success', message: 'Floor plan deleted successfully!' })
            setShowNotificationModal(true)
          } else {
            setNotificationMessage({ type: 'error', message: data.error || 'Failed to delete floor plan' })
            setShowNotificationModal(true)
          }
        } catch (error) {
          console.error('Error deleting floor plan:', error)
          setNotificationMessage({ type: 'error', message: 'Failed to delete floor plan' })
          setShowNotificationModal(true)
        }
      }
    })
    setShowConfirmModal(true)
  }

  // Load categories
  const loadCategories = async () => {
    try {
      const response = await apiService.getCategories()
      if (response.success && response.data) {
        setCategories((response.data as any[]).filter((c: any) => c.is_active !== false))
      }
    } catch (error) {
      console.error('Error loading categories:', error)
    }
  }

  // Floor Plan KDS Category Assignment Management
  const loadFloorPlanKDSCategoryAssignments = async (floorPlanId: string) => {
    try {
      const response = await apiService.getFloorPlanKDSCategoryAssignments(floorPlanId)
      if (response.success && response.data) {
        const assignments = Array.isArray(response.data) ? response.data : []
        setFloorPlanKDSAssignments(prev => ({
          ...prev,
          [floorPlanId]: assignments
        }))
      }
    } catch (error) {
      console.error('Error loading floor plan KDS category assignments:', error)
    }
  }

  const handleAssignCategoryToKDSStation = async (floorPlanId: string, kdsStationId: string, categoryId: string) => {
    try {
      const response = await apiService.assignCategoryToKDSStation(floorPlanId, kdsStationId, categoryId)
      if (response.success) {
        await loadFloorPlanKDSCategoryAssignments(floorPlanId)
        setNotificationMessage({ type: 'success', message: 'Category assigned to KDS station successfully!' })
        setShowNotificationModal(true)
      } else {
        setNotificationMessage({ type: 'error', message: response.error || 'Failed to assign category' })
        setShowNotificationModal(true)
      }
    } catch (error) {
      console.error('Error assigning category:', error)
      setNotificationMessage({ type: 'error', message: 'Failed to assign category' })
      setShowNotificationModal(true)
    }
  }

  const handleRemoveCategoryAssignment = (floorPlanId: string, assignmentId: string) => {
    setConfirmModalConfig({
      message: 'Are you sure you want to remove this category assignment?',
      onConfirm: async () => {
        setShowConfirmModal(false)
        try {
          const response = await apiService.removeCategoryAssignmentFromFloorPlan(floorPlanId, assignmentId)
          if (response.success) {
            await loadFloorPlanKDSCategoryAssignments(floorPlanId)
            setNotificationMessage({ type: 'success', message: 'Category assignment removed successfully!' })
            setShowNotificationModal(true)
          } else {
            setNotificationMessage({ type: 'error', message: response.error || 'Failed to remove category assignment' })
            setShowNotificationModal(true)
          }
        } catch (error) {
          console.error('Error removing category assignment:', error)
          setNotificationMessage({ type: 'error', message: 'Failed to remove category assignment' })
          setShowNotificationModal(true)
        }
      }
    })
    setShowConfirmModal(true)
  }

  const toggleFloorPlanExpanded = (floorPlanId: string) => {
    const newExpanded = new Set(expandedFloorPlans)
    if (newExpanded.has(floorPlanId)) {
      newExpanded.delete(floorPlanId)
    } else {
      newExpanded.add(floorPlanId)
      // Load category assignments when expanding
      if (!floorPlanKDSAssignments[floorPlanId]) {
        loadFloorPlanKDSCategoryAssignments(floorPlanId)
      }
    }
    setExpandedFloorPlans(newExpanded)
  }

  // Handle expense category operations
  const handleAddExpenseCategory = () => {
    setEditingExpenseCategory(null)
    setExpenseCategoryFormData({
      category_name: '',
      account_id: null,
      description: '',
      is_active: true
    })
    setShowExpenseCategoryModal(true)
  }

  const handleEditExpenseCategory = (category: any) => {
    setEditingExpenseCategory(category)
    setExpenseCategoryFormData({
      category_name: category.category_name || '',
      account_id: category.account_id || null,
      description: category.description || '',
      is_active: category.is_active !== undefined ? category.is_active : true
    })
    setShowExpenseCategoryModal(true)
  }

  const handleSaveExpenseCategory = async () => {
    try {
      if (editingExpenseCategory) {
        const response = await apiService.updateExpenseCategory(editingExpenseCategory.id, expenseCategoryFormData)
        if (response.success) {
          await loadExpenseCategories()
          setShowExpenseCategoryModal(false)
          setNotificationMessage({ type: 'success', message: 'Expense category updated successfully!' })
          setShowNotificationModal(true)
        } else {
          setNotificationMessage({ type: 'error', message: response.error || 'Error updating expense category' })
          setShowNotificationModal(true)
        }
      } else {
        const response = await apiService.createExpenseCategory(expenseCategoryFormData)
        if (response.success) {
          await loadExpenseCategories()
          setShowExpenseCategoryModal(false)
          setNotificationMessage({ type: 'success', message: 'Expense category created successfully!' })
          setShowNotificationModal(true)
        } else {
          setNotificationMessage({ type: 'error', message: response.error || 'Error creating expense category' })
          setShowNotificationModal(true)
        }
      }
    } catch (error: any) {
      console.error('Error saving expense category:', error)
      setNotificationMessage({ type: 'error', message: error.message || 'Error saving expense category' })
      setShowNotificationModal(true)
    }
  }

  const handleDeleteExpenseCategory = async (id: string) => {
    setConfirmModalConfig({
      message: 'Are you sure you want to delete this expense category?',
      onConfirm: async () => {
        setShowConfirmModal(false)
        try {
          const response = await apiService.deleteExpenseCategory(id)
          if (response.success) {
            await loadExpenseCategories()
            setNotificationMessage({ type: 'success', message: 'Expense category deleted successfully!' })
          } else {
            setNotificationMessage({ type: 'error', message: response.error || 'Error deleting expense category' })
          }
        } catch (error) {
          console.error('Error deleting expense category:', error)
          setNotificationMessage({ type: 'error', message: 'Error deleting expense category' })
        }
      }
    })
    setShowConfirmModal(true)
  }

  // Inventory Locations
  const loadInventoryLocations = async () => {
    try {
      const response = await apiService.getInventoryLocations()
      if (response.success && response.data) {
        // Use flat array for easier management
        setInventoryLocations((response as any).flat || (Array.isArray(response.data) ? response.data : []) || [])
      }
    } catch (error) {
      console.error('Error loading inventory locations:', error)
    }
  }

  const loadAllergens = async () => {
    try {
      const response = await apiService.getAllergens()
      if (response.success && response.data) {
        setAllergensList(Array.isArray(response.data) ? response.data : [])
      }
    } catch (error) {
      console.error('Error loading allergens:', error)
    }
  }

  const openAllergenModal = (allergen?: { id: string; name: string; icon: string | null; sort_order: number }) => {
    if (allergen) {
      setEditingAllergen(allergen)
      setAllergenForm({ name: allergen.name, icon: allergen.icon, sort_order: allergen.sort_order })
    } else {
      setEditingAllergen(null)
      setAllergenForm({ name: '', icon: null, sort_order: allergensList.length })
    }
    setShowAllergenModal(true)
  }

  const handleSaveAllergen = async () => {
    if (!allergenForm.name.trim()) {
      setNotificationMessage({ type: 'error', message: 'Name is required' })
      setShowNotificationModal(true)
      return
    }
    setAllergenSaving(true)
    try {
      if (editingAllergen) {
        const res = await apiService.updateAllergen(editingAllergen.id, {
          name: allergenForm.name.trim(),
          icon: allergenForm.icon || null,
          sort_order: allergenForm.sort_order
        })
        if (res.success) {
          await loadAllergens()
          setShowAllergenModal(false)
          setNotificationMessage({ type: 'success', message: 'Allergen updated' })
          setShowNotificationModal(true)
        } else {
          setNotificationMessage({ type: 'error', message: (res as any).error || 'Update failed' })
          setShowNotificationModal(true)
        }
      } else {
        const res = await apiService.createAllergen({
          name: allergenForm.name.trim(),
          icon: allergenForm.icon || null,
          sort_order: allergenForm.sort_order
        })
        if (res.success) {
          await loadAllergens()
          setShowAllergenModal(false)
          setNotificationMessage({ type: 'success', message: 'Allergen created' })
          setShowNotificationModal(true)
        } else {
          setNotificationMessage({ type: 'error', message: (res as any).error || 'Create failed' })
          setShowNotificationModal(true)
        }
      }
    } catch (error) {
      setNotificationMessage({ type: 'error', message: error instanceof Error ? error.message : 'Failed to save allergen' })
      setShowNotificationModal(true)
    } finally {
      setAllergenSaving(false)
    }
  }

  const handleDeleteAllergen = (allergen: { id: string; name: string }) => {
    setConfirmModalConfig({
      message: `Delete allergen "${allergen.name}"? This will only succeed if it is not assigned to any menu item.`,
      onConfirm: async () => {
        try {
          const res = await apiService.deleteAllergen(allergen.id)
          if (res.success) {
            await loadAllergens()
            setNotificationMessage({ type: 'success', message: 'Allergen deleted' })
            setShowNotificationModal(true)
          } else {
            setNotificationMessage({ type: 'error', message: (res as any).error || 'Delete failed' })
            setShowNotificationModal(true)
          }
        } catch (error) {
          setNotificationMessage({ type: 'error', message: error instanceof Error ? error.message : 'Delete failed' })
          setShowNotificationModal(true)
        }
        setShowConfirmModal(false)
        setConfirmModalConfig(null)
      }
    })
    setShowConfirmModal(true)
  }

  const handleAddLocation = () => {
    setEditingLocation(null)
    setLocationFormData({
      name: '',
      parent_id: null,
      description: '',
      is_active: true,
      sort_order: 0
    })
    setShowLocationModal(true)
  }

  const handleEditLocation = (location: any) => {
    setEditingLocation(location)
    setLocationFormData({
      name: location.name || '',
      parent_id: location.parent_id || null,
      description: location.description || '',
      is_active: location.is_active !== undefined ? location.is_active : true,
      sort_order: location.sort_order || 0
    })
    setShowLocationModal(true)
  }

  const handleSaveLocation = async () => {
    if (!locationFormData.name || locationFormData.name.trim() === '') {
      setNotificationMessage({ type: 'error', message: 'Please enter a location name' })
      setShowNotificationModal(true)
      return
    }

    try {
      if (editingLocation) {
        const response = await apiService.updateInventoryLocation(editingLocation.id, locationFormData)
        if (response.success) {
          await loadInventoryLocations()
          setShowLocationModal(false)
          setEditingLocation(null)
          setNotificationMessage({ type: 'success', message: 'Location updated successfully!' })
          setShowNotificationModal(true)
        } else {
          setNotificationMessage({ type: 'error', message: response.error || 'Failed to update location' })
          setShowNotificationModal(true)
        }
      } else {
        const response = await apiService.createInventoryLocation(locationFormData)
        if (response.success) {
          await loadInventoryLocations()
          setShowLocationModal(false)
          setNotificationMessage({ type: 'success', message: 'Location created successfully!' })
          setShowNotificationModal(true)
        } else {
          setNotificationMessage({ type: 'error', message: response.error || 'Failed to create location' })
          setShowNotificationModal(true)
        }
      }
    } catch (error: any) {
      console.error('Error saving location:', error)
      setNotificationMessage({ type: 'error', message: error.message || 'Failed to save location' })
      setShowNotificationModal(true)
    }
  }

  const handleDeleteLocation = async (id: string) => {
    setConfirmModalConfig({
      message: 'Are you sure you want to delete this location?',
      onConfirm: async () => {
        setShowConfirmModal(false)
        try {
          const response = await apiService.deleteInventoryLocation(id)
          if (response.success) {
            await loadInventoryLocations()
            setNotificationMessage({ type: 'success', message: 'Location deleted successfully!' })
          } else {
            setNotificationMessage({ type: 'error', message: response.error || 'Failed to delete location' })
          }
        } catch (error) {
          console.error('Error deleting location:', error)
          setNotificationMessage({ type: 'error', message: 'Failed to delete location' })
        }
      }
    })
    setShowConfirmModal(true)
  }

  // Helper function to get location path (for display)
  const getLocationPath = (location: any, locations: any[]): string => {
    if (!location.parent_id) {
      return location.name
    }
    const parent = locations.find(l => l.id === location.parent_id)
    if (parent) {
      return `${getLocationPath(parent, locations)} > ${location.name}`
    }
    return location.name
  }

  // Helper function to get root locations (for parent dropdown)
  const getRootLocations = (locations: any[], excludeId?: string): any[] => {
    return locations.filter(loc => !loc.parent_id && loc.id !== excludeId)
  }

  // Inventory Categories
  const loadInventoryCategories = async () => {
    try {
      const response = await apiService.getInventoryCategories()
      if (response.success && response.data) {
        setInventoryCategories(Array.isArray(response.data) ? response.data : [])
      }
    } catch (error) {
      console.error('Error loading inventory categories:', error)
    }
  }

  const handleAddCategory = () => {
    setEditingCategory(null)
    setCategoryFormData({
      name: '',
      description: '',
      is_active: true,
      sort_order: 0
    })
    setShowCategoryModal(true)
  }

  const handleEditCategory = (category: any) => {
    setEditingCategory(category)
    setCategoryFormData({
      name: category.name || '',
      description: category.description || '',
      is_active: category.is_active !== undefined ? category.is_active : true,
      sort_order: category.sort_order || 0
    })
    setShowCategoryModal(true)
  }

  const handleSaveCategory = async () => {
    if (!categoryFormData.name || categoryFormData.name.trim() === '') {
      setNotificationMessage({ type: 'error', message: 'Please enter a category name' })
      setShowNotificationModal(true)
      return
    }

    try {
      if (editingCategory) {
        const response = await apiService.updateInventoryCategory(editingCategory.id, categoryFormData)
        if (response.success) {
          await loadInventoryCategories()
          setShowCategoryModal(false)
          setEditingCategory(null)
          setNotificationMessage({ type: 'success', message: 'Category updated successfully!' })
          setShowNotificationModal(true)
        } else {
          setNotificationMessage({ type: 'error', message: response.error || 'Failed to update category' })
          setShowNotificationModal(true)
        }
      } else {
        const response = await apiService.createInventoryCategory(categoryFormData)
        if (response.success) {
          await loadInventoryCategories()
          setShowCategoryModal(false)
          setNotificationMessage({ type: 'success', message: 'Category created successfully!' })
          setShowNotificationModal(true)
        } else {
          setNotificationMessage({ type: 'error', message: response.error || 'Failed to create category' })
          setShowNotificationModal(true)
        }
      }
    } catch (error: any) {
      console.error('Error saving category:', error)
      setNotificationMessage({ type: 'error', message: error.message || 'Failed to save category' })
      setShowNotificationModal(true)
    }
  }

  const handleDeleteCategory = async (id: string) => {
    setConfirmModalConfig({
      message: 'Are you sure you want to delete this category?',
      onConfirm: async () => {
        setShowConfirmModal(false)
        try {
          const response = await apiService.deleteInventoryCategory(id)
          if (response.success) {
            await loadInventoryCategories()
            setNotificationMessage({ type: 'success', message: 'Category deleted successfully!' })
          } else {
            setNotificationMessage({ type: 'error', message: response.error || 'Failed to delete category' })
          }
        } catch (error) {
          console.error('Error deleting category:', error)
          setNotificationMessage({ type: 'error', message: 'Failed to delete category' })
        }
      }
    })
    setShowConfirmModal(true)
  }

  const saveSettings = async () => {
    setSaving(true)
    try {

      if (activeTab === 'restaurant') {

        await handleSaveRestaurantSettings()
      } else if (activeTab === 'pos') {

        await handleSavePOSSettings()
      } else {
        // For other tabs, simulate API call
        setTimeout(() => {
          setSaving(false)
          setNotificationMessage({ type: 'success', message: 'Settings saved successfully!' })
          setShowNotificationModal(true)
        }, 1000)
      }
    } catch (error) {
      console.error('Error saving settings:', error)
      setSaving(false)
    }
  }

  const resetSettings = () => {
    if (confirm('Are you sure you want to reset all settings to default?')) {
      // Reset to default values
      alert('Settings reset to default values')
    }
  }

  // Customer Field Management Functions
  const handleSaveField = async () => {
    try {
      let response
      if (editingField) {
        response = await apiService.updateCustomerField(editingField.id, fieldFormData)
      } else {
        response = await apiService.createCustomerField(fieldFormData)
      }
      
      if (response.success) {
        await loadCustomerFields()
        setShowFieldModal(false)
        setEditingField(null)
        setFieldFormData({})
      }
    } catch (error) {
      console.error('Error saving field:', error)
    }
  }

  const handleDeleteField = async (fieldId: string) => {
    setConfirmModalConfig({
      message: 'Are you sure you want to delete this field?',
      onConfirm: async () => {
        setShowConfirmModal(false)
        try {
          const response = await apiService.deleteCustomerField(fieldId)
          if (response.success) {
            await loadCustomerFields()
            setNotificationMessage({ type: 'success', message: 'Customer field deleted successfully!' })
          } else {
            setNotificationMessage({ type: 'error', message: response.error || 'Failed to delete customer field' })
          }
        } catch (error) {
          console.error('Error deleting field:', error)
          setNotificationMessage({ type: 'error', message: 'Failed to delete customer field' })
        }
      }
    })
    setShowConfirmModal(true)
  }

  const handleEditField = (field: CustomerField) => {
    setEditingField(field)
    setFieldFormData(field)
    setShowFieldModal(true)
  }

  const handleAddField = () => {
    setEditingField(null)
    setFieldFormData({
      fieldType: 'text',
      isRequired: false,
      isActive: true,
      sortOrder: customerFields.length + 1
    })
    setShowFieldModal(true)
  }

  // POS Settings Functions
  const handleSavePOSSettings = async () => {
    setSaving(true)
    try {
      // Validate receiptLayout before saving
      if (posSettings.receiptLayout && !Array.isArray(posSettings.receiptLayout)) {
        console.error('receiptLayout is not an array!', posSettings.receiptLayout);
        alert('Error: Receipt layout must be an array. Please redesign the layout.');
        setSaving(false);
        return;
      }
      
      
      // Ensure kdsBumpingMode has default values
      // Note: zatcaPhase1Enabled is stored in restaurant_settings, not pos_settings
      const { zatcaPhase1Enabled, ...posSettingsToSave } = posSettings
      
      // Explicitly ensure critical numeric values are included (they might be 0 which is falsy)
      const settingsToSave = {
        ...posSettingsToSave,
        kdsBumpingMode: posSettings.kdsBumpingMode || 'item_wise',
        kdsStartPreparingMode: posSettings.kdsStartPreparingMode || 'item_wise',
        kdsColorThresholdGreen: posSettings.kdsColorThresholdGreen ?? 10,
        kdsColorThresholdYellow: posSettings.kdsColorThresholdYellow ?? 15,
        // Explicitly include these values to ensure they're saved even if they're 0
        maxDiscountPercentage: posSettings.maxDiscountPercentage !== undefined ? posSettings.maxDiscountPercentage : 50,
        maxDiscountAmount: posSettings.maxDiscountAmount !== undefined ? posSettings.maxDiscountAmount : 100,
        returnDurationHours: posSettings.returnDurationHours !== undefined ? posSettings.returnDurationHours : 24
      }
      
      
      // Save POS settings
      const posResponse = await apiService.updatePOSSettings(settingsToSave)

      
      if (!posResponse.success) {
        console.error('❌ Error saving POS settings:', posResponse.error)
        setNotificationMessage({ type: 'error', message: 'Error saving POS settings: ' + (posResponse.error || 'Unknown error') })
        setShowNotificationModal(true)
        setSaving(false)
        return
      }
      

      
      // Also save restaurant settings if dual_invoicing_enabled or zatca_phase1_enabled was changed
      // (since they're in the POS Settings tab but stored in restaurant_settings)
      // Use the tracked toggle value as the source of truth
      const zatcaValue = zatcaToggleValue

      
      const restaurantResponse = await apiService.updateRestaurantSettings({
        dual_invoicing_enabled: restaurantSettings.dual_invoicing_enabled,
        zatca_phase1_enabled: zatcaValue,
        qr_menu_order_placing_enabled: posSettings.qrMenuOrderPlacingEnabled ?? true
      })
      

      
      if (restaurantResponse.success) {

        setNotificationMessage({ type: 'success', message: 'Settings saved successfully!' })
        setShowNotificationModal(true)
        // Don't reload immediately - the state is already up to date
        // Only reload if user navigates away and comes back
      } else {
        console.error('⚠️ POS settings saved, but restaurant settings failed:', restaurantResponse.error)
        // Don't fail the whole operation if restaurant settings fail
        setNotificationMessage({ type: 'error', message: 'POS settings saved successfully, but there was an error saving invoice settings: ' + (restaurantResponse.error || 'Unknown error') })
        setShowNotificationModal(true)
        // Don't reload - state is already correct for POS settings
      }
    } catch (error: any) {
      console.error('Error saving settings:', error)
      setNotificationMessage({ type: 'error', message: 'Error saving settings: ' + (error.message || 'Unknown error') })
      setShowNotificationModal(true)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 dark:text-white sm:text-3xl sm:truncate">
            Settings
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Configure your restaurant management system
          </p>
        </div>
        <div className="mt-4 flex space-x-3 md:mt-0 md:ml-4">
          <button
            onClick={resetSettings}
            className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Reset
          </button>
          <button
            onClick={saveSettings}
            disabled={saving}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar */}
        <div className="lg:w-64">
          <nav className="space-y-1">
            {tabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as any)}
                className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  activeTab === tab.key
                    ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-200'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <tab.icon className="mr-3 h-5 w-5" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1">
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
            {/* Restaurant Settings */}
            {activeTab === 'restaurant' && (
              <div className="p-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-6">
                  Restaurant Information
                </h3>
                
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Restaurant Name
                      </label>
                      <input
                        type="text"
                        value={restaurantSettings.restaurant_name}
                        onChange={(e) => setRestaurantSettings({ ...restaurantSettings, restaurant_name: e.target.value })}
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Restaurant Name (Arabic) <span className="text-gray-500 text-xs">(Optional)</span>
                      </label>
                      <input
                        type="text"
                        value={restaurantSettings.restaurant_name_ar || ''}
                        onChange={(e) => setRestaurantSettings({ ...restaurantSettings, restaurant_name_ar: e.target.value })}
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        dir="rtl"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Restaurant Logo <span className="text-gray-500 text-xs">(Optional)</span>
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files?.[0]
                        if (!file) return
                        if (file.size > 10 * 1024 * 1024) { alert('Image size must be less than 10MB'); return }
                        if (!file.type.startsWith('image/')) { alert('Please select a valid image file'); return }
                        const res = await apiService.uploadImage(file, 'logos')
                        if (res.success && res.url) {
                          setRestaurantSettings({ ...restaurantSettings, restaurant_logo_url: res.url })
                        } else {
                          alert(res.error || 'Error uploading image')
                        }
                      }}
                      className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 shadow-sm py-2 px-3 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:file:bg-blue-900 dark:file:text-blue-300"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Upload your restaurant logo. Recommended size: 200-300px width, max 10MB. Supported formats: PNG, JPG, GIF, WebP.
                    </p>
                    {restaurantSettings.restaurant_logo_url && (
                      <div className="mt-3">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs text-gray-600 dark:text-gray-400">Preview:</p>
                          <button
                            type="button"
                            onClick={() => setRestaurantSettings({ ...restaurantSettings, restaurant_logo_url: '' })}
                            className="text-xs text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                          >
                            Remove Logo
                          </button>
                        </div>
                        <img 
                          src={resolveImageUrl(restaurantSettings.restaurant_logo_url)} 
                          alt="Restaurant Logo Preview" 
                          className="max-w-xs max-h-24 object-contain border border-gray-300 dark:border-gray-600 rounded"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none'
                          }}
                        />
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Customer Frontend Logo <span className="text-gray-500 text-xs">(Optional)</span>
                    </label>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                      Logo shown on the QR menu / customer app home screen only. Use a different image from the main restaurant logo if needed. Click &quot;Save&quot; below to apply.
                    </p>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files?.[0]
                        if (!file) return
                        if (file.size > 10 * 1024 * 1024) { alert('Image size must be less than 10MB'); return }
                        if (!file.type.startsWith('image/')) { alert('Please select a valid image file'); return }
                        const res = await apiService.uploadImage(file, 'logos')
                        if (res.success && res.url) {
                          setRestaurantSettings({ ...restaurantSettings, customer_frontend_logo_url: res.url })
                        } else {
                          alert(res.error || 'Error uploading image')
                        }
                      }}
                      className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 shadow-sm py-2 px-3 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:file:bg-blue-900 dark:file:text-blue-300"
                    />
                    {restaurantSettings.customer_frontend_logo_url && (
                      <div className="mt-3">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs text-gray-600 dark:text-gray-400">Preview:</p>
                          <button
                            type="button"
                            onClick={() => setRestaurantSettings({ ...restaurantSettings, customer_frontend_logo_url: '' })}
                            className="text-xs text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                          >
                            Remove
                          </button>
                        </div>
                        <img
                          src={resolveImageUrl(restaurantSettings.customer_frontend_logo_url)}
                          alt="Customer frontend logo preview"
                          className="max-w-xs max-h-24 object-contain border border-gray-300 dark:border-gray-600 rounded"
                          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                        />
                      </div>
                    )}
                  </div>

                  <div className="border-t border-gray-200 dark:border-gray-600 pt-6 mt-6">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">App Icons (Favicon &amp; Loading)</h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                      Used on customer menu, admin panel, and waiter module. Favicon appears in browser tab; loading icon is shown while the app loads.
                    </p>
                    <div className="grid gap-6 sm:grid-cols-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Favicon</label>
                        <input
                          type="file"
                          accept="image/*,.ico"
                          onChange={async (e) => {
                            const file = e.target.files?.[0]
                            if (!file) return
                            if (file.size > 2 * 1024 * 1024) { alert('Favicon should be under 2MB'); return }
                            const res = await apiService.uploadImage(file, 'logos')
                            if (res.success && res.url) {
                              setRestaurantSettings({ ...restaurantSettings, app_favicon_url: res.url })
                            } else {
                              alert(res.error || 'Error uploading image')
                            }
                          }}
                          className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 shadow-sm py-2 px-3 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:file:bg-blue-900 dark:file:text-blue-300"
                        />
                        {restaurantSettings.app_favicon_url && (
                          <div className="mt-3 flex items-center gap-3">
                            <img src={resolveImageUrl(restaurantSettings.app_favicon_url)} alt="Favicon" className="h-8 w-8 object-contain border border-gray-300 dark:border-gray-600 rounded" />
                            <button type="button" onClick={() => setRestaurantSettings({ ...restaurantSettings, app_favicon_url: '' })} className="text-xs text-red-600 hover:text-red-800 dark:text-red-400">Remove</button>
                          </div>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Loading icon</label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={async (e) => {
                            const file = e.target.files?.[0]
                            if (!file) return
                            if (file.size > 5 * 1024 * 1024) { alert('Image must be under 5MB'); return }
                            if (!file.type.startsWith('image/')) { alert('Please select a valid image file'); return }
                            const res = await apiService.uploadImage(file, 'logos')
                            if (res.success && res.url) {
                              setRestaurantSettings({ ...restaurantSettings, app_loading_icon_url: res.url })
                            } else {
                              alert(res.error || 'Error uploading image')
                            }
                          }}
                          className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 shadow-sm py-2 px-3 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:file:bg-blue-900 dark:file:text-blue-300"
                        />
                        {restaurantSettings.app_loading_icon_url && (
                          <div className="mt-3 flex items-center gap-3">
                            <img src={resolveImageUrl(restaurantSettings.app_loading_icon_url)} alt="Loading" className="h-12 w-12 object-contain border border-gray-300 dark:border-gray-600 rounded" />
                            <button type="button" onClick={() => setRestaurantSettings({ ...restaurantSettings, app_loading_icon_url: '' })} className="text-xs text-red-600 hover:text-red-800 dark:text-red-400">Remove</button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={restaurantSettings.phone_number}
                      onChange={(e) => setRestaurantSettings({ ...restaurantSettings, phone_number: e.target.value })}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Address
                    </label>
                    <textarea
                      value={restaurantSettings.address}
                      onChange={(e) => setRestaurantSettings({ ...restaurantSettings, address: e.target.value })}
                      rows={3}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Address (Arabic) <span className="text-gray-500 text-xs">(Optional)</span>
                    </label>
                    <textarea
                      value={restaurantSettings.address_ar || ''}
                      onChange={(e) => setRestaurantSettings({ ...restaurantSettings, address_ar: e.target.value })}
                      rows={3}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      dir="rtl"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        value={restaurantSettings.email}
                        onChange={(e) => setRestaurantSettings({ ...restaurantSettings, email: e.target.value })}
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Website
                      </label>
                      <input
                        type="url"
                        value={restaurantSettings.website}
                        onChange={(e) => setRestaurantSettings({ ...restaurantSettings, website: e.target.value })}
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Google Maps link
                      </label>
                      <input
                        type="url"
                        value={restaurantSettings.google_maps_review_url || ''}
                        onChange={(e) => setRestaurantSettings({ ...restaurantSettings, google_maps_review_url: e.target.value })}
                        placeholder="https://maps.google.com/... or https://g.page/... or review link"
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Used when customers tap &quot;Leave us a review on Google Maps&quot; — opens this link (e.g. your place&apos;s review page or map).
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      VAT Number
                    </label>
                    <input
                      type="text"
                      value={restaurantSettings.vat_number}
                      onChange={(e) => setRestaurantSettings({ ...restaurantSettings, vat_number: e.target.value })}
                      placeholder="Enter VAT registration number"
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Currency
                      </label>
                      <select
                        value={restaurantSettings.currency}
                        onChange={(e) => setRestaurantSettings({ ...restaurantSettings, currency: e.target.value })}
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="SAR">SAR (Saudi Riyal)</option>
                        <option value="USD">USD (US Dollar)</option>
                        <option value="EUR">EUR (Euro)</option>
                        <option value="AED">AED (UAE Dirham)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Timezone
                      </label>
                      <select
                        value={restaurantSettings.timezone}
                        onChange={(e) => setRestaurantSettings({ ...restaurantSettings, timezone: e.target.value })}
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="Asia/Riyadh">Asia/Riyadh</option>
                        <option value="Asia/Dubai">Asia/Dubai</option>
                        <option value="Europe/London">Europe/London</option>
                        <option value="America/New_York">America/New_York</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Language
                      </label>
                      <select
                        value={restaurantSettings.language}
                        onChange={(e) => setRestaurantSettings({ ...restaurantSettings, language: e.target.value })}
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="en">English</option>
                        <option value="ar">العربية</option>
                        <option value="fr">Français</option>
                        <option value="es">Español</option>
                      </select>
                    </div>
                  </div>

                  {/* Global: Business hours (per day) */}
                  <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-800/50">
                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3">
                      Business hours
                    </h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                      Set opening and closing times per day. Used for reservations and general availability. Mark a day as closed for holidays or rest days.
                    </p>
                    <div className="space-y-3">
                      {DAY_NAMES.map((label, i) => {
                        const key = String(i)
                        const slot = (restaurantSettings.restaurant_schedule ?? {})[key] ?? { open: '16:00', close: '22:00', closed: false }
                        return (
                          <div key={key} className="flex flex-wrap items-center gap-3 p-3 rounded-lg bg-white dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600">
                            <span className="w-24 text-sm font-medium text-gray-700 dark:text-gray-300">{label}</span>
                            <label className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={slot.closed}
                                onChange={(e) => {
                                  const s = { ...restaurantSettings, restaurant_schedule: { ...(restaurantSettings.restaurant_schedule ?? {}), [key]: { ...slot, closed: e.target.checked } } }
                                  setRestaurantSettings(s)
                                }}
                                className="rounded border-gray-300 dark:border-gray-600"
                              />
                              <span className="text-sm text-gray-600 dark:text-gray-400">Closed</span>
                            </label>
                            {!slot.closed && (
                              <>
                                <input
                                  type="time"
                                  value={slot.open}
                                  onChange={(e) => {
                                    const s = { ...restaurantSettings, restaurant_schedule: { ...(restaurantSettings.restaurant_schedule ?? {}), [key]: { ...slot, open: e.target.value } } }
                                    setRestaurantSettings(s)
                                  }}
                                  className="border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1.5 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                />
                                <span className="text-gray-500">–</span>
                                <input
                                  type="time"
                                  value={slot.close}
                                  onChange={(e) => {
                                    const s = { ...restaurantSettings, restaurant_schedule: { ...(restaurantSettings.restaurant_schedule ?? {}), [key]: { ...slot, close: e.target.value } } }
                                    setRestaurantSettings(s)
                                  }}
                                  className="border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1.5 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                />
                              </>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </div>

                  {/* Business day for register and reports (e.g. 4pm–3am = one day) */}
                  <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-800/50">
                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3">
                      Business day (register &amp; reports)
                    </h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                      Used for register sessions, sales reports, and &quot;today&quot; in reports and orders. E.g. 4pm–3am = one day (opens 4pm, closes 3am next morning). Leave 00:00–23:59 for calendar day (midnight to midnight).
                    </p>
                    <div className="flex flex-wrap items-center gap-4">
                      <label className="flex items-center gap-2">
                        <span className="text-sm text-gray-700 dark:text-gray-300">Day start (e.g. 4pm)</span>
                        <input
                          type="time"
                          value={restaurantSettings.business_day_start_time ?? '00:00'}
                          onChange={(e) => setRestaurantSettings({ ...restaurantSettings, business_day_start_time: e.target.value })}
                          className="border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1.5 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </label>
                      <span className="text-gray-500">–</span>
                      <label className="flex items-center gap-2">
                        <span className="text-sm text-gray-700 dark:text-gray-300">Day end (e.g. 3am)</span>
                        <input
                          type="time"
                          value={restaurantSettings.business_day_end_time ?? '23:59'}
                          onChange={(e) => setRestaurantSettings({ ...restaurantSettings, business_day_end_time: e.target.value })}
                          className="border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1.5 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </label>
                    </div>
                  </div>

                  {/* Global: Holidays (closed dates) */}
                  <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-800/50">
                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3">
                      Holidays
                    </h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                      Add dates when the restaurant is closed. These dates will not be available for reservations.
                    </p>
                    <div className="flex flex-wrap gap-2 mb-3">
                      <input
                        type="date"
                        id="new-holiday-date"
                        className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const input = document.getElementById('new-holiday-date') as HTMLInputElement
                          const v = input?.value
                          if (!v) return
                          const list = restaurantSettings.restaurant_holidays ?? []
                          if (list.includes(v)) return
                          setRestaurantSettings({ ...restaurantSettings, restaurant_holidays: [...list, v].sort() })
                          if (input) input.value = ''
                        }}
                        className="px-3 py-2 rounded-md bg-orange-500 text-white text-sm font-medium hover:bg-orange-600"
                      >
                        Add holiday
                      </button>
                    </div>
                    <ul className="space-y-1">
                      {(restaurantSettings.restaurant_holidays ?? []).length === 0 ? (
                        <li className="text-sm text-gray-500 dark:text-gray-400">No holidays added.</li>
                      ) : (
                        (restaurantSettings.restaurant_holidays ?? []).map((d) => (
                          <li key={d} className="flex items-center justify-between text-sm">
                            <span className="text-gray-700 dark:text-gray-300">{d}</span>
                            <button
                              type="button"
                              onClick={() => setRestaurantSettings({ ...restaurantSettings, restaurant_holidays: (restaurantSettings.restaurant_holidays ?? []).filter((x) => x !== d) })}
                              className="text-red-600 hover:underline"
                            >
                              Remove
                            </button>
                          </li>
                        ))
                      )}
                    </ul>
                  </div>

                  {/* Reservation settings (customer booking) */}
                  <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-800/50">
                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3">
                      Reservation settings
                    </h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                      Configure time slot duration and how far in advance customers must book. Time slots use the business hours above.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Time slot duration
                        </label>
                        <select
                          value={restaurantSettings.reservation_timeslot_duration_minutes ?? 30}
                          onChange={(e) => setRestaurantSettings({ ...restaurantSettings, reservation_timeslot_duration_minutes: Number(e.target.value) })}
                          className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        >
                          <option value={15}>15 minutes</option>
                          <option value={30}>30 minutes</option>
                          <option value={60}>1 hour</option>
                        </select>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Interval between selectable reservation times (e.g. 30 min → 16:00, 16:30, 17:00…).
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Minimum booking time (hours ahead)
                        </label>
                        <select
                          value={restaurantSettings.reservation_minimum_booking_hours ?? 2}
                          onChange={(e) => setRestaurantSettings({ ...restaurantSettings, reservation_minimum_booking_hours: Number(e.target.value) })}
                          className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        >
                          <option value={0}>No minimum</option>
                          <option value={1}>1 hour</option>
                          <option value={2}>2 hours</option>
                          <option value={3}>3 hours</option>
                          <option value={4}>4 hours</option>
                          <option value={6}>6 hours</option>
                          <option value={12}>12 hours</option>
                          <option value={24}>24 hours</option>
                        </select>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Customers can only book slots at least this many hours from now.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Currency Display Settings */}
                  <div>
                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">
                      Currency Display Settings
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Currency Symbol
                        </label>
                        <input
                          type="text"
                          value={restaurantSettings.currency_symbol}
                          onChange={(e) => setRestaurantSettings({ ...restaurantSettings, currency_symbol: e.target.value })}
                          className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          placeholder="ر.س"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Display Type
                        </label>
                        <select
                          value={restaurantSettings.currency_display_type}
                          onChange={(e) => setRestaurantSettings({ ...restaurantSettings, currency_display_type: e.target.value as 'text' | 'icon' })}
                          className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        >
                          <option value="text">Text Symbol</option>
                          <option value="icon">PNG Icon</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Display Position
                        </label>
                        <select
                          value={restaurantSettings.currency_display_position}
                          onChange={(e) => setRestaurantSettings({ ...restaurantSettings, currency_display_position: e.target.value as 'left' | 'right' })}
                          className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        >
                          <option value="left">Left Side</option>
                          <option value="right">Right Side</option>
                        </select>
                      </div>
                      {restaurantSettings.currency_display_type === 'icon' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Currency Icon URL
                          </label>
                          <input
                            type="url"
                            value={restaurantSettings.currency_icon_url}
                            onChange={(e) => setRestaurantSettings({ ...restaurantSettings, currency_icon_url: e.target.value })}
                            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            placeholder="https://example.com/currency-icon.png"
                          />
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            Upload a PNG icon and provide the URL here
                          </p>
                        </div>
                      )}
                    </div>
                    {/* Currency Preview */}
                    <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Preview:</h5>
                      <div className="text-lg font-semibold text-gray-900 dark:text-white">
                        {restaurantSettings.currency_display_position === 'left' ? (
                          <>
                            {restaurantSettings.currency_display_type === 'icon' && restaurantSettings.currency_icon_url ? (
                              <img src={restaurantSettings.currency_icon_url} alt="Currency" className="inline-block w-5 h-5 mr-1" />
                            ) : (
                              <span>{restaurantSettings.currency_symbol}</span>
                            )}
                            <span>25.50</span>
                          </>
                        ) : (
                          <>
                            <span>25.50</span>
                            {restaurantSettings.currency_display_type === 'icon' && restaurantSettings.currency_icon_url ? (
                              <img src={restaurantSettings.currency_icon_url} alt="Currency" className="inline-block w-5 h-5 ml-1" />
                            ) : (
                              <span className="ml-1">{restaurantSettings.currency_symbol}</span>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Tax Rate (%)
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        value={restaurantSettings.tax_rate}
                        onChange={(e) => setRestaurantSettings({ ...restaurantSettings, tax_rate: Number(e.target.value) })}
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Service Charge (%)
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        value={restaurantSettings.service_charge}
                        onChange={(e) => setRestaurantSettings({ ...restaurantSettings, service_charge: Number(e.target.value) })}
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                  </div>

                  {/* Waiter Order Flow Setting */}
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">
                      Waiter Order Flow
                    </h4>
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                            How should waiter orders be processed?
                          </label>
                          <div className="space-y-3">
                            <label className={`flex items-start p-3 border-2 rounded-lg cursor-pointer transition-colors ${
                              restaurantSettings.waiter_order_flow === 'direct_to_kitchen'
                                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                                : 'border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600'
                            }`}>
                              <input
                                type="radio"
                                name="waiter_order_flow"
                                value="direct_to_kitchen"
                                checked={restaurantSettings.waiter_order_flow === 'direct_to_kitchen'}
                                onChange={(e) => setRestaurantSettings({ ...restaurantSettings, waiter_order_flow: e.target.value as 'direct_to_kitchen' | 'requires_approval' })}
                                className="mt-1 mr-3"
                              />
                              <div className="flex-1">
                                <div className="font-medium text-gray-900 dark:text-white">
                                  Direct to Kitchen
                                </div>
                                <div className="text-sm text-gray-600 dark:text-gray-400">
                                  Orders placed by waiters go directly to the kitchen without approval
                                </div>
                              </div>
                            </label>
                            <label className={`flex items-start p-3 border-2 rounded-lg cursor-pointer transition-colors ${
                              restaurantSettings.waiter_order_flow === 'requires_approval'
                                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                                : 'border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600'
                            }`}>
                              <input
                                type="radio"
                                name="waiter_order_flow"
                                value="requires_approval"
                                checked={restaurantSettings.waiter_order_flow === 'requires_approval'}
                                onChange={(e) => setRestaurantSettings({ ...restaurantSettings, waiter_order_flow: e.target.value as 'direct_to_kitchen' | 'requires_approval' })}
                                className="mt-1 mr-3"
                              />
                              <div className="flex-1">
                                <div className="font-medium text-gray-900 dark:text-white">
                                  Requires Approval
                                </div>
                                <div className="text-sm text-gray-600 dark:text-gray-400">
                                  Orders placed by waiters must be approved by POS/cashier before going to kitchen
                                </div>
                              </div>
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Waiter Module Settings */}
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">
                      Waiter Module Settings
                    </h4>
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                      <div className="space-y-4">
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Additional Items Flow
                          </label>
                          <select
                            value={restaurantSettings.waiter_additional_items_flow || 'direct_to_kitchen'}
                            onChange={(e) => setRestaurantSettings({
                              ...restaurantSettings,
                              waiter_additional_items_flow: e.target.value as 'direct_to_kitchen' | 'requires_approval'
                            })}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                          >
                            <option value="direct_to_kitchen">Direct to Kitchen</option>
                            <option value="requires_approval">Needs Approval</option>
                          </select>
                          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            When additional items are added to existing orders from waiter module, they will {restaurantSettings.waiter_additional_items_flow === 'requires_approval' ? 'require approval' : 'go directly to kitchen'}
                          </p>
                        </div>
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Order Modification (add/remove items)
                          </label>
                          <select
                            value={restaurantSettings.requires_approval_order_modification !== false ? 'requires_approval' : 'direct_to_kitchen'}
                            onChange={(e) => setRestaurantSettings({
                              ...restaurantSettings,
                              requires_approval_order_modification: e.target.value === 'requires_approval'
                            })}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                          >
                            <option value="requires_approval">Requires Approval</option>
                            <option value="direct_to_kitchen">Direct to Kitchen</option>
                          </select>
                          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            When items are added or removed from an existing order (waiter module), they will {restaurantSettings.requires_approval_order_modification !== false ? 'require POS approval before going to KDS' : 'go to KDS directly without approval'}
                          </p>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">
                              Require Customer Count
                            </label>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              If enabled, waiters must enter the customer count before placing an order
                            </p>
                          </div>
                          <div className="ml-4">
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={restaurantSettings.waiter_require_customer_count ?? false}
                                onChange={(e) => setRestaurantSettings({ ...restaurantSettings, waiter_require_customer_count: e.target.checked })}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Takeaway & Catering KDS Flow Settings */}
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">
                      Takeaway & Catering KDS Flow
                    </h4>
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                      <div className="space-y-4">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Select which floor's KDS station assignments to use for takeaway and catering orders.
                          Items will be routed to KDS stations based on the selected floor's category assignments.
                        </p>

                        {/* Takeaway Floor Plan Setting */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Takeaway Orders - Floor Plan
                          </label>
                          <select
                            value={restaurantSettings.takeaway_floor_plan_id || ''}
                            onChange={(e) => setRestaurantSettings({ ...restaurantSettings, takeaway_floor_plan_id: e.target.value || undefined })}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                          >
                            <option value="">-- Select Floor Plan --</option>
                            {floorPlans.map((floorPlan) => (
                              <option key={floorPlan.id} value={floorPlan.id}>
                                {floorPlan.name} (Level {floorPlan.level})
                              </option>
                            ))}
                          </select>
                          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            Takeaway orders will use this floor's KDS category-to-station assignments
                          </p>
                        </div>

                        {/* Catering Floor Plan Setting */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Catering Orders - Floor Plan
                          </label>
                          <select
                            value={restaurantSettings.catering_floor_plan_id || ''}
                            onChange={(e) => setRestaurantSettings({ ...restaurantSettings, catering_floor_plan_id: e.target.value || undefined })}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                          >
                            <option value="">-- Select Floor Plan --</option>
                            {floorPlans.map((floorPlan) => (
                              <option key={floorPlan.id} value={floorPlan.id}>
                                {floorPlan.name} (Level {floorPlan.level})
                              </option>
                            ))}
                          </select>
                          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            Catering orders will use this floor's KDS category-to-station assignments
                          </p>
                        </div>

                        {(restaurantSettings.takeaway_floor_plan_id || restaurantSettings.catering_floor_plan_id) && (
                          <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                            <p className="text-sm text-blue-700 dark:text-blue-300">
                              <strong>Note:</strong> Make sure the selected floor plans have KDS category assignments configured in the Floor Plans section below.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Locations Management */}
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-6 mt-6">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-md font-medium text-gray-900 dark:text-white">
                        Table Locations
                      </h4>
                      <button
                        onClick={() => {
                          setSelectedTableLocation(null)
                          setNewTableLocation({ name: '', description: '', color: '#3B82F6', sort_order: tableLocations.length, is_active: true })
                          setShowTableLocationModal(true)
                        }}
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                      >
                        <PlusIcon className="h-4 w-4 mr-2" />
                        Add Location
                      </button>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                      <div className="space-y-2">
                        {tableLocations.map(location => (
                          <div key={location.id} className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg">
                            <div className="flex items-center gap-3">
                              <div 
                                className="w-4 h-4 rounded-full" 
                                style={{ backgroundColor: location.color }}
                              />
                              <div>
                                <div className="font-medium text-gray-900 dark:text-white">{location.name}</div>
                                {location.description && (
                                  <div className="text-sm text-gray-500 dark:text-gray-400">{location.description}</div>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => {
                                  setSelectedTableLocation(location)
                                  setNewTableLocation({ ...location })
                                  setShowTableLocationModal(true)
                                }}
                                className="text-blue-600 hover:text-blue-900 dark:text-blue-400"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteTableLocation(location.id)}
                                className="text-red-600 hover:text-red-900 dark:text-red-400"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                        {tableLocations.length === 0 && (
                          <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                            No locations added yet. Click "Add Location" to create one.
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Floor Plans Management */}
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-6 mt-6">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-md font-medium text-gray-900 dark:text-white">
                        Floor Plans
                      </h4>
                      <button
                        onClick={() => {
                          setSelectedFloorPlan(null)
                          setNewFloorPlan({ name: '', level: 0, description: '', width: 2000, height: 1500, sort_order: floorPlans.length, is_active: true })
                          setShowFloorPlanModal(true)
                        }}
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                      >
                        <PlusIcon className="h-4 w-4 mr-2" />
                        Add Floor Plan
                      </button>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                      <div className="space-y-2">
                        {floorPlans.map(floorPlan => {
                          const assignments = floorPlanKDSAssignments[floorPlan.id] || []
                          const isExpanded = expandedFloorPlans.has(floorPlan.id)
                          
                          // Group assignments by KDS station
                          const assignmentsByStation = new Map<string, any[]>()
                          assignments.forEach(assignment => {
                            const stationId = assignment.kds_station_id || assignment.kds_stations?.id
                            if (stationId) {
                              if (!assignmentsByStation.has(stationId)) {
                                assignmentsByStation.set(stationId, [])
                              }
                              assignmentsByStation.get(stationId)!.push(assignment)
                            }
                          })

                          return (
                            <div key={floorPlan.id} className="bg-white dark:bg-gray-800 rounded-lg">
                              <div className="flex items-center justify-between p-3">
                                <div className="flex-1">
                                  <div className="font-medium text-gray-900 dark:text-white">{floorPlan.name}</div>
                                  <div className="text-sm text-gray-500 dark:text-gray-400">
                                    Level {floorPlan.level > 0 ? `+${floorPlan.level}` : floorPlan.level} • {floorPlan.width}×{floorPlan.height}px
                                    {floorPlan.description && ` • ${floorPlan.description}`}
                                    {assignments.length > 0 && (
                                      <span className="ml-2 text-blue-600 dark:text-blue-400">
                                        • {assignments.length} Category Assignment{assignments.length !== 1 ? 's' : ''}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => toggleFloorPlanExpanded(floorPlan.id)}
                                    className="text-sm text-blue-600 hover:text-blue-900 dark:text-blue-400 px-2 py-1"
                                  >
                                    {isExpanded ? 'Hide' : 'Manage KDS'}
                                  </button>
                                  <button
                                    onClick={() => {
                                      setSelectedFloorPlan(floorPlan)
                                      setNewFloorPlan({ ...floorPlan })
                                      setShowFloorPlanModal(true)
                                    }}
                                    className="text-blue-600 hover:text-blue-900 dark:text-blue-400"
                                  >
                                    <PencilIcon className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteFloorPlan(floorPlan.id)}
                                    className="text-red-600 hover:text-red-900 dark:text-red-400"
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                              {isExpanded && (
                                <div className="border-t border-gray-200 dark:border-gray-700 p-4 space-y-4">
                                  <div className="flex items-center justify-between">
                                    <h5 className="text-sm font-medium text-gray-900 dark:text-white">Category to KDS Station Assignments</h5>
                                  </div>
                                  
                                  {/* Group by KDS Station */}
                                  {Array.from(assignmentsByStation.entries()).map(([stationId, stationAssignments]) => {
                                    const station = kdsStations.find(s => s.id === stationId)
                                    if (!station) return null
                                    
                                    const assignedCategoryIds = stationAssignments.map(a => a.category_id || a.categories?.id).filter(Boolean)
                                    const availableCategories = categories.filter(c => !assignedCategoryIds.includes(c.id))
                                    
                                    return (
                                      <div key={stationId} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 space-y-2">
                                        <div className="flex items-center justify-between mb-2">
                                          <div className="flex items-center gap-2">
                                            <div 
                                              className="w-3 h-3 rounded-full" 
                                              style={{ backgroundColor: station.color || '#3B82F6' }}
                                            />
                                            <span className="text-sm font-medium text-gray-900 dark:text-white">{station.name}</span>
                                          </div>
                                          {availableCategories.length > 0 && (
                                            <select
                                              onChange={(e) => {
                                                if (e.target.value) {
                                                  handleAssignCategoryToKDSStation(floorPlan.id, stationId, e.target.value)
                                                  e.target.value = ''
                                                }
                                              }}
                                              className="text-xs border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                            >
                                              <option value="">Assign Category...</option>
                                              {availableCategories.map(category => (
                                                <option key={category.id} value={category.id}>{category.name}</option>
                                              ))}
                                            </select>
                                          )}
                                        </div>
                                        {stationAssignments.length > 0 ? (
                                          <div className="space-y-1">
                                            {stationAssignments.map(assignment => {
                                              const category = assignment.categories || categories.find(c => c.id === assignment.category_id)
                                              return category ? (
                                                <div key={assignment.id} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded text-xs">
                                                  <span className="text-gray-900 dark:text-white">{category.name}</span>
                                                  <button
                                                    onClick={() => handleRemoveCategoryAssignment(floorPlan.id, assignment.id)}
                                                    className="text-red-600 hover:text-red-900 dark:text-red-400"
                                                  >
                                                    Remove
                                                  </button>
                                                </div>
                                              ) : null
                                            })}
                                          </div>
                                        ) : (
                                          <div className="text-xs text-gray-500 dark:text-gray-400 text-center py-1">
                                            No categories assigned. Select one from the dropdown above.
                                          </div>
                                        )}
                                      </div>
                                    )
                                  })}
                                  
                                  {/* Show option to add new KDS station if there are unassigned stations */}
                                  {kdsStations.filter(s => s.is_active && !assignmentsByStation.has(s.id)).length > 0 && (
                                    <div className="border border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-3">
                                      <select
                                        onChange={(e) => {
                                          if (e.target.value) {
                                            // When a station is selected, show category dropdown for that station
                                            const stationId = e.target.value
                                            const station = kdsStations.find(s => s.id === stationId)
                                            if (station && categories.length > 0) {
                                              // Auto-assign first available category or show prompt
                                              const firstCategory = categories[0]
                                              if (firstCategory) {
                                                setConfirmModalConfig({
                                                  message: `Assign "${firstCategory.name}" category to "${station.name}"?`,
                                                  confirmButtonText: 'Assign',
                                                  confirmButtonColor: 'green',
                                                  onConfirm: () => {
                                                    handleAssignCategoryToKDSStation(floorPlan.id, stationId, firstCategory.id)
                                                    setShowConfirmModal(false)
                                                  }
                                                })
                                                setShowConfirmModal(true)
                                              }
                                            }
                                            e.target.value = ''
                                          }
                                        }}
                                        className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                      >
                                        <option value="">Add KDS Station...</option>
                                        {kdsStations.filter(s => s.is_active && !assignmentsByStation.has(s.id)).map(station => (
                                          <option key={station.id} value={station.id}>{station.name}</option>
                                        ))}
                                      </select>
                                    </div>
                                  )}
                                  
                                  {assignments.length === 0 && (
                                    <div className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                                      No category assignments yet. Add a KDS station above and assign categories to it.
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )
                        })}
                        {floorPlans.length === 0 && (
                          <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                            No floor plans added yet. Click "Add Floor Plan" to create one.
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* User Settings */}
            {activeTab === 'users' && (
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    Users & Roles Management
                  </h3>
                </div>

                {/* Sub-tabs */}
                <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
                  <nav className="-mb-px flex space-x-8">
                    {[
                      { key: 'users', label: 'Users' },
                      { key: 'roles', label: 'Roles' },
                      { key: 'permissions', label: 'Permissions' }
                    ].map((tab) => (
                      <button
                        key={tab.key}
                        onClick={() => setActiveUsersSubTab(tab.key as any)}
                        className={`py-2 px-1 border-b-2 font-medium text-sm ${
                          activeUsersSubTab === tab.key
                            ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                        }`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </nav>
                </div>

                {/* Users Tab */}
                {activeUsersSubTab === 'users' && (
                  <div>
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="text-md font-medium text-gray-900 dark:text-white">Users</h4>
                      <button
                        onClick={() => {
                          setSelectedUser(null)
                          setUserFormData({ email: '', password: '', name: '', phone: '', role_ids: [], default_floor_plan_id: '', kds_can_start_order: true, kds_can_mark_ready: true, kds_can_mark_complete: true, kds_station_ids: [] })
                          setShowUserModal(true)
                        }}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium"
                      >
                        Add User
                      </button>
                    </div>

                    {usersLoading ? (
                      <div className="text-center py-8">Loading users...</div>
                    ) : (
                      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                          <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Name</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Email</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Phone</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Roles</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {users.map((user) => (
                              <tr key={user.id}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">{user.name}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{user.email}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{user.phone || '-'}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                  <div className="flex flex-wrap gap-1">
                                    {user.roles?.map((role: any) => (
                                      <span key={role.id} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                        {role.name}
                                      </span>
                                    )) || <span className="text-gray-400">No roles</span>}
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                  <button
                                    onClick={async () => {
                                      setSelectedUser(user)
                                      try {
                                        const res = await apiService.getUser(user.id)
                                        const u = res.success && res.data ? res.data : user
                                        setUserFormData({
                                          email: (u as any).email || user.email,
                                          password: '',
                                          name: (u as any).name || user.name,
                                          phone: (u as any).phone || user.phone || '',
                                          role_ids: (u as any).roles?.map((r: any) => r.id) || user.roles?.map((r: any) => r.id) || [],
                                          default_floor_plan_id: (u as any).default_floor_plan_id || (user as any).default_floor_plan_id || '',
                                          kds_can_start_order: (u as any).kds_can_start_order !== false,
                                          kds_can_mark_ready: (u as any).kds_can_mark_ready !== false,
                                          kds_can_mark_complete: (u as any).kds_can_mark_complete !== false,
                                          kds_station_ids: Array.isArray((u as any).kds_station_ids) ? (u as any).kds_station_ids : []
                                        })
                                      } catch {
                                        setUserFormData({
                                          email: user.email,
                                          password: '',
                                          name: user.name,
                                          phone: user.phone || '',
                                          role_ids: user.roles?.map((r: any) => r.id) || [],
                                          default_floor_plan_id: (user as any).default_floor_plan_id || '',
                                          kds_can_start_order: true,
                                          kds_can_mark_ready: true,
                                          kds_can_mark_complete: true,
                                          kds_station_ids: []
                                        })
                                      }
                                      setShowUserModal(true)
                                    }}
                                    className="text-blue-600 hover:text-blue-900 dark:text-blue-400 mr-3"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => {
                                      setConfirmModalConfig({
                                        message: `Are you sure you want to delete user ${user.name}?`,
                                        onConfirm: async () => {
                                          setShowConfirmModal(false)
                                          const response = await apiService.deleteUser(user.id)
                                          if (response.success) {
                                            await loadUsers()
                                            setNotificationMessage({ type: 'success', message: 'User deleted successfully!' })
                                          } else {
                                            setNotificationMessage({ type: 'error', message: response.error || 'Failed to delete user' })
                                          }
                                        }
                                      })
                                      setShowConfirmModal(true)
                                    }}
                                    className="text-red-600 hover:text-red-900 dark:text-red-400"
                                  >
                                    Delete
                                  </button>
                                </td>
                              </tr>
                            ))}
                            {users.length === 0 && (
                              <tr>
                                <td colSpan={5} className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                                  No users found
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {/* Roles Tab */}
                {activeUsersSubTab === 'roles' && (
                  <div>
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="text-md font-medium text-gray-900 dark:text-white">Roles</h4>
                      <button
                        onClick={() => {
                          setSelectedRole(null)
                          setRoleFormData({ name: '', description: '' })
                          setShowRoleModal(true)
                        }}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium"
                      >
                        Add Role
                      </button>
                    </div>

                    {rolesLoading ? (
                      <div className="text-center py-8">Loading roles...</div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {roles.map((role) => (
                          <div key={role.id} className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                            <div className="flex justify-between items-start mb-2">
                              <h5 className="text-lg font-medium text-gray-900 dark:text-white">{role.name}</h5>
                              {role.is_system_role && (
                                <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-2 py-1 rounded">System</span>
                              )}
                            </div>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{role.description || 'No description'}</p>
                            <div className="flex space-x-2">
                              <button
                                onClick={async () => {
                                  await loadRoleWithPermissions(role.id)
                                  setShowRolePermissionsModal(true)
                                }}
                                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded text-sm font-medium"
                              >
                                Manage Permissions
                              </button>
                              {!role.is_system_role && (
                                <>
                                  <button
                                    onClick={() => {
                                      setSelectedRole(role)
                                      setRoleFormData({ name: role.name, description: role.description || '' })
                                      setShowRoleModal(true)
                                    }}
                                    className="flex-1 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 px-3 py-2 rounded text-sm font-medium"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => {
                                      setConfirmModalConfig({
                                        message: `Are you sure you want to delete role ${role.name}?`,
                                        onConfirm: async () => {
                                          setShowConfirmModal(false)
                                          const response = await apiService.deleteRole(role.id)
                                          if (response.success) {
                                            await loadRoles()
                                            setNotificationMessage({ type: 'success', message: 'Role deleted successfully!' })
                                          } else {
                                            setNotificationMessage({ type: 'error', message: response.error || 'Failed to delete role' })
                                          }
                                        }
                                      })
                                      setShowConfirmModal(true)
                                    }}
                                    className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded text-sm font-medium"
                                  >
                                    Delete
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        ))}
                        {roles.length === 0 && (
                          <div className="col-span-full text-center py-8 text-gray-500 dark:text-gray-400">No roles found</div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Permissions Tab */}
                {activeUsersSubTab === 'permissions' && (
                  <div>
                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">Permissions</h4>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Select role to manage permissions</label>
                      <select
                        value={permissionsTabSelectedRoleId ?? ''}
                        onChange={(e) => setPermissionsTabSelectedRoleId(e.target.value || null)}
                        className="w-full max-w-xs border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="">— Select role —</option>
                        {roles.map((r) => (
                          <option key={r.id} value={r.id}>{r.name}</option>
                        ))}
                      </select>
                      {permissionsTabSelectedRoleId && (
                        <button
                          type="button"
                          onClick={() => { setPermissionsTabSelectedRoleId(null); setPermissionsTabRole(null) }}
                          className="ml-2 text-sm text-gray-600 dark:text-gray-400 hover:underline"
                        >
                          Reset
                        </button>
                      )}
                    </div>
                    {!permissionsTabSelectedRoleId ? (
                      <p className="text-gray-600 dark:text-gray-400">Select a role above to check or uncheck page access and permissions. Changes save immediately when you toggle a checkbox.</p>
                    ) : (
                      <div className="space-y-6">
                        {/* Start page for this role */}
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                          <h5 className="text-lg font-medium text-gray-900 dark:text-white mb-3">Start page</h5>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">Where users with this role land after login. Default = Dashboard.</p>
                          <div className="max-w-xs">
                            <select
                              value={permissionsTabRole?.start_page ?? ''}
                              onChange={async (e) => {
                                const value = e.target.value || null
                                if (!permissionsTabSelectedRoleId) return
                                try {
                                  await apiService.updateRole(permissionsTabSelectedRoleId, { start_page: value })
                                  await loadRoleForPermissionsTab(permissionsTabSelectedRoleId)
                                  setNotificationMessage({ type: 'success', message: 'Start page updated' })
                                } catch (err) {
                                  console.error(err)
                                  setNotificationMessage({ type: 'error', message: 'Failed to update start page' })
                                }
                              }}
                              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            >
                              <option value="">Default (Dashboard)</option>
                              {PAGE_PERMISSIONS.map((page) => (
                                <option key={page.path} value={page.path}>{page.label}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        {/* Page access */}
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                          <h5 className="text-lg font-medium text-gray-900 dark:text-white mb-3">Page access</h5>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">Grant access to each app page. Checked = role has that page’s required permission.</p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {PAGE_PERMISSIONS.map((page) => {
                              const perm = allPermissions.find((p: any) => p.name === page.requiredPermission)
                              const hasIt = perm && permissionsTabRole?.permissions?.some((p: any) => p.id === perm.id)
                              return (
                                <label key={page.path} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                                  <input
                                    type="checkbox"
                                    checked={!!hasIt}
                                    disabled={!perm}
                                    onChange={async () => {
                                      if (!perm || !permissionsTabSelectedRoleId) return
                                      try {
                                        if (hasIt) {
                                          await apiService.removePermissionFromRole(permissionsTabSelectedRoleId, perm.id)
                                        } else {
                                          await apiService.assignPermissionsToRole(permissionsTabSelectedRoleId, [perm.id])
                                        }
                                        await loadRoleForPermissionsTab(permissionsTabSelectedRoleId)
                                      } catch (e) {
                                        console.error(e)
                                      }
                                    }}
                                    className="rounded border-gray-300 dark:border-gray-600 text-indigo-600"
                                  />
                                  <span>{page.label}</span>
                                  {!perm && <span className="text-xs text-amber-600 dark:text-amber-400">(Permission not in database)</span>}
                                </label>
                              )
                            })}
                          </div>
                        </div>
                        {/* Permissions (CRUD/operations) */}
                        <div className="space-y-4">
                          {Object.entries(groupedPermissions).map(([category, resources]: [string, any]) => (
                            <div key={category} className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                              <h5 className="text-lg font-medium text-gray-900 dark:text-white mb-3 capitalize">{category}</h5>
                              {Object.entries(resources).map(([resource, perms]: [string, any]) => (
                                <div key={resource} className="mb-4">
                                  <h6 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 capitalize">{resource}</h6>
                                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                                    {Array.isArray(perms) && perms.map((perm: any) => {
                                      const hasIt = permissionsTabRole?.permissions?.some((p: any) => p.id === perm.id)
                                      return (
                                        <label key={perm.id} className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 p-2 rounded cursor-pointer">
                                          <input
                                            type="checkbox"
                                            checked={!!hasIt}
                                            onChange={async () => {
                                              if (!permissionsTabSelectedRoleId) return
                                              try {
                                                if (hasIt) {
                                                  await apiService.removePermissionFromRole(permissionsTabSelectedRoleId, perm.id)
                                                } else {
                                                  await apiService.assignPermissionsToRole(permissionsTabSelectedRoleId, [perm.id])
                                                }
                                                await loadRoleForPermissionsTab(permissionsTabSelectedRoleId)
                                              } catch (e) {
                                                console.error(e)
                                              }
                                            }}
                                            className="mt-0.5 rounded border-gray-300 dark:border-gray-600 text-indigo-600"
                                          />
                                          <div>
                                            <div className="font-medium">{perm.name}</div>
                                            <div className="text-xs text-gray-500 dark:text-gray-500">{perm.description || perm.action}</div>
                                          </div>
                                        </label>
                                      )
                                    })}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* User Modal */}
                {showUserModal && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                        {selectedUser ? 'Edit User' : 'Add User'}
                      </h3>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Name</label>
                          <input
                            type="text"
                            value={userFormData.name}
                            onChange={(e) => setUserFormData({ ...userFormData, name: e.target.value })}
                            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Email</label>
                          <input
                            type="email"
                            value={userFormData.email}
                            onChange={(e) => setUserFormData({ ...userFormData, email: e.target.value })}
                            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            disabled={!!selectedUser}
                          />
                        </div>
                        {!selectedUser && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Password</label>
                            <input
                              type="password"
                              value={userFormData.password}
                              onChange={(e) => setUserFormData({ ...userFormData, password: e.target.value })}
                              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            />
                          </div>
                        )}
                        {selectedUser && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">New Password (leave blank to keep current)</label>
                            <input
                              type="password"
                              value={userFormData.password}
                              onChange={(e) => setUserFormData({ ...userFormData, password: e.target.value })}
                              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            />
                          </div>
                        )}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Phone</label>
                          <input
                            type="tel"
                            value={userFormData.phone}
                            onChange={(e) => setUserFormData({ ...userFormData, phone: e.target.value })}
                            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Roles</label>
                          <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-md p-2">
                            {roles.map((role) => (
                              <label key={role.id} className="flex items-center">
                                <input
                                  type="checkbox"
                                  checked={userFormData.role_ids.includes(role.id)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setUserFormData({ ...userFormData, role_ids: [...userFormData.role_ids, role.id] })
                                    } else {
                                      setUserFormData({ ...userFormData, role_ids: userFormData.role_ids.filter(id => id !== role.id) })
                                    }
                                  }}
                                  className="mr-2"
                                />
                                <span className="text-sm text-gray-700 dark:text-gray-300">{role.name}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                        {userFormData.role_ids.some(rid => roles.find(r => r.id === rid)?.name === 'Waiter') && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Default floor (Waiter)</label>
                            <select
                              value={userFormData.default_floor_plan_id}
                              onChange={(e) => setUserFormData({ ...userFormData, default_floor_plan_id: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                            >
                              <option value="">No default (show All Floors)</option>
                              {floorPlans.filter(fp => fp.is_active !== false).map((fp) => (
                                <option key={fp.id} value={fp.id}>{fp.name}</option>
                              ))}
                            </select>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">When this waiter logs in to the Waiter Module, this floor will be selected by default. They can still switch to All Floors.</p>
                          </div>
                        )}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">KDS permissions</label>
                          <div className="space-y-2">
                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                checked={userFormData.kds_can_start_order}
                                onChange={(e) => setUserFormData({ ...userFormData, kds_can_start_order: e.target.checked })}
                                className="mr-2"
                              />
                              <span className="text-sm text-gray-700 dark:text-gray-300">Can start order</span>
                            </label>
                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                checked={userFormData.kds_can_mark_ready}
                                onChange={(e) => setUserFormData({ ...userFormData, kds_can_mark_ready: e.target.checked })}
                                className="mr-2"
                              />
                              <span className="text-sm text-gray-700 dark:text-gray-300">Can mark ready</span>
                            </label>
                            <label className="flex items-center">
                              <input
                                type="checkbox"
                                checked={userFormData.kds_can_mark_complete}
                                onChange={(e) => setUserFormData({ ...userFormData, kds_can_mark_complete: e.target.checked })}
                                className="mr-2"
                              />
                              <span className="text-sm text-gray-700 dark:text-gray-300">Can mark complete</span>
                            </label>
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Applied when this user uses the Kitchen Display System (KDS).</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">KDS stations</label>
                          <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-md p-2">
                            {kdsStations.filter(s => s.is_active !== false).map((station) => (
                              <label key={station.id} className="flex items-center">
                                <input
                                  type="checkbox"
                                  checked={userFormData.kds_station_ids.includes(station.id)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setUserFormData({ ...userFormData, kds_station_ids: [...userFormData.kds_station_ids, station.id] })
                                    } else {
                                      setUserFormData({ ...userFormData, kds_station_ids: userFormData.kds_station_ids.filter(id => id !== station.id) })
                                    }
                                  }}
                                  className="mr-2"
                                />
                                <span className="text-sm text-gray-700 dark:text-gray-300">{station.name}</span>
                              </label>
                            ))}
                            {kdsStations.filter(s => s.is_active !== false).length === 0 && (
                              <span className="text-xs text-gray-500 dark:text-gray-400">No KDS stations. Create stations in Settings first.</span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">If none selected, user sees all stations. If one or more selected, user only sees those stations on KDS.</p>
                        </div>
                      </div>
                      <div className="flex justify-end space-x-3 mt-6">
                        <button
                          onClick={() => setShowUserModal(false)}
                          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={async () => {
                            if (!userFormData.name || !userFormData.email || (!selectedUser && !userFormData.password)) {
                              alert('Please fill in all required fields')
                              return
                            }
                            
                            const response = selectedUser
                              ? await apiService.updateUser(selectedUser.id, {
                                  name: userFormData.name,
                                  email: userFormData.email,
                                  phone: userFormData.phone,
                                  password: userFormData.password || undefined,
                                  role_ids: userFormData.role_ids,
                                  default_floor_plan_id: userFormData.default_floor_plan_id || undefined,
                                  kds_can_start_order: userFormData.kds_can_start_order,
                                  kds_can_mark_ready: userFormData.kds_can_mark_ready,
                                  kds_can_mark_complete: userFormData.kds_can_mark_complete,
                                  kds_station_ids: userFormData.kds_station_ids
                                })
                              : await apiService.createUser({
                                  ...userFormData,
                                  default_floor_plan_id: userFormData.default_floor_plan_id || undefined
                                })
                            
                            if (response.success) {
                              setShowUserModal(false)
                              await loadUsers()
                            } else {
                              alert(response.error || 'Failed to save user')
                            }
                          }}
                          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
                        >
                          {selectedUser ? 'Update' : 'Create'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Role Modal */}
                {showRoleModal && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                        {selectedRole ? 'Edit Role' : 'Add Role'}
                      </h3>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Name</label>
                          <input
                            type="text"
                            value={roleFormData.name}
                            onChange={(e) => setRoleFormData({ ...roleFormData, name: e.target.value })}
                            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Description</label>
                          <textarea
                            value={roleFormData.description}
                            onChange={(e) => setRoleFormData({ ...roleFormData, description: e.target.value })}
                            rows={3}
                            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />
                        </div>
                      </div>
                      <div className="flex justify-end space-x-3 mt-6">
                        <button
                          onClick={() => setShowRoleModal(false)}
                          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={async () => {
                            if (!roleFormData.name) {
                              alert('Please enter a role name')
                              return
                            }
                            
                            const response = selectedRole
                              ? await apiService.updateRole(selectedRole.id, roleFormData)
                              : await apiService.createRole(roleFormData)
                            
                            if (response.success) {
                              setShowRoleModal(false)
                              await loadRoles()
                            } else {
                              alert(response.error || 'Failed to save role')
                            }
                          }}
                          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
                        >
                          {selectedRole ? 'Update' : 'Create'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Role Permissions Modal */}
                {showRolePermissionsModal && selectedRole && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                        Manage Permissions: {selectedRole.name}
                      </h3>
                      <div className="space-y-6">
                        {/* Page access */}
                        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                          <h5 className="text-md font-medium text-gray-900 dark:text-white mb-3">Page access</h5>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">Grant access to each app page. Checked = role has that page’s required permission.</p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {PAGE_PERMISSIONS.map((page) => {
                              const perm = allPermissions.find((p: any) => p.name === page.requiredPermission)
                              const hasIt = perm && selectedRole.permissions?.some((p: any) => p.id === perm.id)
                              return (
                                <label key={page.path} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                                  <input
                                    type="checkbox"
                                    checked={!!hasIt}
                                    disabled={!perm}
                                    onChange={async () => {
                                      if (!perm) return
                                      try {
                                        if (hasIt) {
                                          await apiService.removePermissionFromRole(selectedRole.id, perm.id)
                                        } else {
                                          await apiService.assignPermissionsToRole(selectedRole.id, [perm.id])
                                        }
                                        await loadRoleWithPermissions(selectedRole.id)
                                      } catch (e) {
                                        console.error(e)
                                      }
                                    }}
                                    className="rounded border-gray-300 dark:border-gray-600 text-indigo-600"
                                  />
                                  <span>{page.label}</span>
                                  {!perm && <span className="text-xs text-amber-600 dark:text-amber-400">(Permission not in database)</span>}
                                </label>
                              )
                            })}
                          </div>
                        </div>
                        {/* Permissions (CRUD/operations) */}
                        {Object.entries(groupedPermissions).map(([category, resources]: [string, any]) => (
                          <div key={category} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                            <h5 className="text-md font-medium text-gray-900 dark:text-white mb-3 capitalize">{category}</h5>
                            {Object.entries(resources).map(([resource, perms]: [string, any]) => (
                              <div key={resource} className="mb-4">
                                <h6 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 capitalize">{resource}</h6>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                  {Array.isArray(perms) && perms.map((perm: any) => {
                                    const hasPermission = selectedRole.permissions?.some((p: any) => p.id === perm.id)
                                    return (
                                      <label key={perm.id} className="flex items-center p-2 hover:bg-gray-50 dark:hover:bg-gray-700 rounded">
                                        <input
                                          type="checkbox"
                                          checked={hasPermission}
                                          onChange={async (e) => {
                                            if (e.target.checked) {
                                              const response = await apiService.assignPermissionsToRole(selectedRole.id, [perm.id])
                                              if (response.success) {
                                                await loadRoleWithPermissions(selectedRole.id)
                                              }
                                            } else {
                                              const response = await apiService.removePermissionFromRole(selectedRole.id, perm.id)
                                              if (response.success) {
                                                await loadRoleWithPermissions(selectedRole.id)
                                              }
                                            }
                                          }}
                                          className="mr-2"
                                        />
                                        <div className="flex-1">
                                          <div className="text-sm font-medium text-gray-900 dark:text-white">{perm.name}</div>
                                          <div className="text-xs text-gray-500 dark:text-gray-400">{perm.description || perm.action}</div>
                                        </div>
                                      </label>
                                    )
                                  })}
                                </div>
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                      <div className="flex justify-end mt-6">
                        <button
                          onClick={() => setShowRolePermissionsModal(false)}
                          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Payment Settings */}
            {activeTab === 'payments' && (
              <div className="p-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-6">
                  Payment Configuration
                </h3>
                
                <div className="space-y-6">
                  <div>
                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">
                      Accepted Payment Methods
                    </h4>
                    <div className="space-y-3">
                      {Object.entries(paymentSettings.methods).map(([method, enabled]) => (
                        <label key={method} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={enabled}
                            onChange={(e) => setPaymentSettings({
                              ...paymentSettings,
                              methods: { ...paymentSettings.methods, [method]: e.target.checked }
                            })}
                            className="mr-3"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300 capitalize">{method}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">
                      Payment Options
                    </h4>
                    <div className="space-y-3">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={paymentSettings.taxInclusive}
                          onChange={(e) => setPaymentSettings({ ...paymentSettings, taxInclusive: e.target.checked })}
                          className="mr-3"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Include tax in prices</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={paymentSettings.autoPrintReceipt}
                          onChange={(e) => setPaymentSettings({ ...paymentSettings, autoPrintReceipt: e.target.checked })}
                          className="mr-3"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Auto-print receipts</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={paymentSettings.requireCustomerInfo}
                          onChange={(e) => setPaymentSettings({ ...paymentSettings, requireCustomerInfo: e.target.checked })}
                          className="mr-3"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Require customer information</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Kitchen Display System */}
            {activeTab === 'kds' && (
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    Kitchen Display Stations
                  </h3>
                  <button
                    onClick={handleAddKDS}
                    className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                  >
                    <PlusIcon className="h-4 w-4 mr-2" />
                    Add KDS Station
                  </button>
                </div>
                
                <div className="space-y-6">
                  {/* KDS Settings */}
                  <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">
                      KDS Settings
                    </h4>
                    
                    <div className="space-y-6">
                      {/* Bumping Mode */}
                      <div>
                        <h5 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                          Bumping Mode
                        </h5>
                        <div className="space-y-2">
                          <div className="flex items-center">
                            <input
                              type="radio"
                              id="kdsBumpItemWise"
                              name="kdsBumpingMode"
                              value="item_wise"
                              checked={posSettings.kdsBumpingMode === 'item_wise' || posSettings.kdsBumpingMode === undefined}
                              onChange={() => setPOSSettings({ ...posSettings, kdsBumpingMode: 'item_wise' })}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                            />
                            <label htmlFor="kdsBumpItemWise" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                              Item-Wise Bumping
                            </label>
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 ml-6">
                            When an item is bumped, only that specific item moves to the top. Other items in the same order remain in their original position.
                          </p>
                          <div className="flex items-center">
                            <input
                              type="radio"
                              id="kdsBumpOrderWise"
                              name="kdsBumpingMode"
                              value="order_wise"
                              checked={posSettings.kdsBumpingMode === 'order_wise'}
                              onChange={() => setPOSSettings({ ...posSettings, kdsBumpingMode: 'order_wise' })}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                            />
                            <label htmlFor="kdsBumpOrderWise" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                              Order-Wise Bumping
                            </label>
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 ml-6">
                            When an item is bumped, the entire order moves to the top. All items in the order are prioritized together.
                          </p>
                        </div>
                      </div>

                      {/* Start Preparing Mode */}
                      <div>
                        <h5 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                          Start Preparing Mode
                        </h5>
                        <div className="space-y-2">
                          <div className="flex items-center">
                            <input
                              type="radio"
                              id="kdsStartItemWise"
                              name="kdsStartPreparingMode"
                              value="item_wise"
                              checked={posSettings.kdsStartPreparingMode === 'item_wise' || posSettings.kdsStartPreparingMode === undefined}
                              onChange={() => setPOSSettings({ ...posSettings, kdsStartPreparingMode: 'item_wise' })}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                            />
                            <label htmlFor="kdsStartItemWise" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                              Item-Wise Start
                            </label>
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 ml-6">
                            When you click "Start" on an item, only that specific item will start preparing. Other items in the same order remain in "pending" status.
                          </p>
                          <div className="flex items-center">
                            <input
                              type="radio"
                              id="kdsStartOrderWise"
                              name="kdsStartPreparingMode"
                              value="order_wise"
                              checked={posSettings.kdsStartPreparingMode === 'order_wise'}
                              onChange={() => setPOSSettings({ ...posSettings, kdsStartPreparingMode: 'order_wise' })}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                            />
                            <label htmlFor="kdsStartOrderWise" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                              Order-Wise Start
                            </label>
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400 ml-6">
                            When you click "Start" on any item in an order, all items in that order will start preparing simultaneously.
                          </p>
                        </div>
                      </div>

                      {/* Header Color Thresholds */}
                      <div className="mt-6">
                        <h5 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                          Header Color Thresholds (Minutes)
                        </h5>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                          Configure when the order header changes color based on elapsed time since order placement.
                        </p>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              Green Color Threshold (minutes)
                            </label>
                            <input
                              type="number"
                              min="0"
                              value={posSettings.kdsColorThresholdGreen ?? 10}
                              onChange={(e) => setPOSSettings({ ...posSettings, kdsColorThresholdGreen: parseInt(e.target.value) || 10 })}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                            />
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              Orders less than this time will show green header
                            </p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              Yellow Color Threshold (minutes)
                            </label>
                            <input
                              type="number"
                              min="0"
                              value={posSettings.kdsColorThresholdYellow ?? 15}
                              onChange={(e) => setPOSSettings({ ...posSettings, kdsColorThresholdYellow: parseInt(e.target.value) || 15 })}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                            />
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              Orders between green and yellow thresholds will show dark yellow header. Orders above this will show red header.
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* New order sound (only when user is on KDS screen) */}
                      <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-600">
                        <h5 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                          New order sound
                        </h5>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                          Play a sound when a new order is received, only while the KDS screen is open and visible.
                        </p>
                        <div className="space-y-4">
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              id="kdsNewOrderSoundEnabled"
                              checked={posSettings.kdsNewOrderSoundEnabled ?? false}
                              onChange={(e) => setPOSSettings({ ...posSettings, kdsNewOrderSoundEnabled: e.target.checked })}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <label htmlFor="kdsNewOrderSoundEnabled" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                              Play sound when new order is received
                            </label>
                          </div>
                          {posSettings.kdsNewOrderSoundEnabled && (
                            <>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                  Volume ({posSettings.kdsNewOrderSoundVolume ?? 70}%)
                                </label>
                                <input
                                  type="range"
                                  min="0"
                                  max="100"
                                  value={posSettings.kdsNewOrderSoundVolume ?? 70}
                                  onChange={(e) => setPOSSettings({ ...posSettings, kdsNewOrderSoundVolume: parseInt(e.target.value, 10) })}
                                  className="w-full h-2 bg-gray-200 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                  Sound URL (optional)
                                </label>
                                <input
                                  type="url"
                                  value={posSettings.kdsNewOrderSoundUrl ?? ''}
                                  onChange={(e) => setPOSSettings({ ...posSettings, kdsNewOrderSoundUrl: e.target.value })}
                                  placeholder="Leave empty for default beep"
                                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                                />
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                  URL to an audio file (e.g. .mp3, .wav). Leave empty to use the default beep.
                                </p>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="mt-6">
                      <button
                        onClick={handleSavePOSSettings}
                        disabled={saving}
                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {saving ? 'Saving...' : 'Save KDS Settings'}
                      </button>
                    </div>
                  </div>

                  {/* KDS Stations List */}
                  <div>
                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">
                      Available Stations
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {kdsStations.map((station) => (
                        <div key={station.id} className="bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex items-center">
                              <div 
                                className="w-4 h-4 rounded-full mr-3"
                                style={{ backgroundColor: station.color }}
                              ></div>
                              <div>
                                <h5 className="font-medium text-gray-900 dark:text-white">
                                  {station.name}
                                </h5>
                                {station.description && (
                                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                    {station.description}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => handleEditKDS(station)}
                                className="p-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteKDS(station)}
                                className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                          <div className="mt-3 flex items-center justify-between">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              station.is_active 
                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                            }`}>
                              {station.is_active ? 'Active' : 'Inactive'}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              Order: {station.sort_order}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Notifications */}
            {activeTab === 'notifications' && (
              <div className="p-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-6">
                  Notification Settings
                </h3>
                
                <div className="space-y-6">
                  <div>
                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">
                      Notification Channels
                    </h4>
                    <div className="space-y-3">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={userSettings.notifications.email}
                          onChange={(e) => setUserSettings({
                            ...userSettings,
                            notifications: { ...userSettings.notifications, email: e.target.checked }
                          })}
                          className="mr-3"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Email notifications</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={userSettings.notifications.sms}
                          onChange={(e) => setUserSettings({
                            ...userSettings,
                            notifications: { ...userSettings.notifications, sms: e.target.checked }
                          })}
                          className="mr-3"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">SMS notifications</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={userSettings.notifications.push}
                          onChange={(e) => setUserSettings({
                            ...userSettings,
                            notifications: { ...userSettings.notifications, push: e.target.checked }
                          })}
                          className="mr-3"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Push notifications</span>
                      </label>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">
                      Notification Types
                    </h4>
                    <div className="space-y-3">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={userSettings.notifications.lowStock}
                          onChange={(e) => setUserSettings({
                            ...userSettings,
                            notifications: { ...userSettings.notifications, lowStock: e.target.checked }
                          })}
                          className="mr-3"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Low stock alerts</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={userSettings.notifications.newOrders}
                          onChange={(e) => setUserSettings({
                            ...userSettings,
                            notifications: { ...userSettings.notifications, newOrders: e.target.checked }
                          })}
                          className="mr-3"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">New order alerts</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={userSettings.notifications.payments}
                          onChange={(e) => setUserSettings({
                            ...userSettings,
                            notifications: { ...userSettings.notifications, payments: e.target.checked }
                          })}
                          className="mr-3"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Payment confirmations</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Integrations */}
            {activeTab === 'integrations' && (
              <div className="p-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-6">
                  Third-party Integrations
                </h3>
                
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-gray-900 dark:text-white">ZATCA E-Invoicing</h4>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                          <CheckIcon className="h-3 w-3 mr-1" />
                          Connected
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                        Saudi Arabia e-invoicing compliance
                      </p>
                      <button className="text-blue-600 hover:text-blue-500 text-sm font-medium">
                        Configure
                      </button>
                    </div>

                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-gray-900 dark:text-white">Payment Gateway</h4>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                          <XMarkIcon className="h-3 w-3 mr-1" />
                          Not Connected
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                        Credit card processing
                      </p>
                      <button className="text-blue-600 hover:text-blue-500 text-sm font-medium">
                        Connect
                      </button>
                    </div>

                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-gray-900 dark:text-white">Delivery Platform</h4>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
                          <XMarkIcon className="h-3 w-3 mr-1" />
                          Not Connected
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                        Online delivery integration
                      </p>
                      <button className="text-blue-600 hover:text-blue-500 text-sm font-medium">
                        Connect
                      </button>
                    </div>

                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-gray-900 dark:text-white">Accounting Software</h4>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
                          <XMarkIcon className="h-3 w-3 mr-1" />
                          Not Connected
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                        Financial data sync
                      </p>
                      <button className="text-blue-600 hover:text-blue-500 text-sm font-medium">
                        Connect
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Accounting */}
            {activeTab === 'accounting' && (
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                      Accounting Settings
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Manage expense categories
                    </p>
                  </div>
                </div>

                {/* Expense Categories Section */}
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-md font-medium text-gray-900 dark:text-white flex items-center gap-2">
                      <CalculatorIcon className="h-5 w-5" />
                      Expense Categories
                    </h4>
                    <button
                      onClick={handleAddExpenseCategory}
                      className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                    >
                      <PlusIcon className="h-4 w-4" />
                      Add Category
                    </button>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Category Name</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Account</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Description</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Status</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {expenseCategories.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                              No expense categories found
                            </td>
                          </tr>
                        ) : (
                          expenseCategories.map((category) => (
                            <tr key={category.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                                {category.category_name}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                {category.chart_of_accounts 
                                  ? `${category.chart_of_accounts.account_code} - ${category.chart_of_accounts.account_name}`
                                  : 'No account'}
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                                {category.description || '-'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                  category.is_active
                                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                    : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                                }`}>
                                  {category.is_active ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <button
                                  onClick={() => handleEditExpenseCategory(category)}
                                  className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 mr-3"
                                >
                                  <PencilIcon className="w-5 h-5" />
                                </button>
                                <button
                                  onClick={() => handleDeleteExpenseCategory(category.id)}
                                  className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                                >
                                  <TrashIcon className="w-5 h-5" />
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Allergens */}
            {activeTab === 'allergens' && (
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                      Allergen Management
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Create and manage allergens with icons. Assign them to menu items, variants, and addons; icons will show in POS, Waiter Module, and customer frontend.
                    </p>
                  </div>
                  <button
                    onClick={() => openAllergenModal()}
                    className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                  >
                    <PlusIcon className="h-4 w-4" />
                    Add Allergen
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Icon</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Sort order</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {allergensList.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                            No allergens yet. Add one to use in menu items.
                          </td>
                        </tr>
                      ) : (
                        allergensList.map((a) => (
                          <tr key={a.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                            <td className="px-6 py-4 whitespace-nowrap">
                              {a.icon ? (
                                <img src={resolveImageUrl(a.icon)} alt="" className="w-8 h-8 object-contain rounded" />
                              ) : (
                                <span className="w-8 h-8 inline-flex items-center justify-center rounded bg-gray-200 dark:bg-gray-600 text-gray-500 dark:text-gray-400 text-xs">—</span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">{a.name}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{a.sort_order}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <button
                                onClick={() => openAllergenModal(a)}
                                className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 mr-3"
                                title="Edit"
                              >
                                <PencilIcon className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => handleDeleteAllergen(a)}
                                className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                                title="Delete"
                              >
                                <TrashIcon className="w-5 h-5" />
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Inventory */}
            {activeTab === 'inventory' && (
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                      Inventory Settings
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Manage inventory locations
                    </p>
                  </div>
                </div>

                {/* Categories Section */}
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-md font-medium text-gray-900 dark:text-white flex items-center gap-2">
                      <CubeIcon className="h-5 w-5" />
                      Categories
                    </h4>
                    <button
                      onClick={handleAddCategory}
                      className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                    >
                      <PlusIcon className="h-4 w-4" />
                      Add Category
                    </button>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Category Name</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Description</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Status</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {inventoryCategories.length === 0 ? (
                          <tr>
                            <td colSpan={4} className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                              No categories found. Add a category to get started.
                            </td>
                          </tr>
                        ) : (
                          inventoryCategories.map((category) => (
                            <tr key={category.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                                {category.name}
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                                {category.description || '-'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                  category.is_active
                                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                    : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                                }`}>
                                  {category.is_active ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <button
                                  onClick={() => handleEditCategory(category)}
                                  className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 mr-3"
                                >
                                  <PencilIcon className="w-5 h-5" />
                                </button>
                                <button
                                  onClick={() => handleDeleteCategory(category.id)}
                                  className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                                >
                                  <TrashIcon className="w-5 h-5" />
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Locations Section */}
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-md font-medium text-gray-900 dark:text-white flex items-center gap-2">
                      <CubeIcon className="h-5 w-5" />
                      Locations
                    </h4>
                    <button
                      onClick={handleAddLocation}
                      className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                    >
                      <PlusIcon className="h-4 w-4" />
                      Add Location
                    </button>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Location Name</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Parent Location</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Description</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Status</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {inventoryLocations.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                              No locations found. Add a location to get started.
                            </td>
                          </tr>
                        ) : (
                          inventoryLocations.map((location) => (
                            <tr key={location.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                                {location.name}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                {location.parent_id 
                                  ? (inventoryLocations.find(l => l.id === location.parent_id)?.name || 'N/A')
                                  : 'Main Location'}
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                                {location.description || '-'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                  location.is_active
                                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                    : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                                }`}>
                                  {location.is_active ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <button
                                  onClick={() => handleEditLocation(location)}
                                  className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 mr-3"
                                >
                                  <PencilIcon className="w-5 h-5" />
                                </button>
                                <button
                                  onClick={() => handleDeleteLocation(location.id)}
                                  className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                                >
                                  <TrashIcon className="w-5 h-5" />
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Security */}
            {activeTab === 'data-management' && user?.roles?.some(r => r.name === 'Super Admin' || r.name === 'Admin') && (
              <div className="p-6">
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    Data Management
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Clear all data from the system. These actions are irreversible and should only be used with extreme caution.
                  </p>
                </div>

                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
                  <div className="flex items-start">
                    <ExclamationTriangleIcon className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 mr-3 flex-shrink-0" />
                    <div>
                      <h4 className="text-sm font-medium text-red-800 dark:text-red-200 mb-1">
                        Warning: Destructive Actions
                      </h4>
                      <p className="text-sm text-red-700 dark:text-red-300">
                        All data clearing operations are permanent and cannot be undone. Please ensure you have proper backups before proceeding.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {/* Clear Orders */}
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-gray-900 dark:text-white">Orders</h4>
                        <TrashIcon className="w-5 h-5 text-gray-400" />
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                        Clear all orders, order items, payments, and invoices
                      </p>
                      <button
                        onClick={() => {
                          setConfirmModalConfig({
                            message: 'Are you sure you want to delete ALL orders, order items, payments, and invoices? This action cannot be undone.',
                            onConfirm: async () => {
                              try {
                                const result = await apiService.clearOrders()
                                if (result.success) {
                                  setNotificationMessage({ type: 'success', message: 'All orders cleared successfully' })
                                  setShowNotificationModal(true)
                                } else {
                                  setNotificationMessage({ type: 'error', message: result.error || 'Failed to clear orders' })
                                  setShowNotificationModal(true)
                                }
                              } catch (error: any) {
                                setNotificationMessage({ type: 'error', message: error.message || 'Failed to clear orders' })
                                setShowNotificationModal(true)
                              }
                              setShowConfirmModal(false)
                            }
                          })
                          setShowConfirmModal(true)
                        }}
                        className="w-full px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md text-sm font-medium"
                      >
                        Clear Orders
                      </button>
                    </div>

                    {/* Clear Ledgers */}
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-gray-900 dark:text-white">Ledgers</h4>
                        <TrashIcon className="w-5 h-5 text-gray-400" />
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                        Clear all journal entries and ledger data
                      </p>
                      <button
                        onClick={() => {
                          setConfirmModalConfig({
                            message: 'Are you sure you want to delete ALL journal entries and ledger data? This action cannot be undone.',
                            onConfirm: async () => {
                              try {
                                const result = await apiService.clearLedgers()
                                if (result.success) {
                                  setNotificationMessage({ type: 'success', message: 'All ledgers cleared successfully' })
                                  setShowNotificationModal(true)
                                } else {
                                  setNotificationMessage({ type: 'error', message: result.error || 'Failed to clear ledgers' })
                                  setShowNotificationModal(true)
                                }
                              } catch (error: any) {
                                setNotificationMessage({ type: 'error', message: error.message || 'Failed to clear ledgers' })
                                setShowNotificationModal(true)
                              }
                              setShowConfirmModal(false)
                            }
                          })
                          setShowConfirmModal(true)
                        }}
                        className="w-full px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md text-sm font-medium"
                      >
                        Clear Ledgers
                      </button>
                    </div>

                    {/* Clear Expenses */}
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-gray-900 dark:text-white">Expenses</h4>
                        <TrashIcon className="w-5 h-5 text-gray-400" />
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                        Clear all expenses and expense items
                      </p>
                      <button
                        onClick={() => {
                          setConfirmModalConfig({
                            message: 'Are you sure you want to delete ALL expenses? This action cannot be undone.',
                            onConfirm: async () => {
                              try {
                                const result = await apiService.clearExpenses()
                                if (result.success) {
                                  setNotificationMessage({ type: 'success', message: 'All expenses cleared successfully' })
                                  setShowNotificationModal(true)
                                } else {
                                  setNotificationMessage({ type: 'error', message: result.error || 'Failed to clear expenses' })
                                  setShowNotificationModal(true)
                                }
                              } catch (error: any) {
                                setNotificationMessage({ type: 'error', message: error.message || 'Failed to clear expenses' })
                                setShowNotificationModal(true)
                              }
                              setShowConfirmModal(false)
                            }
                          })
                          setShowConfirmModal(true)
                        }}
                        className="w-full px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md text-sm font-medium"
                      >
                        Clear Expenses
                      </button>
                    </div>

                    {/* Clear Inventory */}
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-gray-900 dark:text-white">Inventory</h4>
                        <TrashIcon className="w-5 h-5 text-gray-400" />
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                        Clear all inventory items and transactions
                      </p>
                      <button
                        onClick={() => {
                          setConfirmModalConfig({
                            message: 'Are you sure you want to delete ALL inventory data? This action cannot be undone.',
                            onConfirm: async () => {
                              try {
                                const result = await apiService.clearInventory()
                                if (result.success) {
                                  setNotificationMessage({ type: 'success', message: 'All inventory cleared successfully' })
                                  setShowNotificationModal(true)
                                } else {
                                  setNotificationMessage({ type: 'error', message: result.error || 'Failed to clear inventory' })
                                  setShowNotificationModal(true)
                                }
                              } catch (error: any) {
                                setNotificationMessage({ type: 'error', message: error.message || 'Failed to clear inventory' })
                                setShowNotificationModal(true)
                              }
                              setShowConfirmModal(false)
                            }
                          })
                          setShowConfirmModal(true)
                        }}
                        className="w-full px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md text-sm font-medium"
                      >
                        Clear Inventory
                      </button>
                    </div>

                    {/* Clear Attendances */}
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-gray-900 dark:text-white">Attendances</h4>
                        <TrashIcon className="w-5 h-5 text-gray-400" />
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                        Clear all attendance records and summaries
                      </p>
                      <button
                        onClick={() => {
                          setConfirmModalConfig({
                            message: 'Are you sure you want to delete ALL attendance data? This action cannot be undone.',
                            onConfirm: async () => {
                              try {
                                const result = await apiService.clearAttendances()
                                if (result.success) {
                                  setNotificationMessage({ type: 'success', message: 'All attendances cleared successfully' })
                                  setShowNotificationModal(true)
                                } else {
                                  setNotificationMessage({ type: 'error', message: result.error || 'Failed to clear attendances' })
                                  setShowNotificationModal(true)
                                }
                              } catch (error: any) {
                                setNotificationMessage({ type: 'error', message: error.message || 'Failed to clear attendances' })
                                setShowNotificationModal(true)
                              }
                              setShowConfirmModal(false)
                            }
                          })
                          setShowConfirmModal(true)
                        }}
                        className="w-full px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md text-sm font-medium"
                      >
                        Clear Attendances
                      </button>
                    </div>

                    {/* Clear Payrolls */}
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-gray-900 dark:text-white">Payrolls</h4>
                        <TrashIcon className="w-5 h-5 text-gray-400" />
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                        Clear all payroll records and deductions
                      </p>
                      <button
                        onClick={() => {
                          setConfirmModalConfig({
                            message: 'Are you sure you want to delete ALL payroll data? This action cannot be undone.',
                            onConfirm: async () => {
                              try {
                                const result = await apiService.clearPayrolls()
                                if (result.success) {
                                  setNotificationMessage({ type: 'success', message: 'All payrolls cleared successfully' })
                                  setShowNotificationModal(true)
                                } else {
                                  setNotificationMessage({ type: 'error', message: result.error || 'Failed to clear payrolls' })
                                  setShowNotificationModal(true)
                                }
                              } catch (error: any) {
                                setNotificationMessage({ type: 'error', message: error.message || 'Failed to clear payrolls' })
                                setShowNotificationModal(true)
                              }
                              setShowConfirmModal(false)
                            }
                          })
                          setShowConfirmModal(true)
                        }}
                        className="w-full px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md text-sm font-medium"
                      >
                        Clear Payrolls
                      </button>
                    </div>

                    {/* Clear Leaves */}
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-gray-900 dark:text-white">Leaves</h4>
                        <TrashIcon className="w-5 h-5 text-gray-400" />
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                        Clear all leave requests and balances
                      </p>
                      <button
                        onClick={() => {
                          setConfirmModalConfig({
                            message: 'Are you sure you want to delete ALL leave data? This action cannot be undone.',
                            onConfirm: async () => {
                              try {
                                const result = await apiService.clearLeaves()
                                if (result.success) {
                                  setNotificationMessage({ type: 'success', message: 'All leaves cleared successfully' })
                                  setShowNotificationModal(true)
                                } else {
                                  setNotificationMessage({ type: 'error', message: result.error || 'Failed to clear leaves' })
                                  setShowNotificationModal(true)
                                }
                              } catch (error: any) {
                                setNotificationMessage({ type: 'error', message: error.message || 'Failed to clear leaves' })
                                setShowNotificationModal(true)
                              }
                              setShowConfirmModal(false)
                            }
                          })
                          setShowConfirmModal(true)
                        }}
                        className="w-full px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md text-sm font-medium"
                      >
                        Clear Leaves
                      </button>
                    </div>

                    {/* Clear Reservations */}
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-gray-900 dark:text-white">Reservations</h4>
                        <TrashIcon className="w-5 h-5 text-gray-400" />
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                        Clear all reservation records
                      </p>
                      <button
                        onClick={() => {
                          setConfirmModalConfig({
                            message: 'Are you sure you want to delete ALL reservations? This action cannot be undone.',
                            onConfirm: async () => {
                              try {
                                const result = await apiService.clearReservations()
                                if (result.success) {
                                  setNotificationMessage({ type: 'success', message: 'All reservations cleared successfully' })
                                  setShowNotificationModal(true)
                                } else {
                                  setNotificationMessage({ type: 'error', message: result.error || 'Failed to clear reservations' })
                                  setShowNotificationModal(true)
                                }
                              } catch (error: any) {
                                setNotificationMessage({ type: 'error', message: error.message || 'Failed to clear reservations' })
                                setShowNotificationModal(true)
                              }
                              setShowConfirmModal(false)
                            }
                          })
                          setShowConfirmModal(true)
                        }}
                        className="w-full px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md text-sm font-medium"
                      >
                        Clear Reservations
                      </button>
                    </div>

                    {/* Clear Customers */}
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-gray-900 dark:text-white">Customers</h4>
                        <TrashIcon className="w-5 h-5 text-gray-400" />
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                        Clear all customer records
                      </p>
                      <button
                        onClick={() => {
                          setConfirmModalConfig({
                            message: 'Are you sure you want to delete ALL customers? This action cannot be undone.',
                            onConfirm: async () => {
                              try {
                                const result = await apiService.clearCustomers()
                                if (result.success) {
                                  setNotificationMessage({ type: 'success', message: 'All customers cleared successfully' })
                                  setShowNotificationModal(true)
                                } else {
                                  setNotificationMessage({ type: 'error', message: result.error || 'Failed to clear customers' })
                                  setShowNotificationModal(true)
                                }
                              } catch (error: any) {
                                setNotificationMessage({ type: 'error', message: error.message || 'Failed to clear customers' })
                                setShowNotificationModal(true)
                              }
                              setShowConfirmModal(false)
                            }
                          })
                          setShowConfirmModal(true)
                        }}
                        className="w-full px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md text-sm font-medium"
                      >
                        Clear Customers
                      </button>
                    </div>

                    {/* Clear AI Data */}
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-gray-900 dark:text-white">AI Data</h4>
                        <TrashIcon className="w-5 h-5 text-gray-400" />
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                        Clear all AI recommendations, events, and conversations
                      </p>
                      <button
                        onClick={() => {
                          setConfirmModalConfig({
                            message: 'Are you sure you want to delete ALL AI data? This action cannot be undone.',
                            onConfirm: async () => {
                              try {
                                const result = await apiService.clearAiData()
                                if (result.success) {
                                  setNotificationMessage({ type: 'success', message: 'All AI data cleared successfully' })
                                  setShowNotificationModal(true)
                                } else {
                                  setNotificationMessage({ type: 'error', message: result.error || 'Failed to clear AI data' })
                                  setShowNotificationModal(true)
                                }
                              } catch (error: any) {
                                setNotificationMessage({ type: 'error', message: error.message || 'Failed to clear AI data' })
                                setShowNotificationModal(true)
                              }
                              setShowConfirmModal(false)
                            }
                          })
                          setShowConfirmModal(true)
                        }}
                        className="w-full px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md text-sm font-medium"
                      >
                        Clear AI Data
                      </button>
                    </div>
                  </div>

                  {/* Clear All Data - Nuclear Option */}
                  <div className="mt-8 border-2 border-red-300 dark:border-red-700 rounded-lg p-6 bg-red-50 dark:bg-red-900/20">
                    <div className="flex items-start mb-4">
                      <ExclamationTriangleIcon className="w-6 h-6 text-red-600 dark:text-red-400 mt-0.5 mr-3 flex-shrink-0" />
                      <div>
                        <h4 className="text-lg font-semibold text-red-900 dark:text-red-200 mb-1">
                          Clear All Data
                        </h4>
                        <p className="text-sm text-red-800 dark:text-red-300">
                          This will delete ALL data from the system including orders, ledgers, expenses, inventory, attendances, payrolls, leaves, reservations, customers, and AI data. This is a nuclear option and cannot be undone.
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setConfirmModalConfig({
                          message: '⚠️ WARNING: This will delete ALL data from the entire system. This includes orders, ledgers, expenses, inventory, attendances, payrolls, leaves, reservations, customers, and AI data. This action is PERMANENT and CANNOT be undone. Are you absolutely sure?',
                          onConfirm: async () => {
                            try {
                              const result = await apiService.clearAllData()
                              if (result.success) {
                                setNotificationMessage({ type: 'success', message: 'All data cleared successfully' })
                                setShowNotificationModal(true)
                              } else {
                                setNotificationMessage({ type: 'error', message: result.error || 'Failed to clear all data' })
                                setShowNotificationModal(true)
                              }
                            } catch (error: any) {
                              setNotificationMessage({ type: 'error', message: error.message || 'Failed to clear all data' })
                              setShowNotificationModal(true)
                            }
                            setShowConfirmModal(false)
                          }
                        })
                        setShowConfirmModal(true)
                      }}
                      className="w-full px-6 py-3 bg-red-700 hover:bg-red-800 text-white rounded-md text-base font-semibold flex items-center justify-center"
                    >
                      <ExclamationTriangleIcon className="w-5 h-5 mr-2" />
                      Clear All Data
                    </button>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'security' && (
              <div className="p-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-6">
                  Security Settings
                </h3>
                
                <div className="space-y-6">
                  <div>
                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">
                      Password Policy
                    </h4>
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                      <div className="space-y-3">
                        <label className="flex items-center">
                          <input type="checkbox" defaultChecked className="mr-3" />
                          <span className="text-sm text-gray-700 dark:text-gray-300">Require strong passwords</span>
                        </label>
                        <label className="flex items-center">
                          <input type="checkbox" defaultChecked className="mr-3" />
                          <span className="text-sm text-gray-700 dark:text-gray-300">Password expiration (90 days)</span>
                        </label>
                        <label className="flex items-center">
                          <input type="checkbox" defaultChecked className="mr-3" />
                          <span className="text-sm text-gray-700 dark:text-gray-300">Two-factor authentication</span>
                        </label>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">
                      Session Management
                    </h4>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Session Timeout (minutes)
                        </label>
                        <input
                          type="number"
                          defaultValue={30}
                          className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>
                      <label className="flex items-center">
                        <input type="checkbox" defaultChecked className="mr-3" />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Auto-logout on inactivity</span>
                      </label>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">
                      Data Protection
                    </h4>
                    <div className="space-y-3">
                      <label className="flex items-center">
                        <input type="checkbox" defaultChecked className="mr-3" />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Encrypt sensitive data</span>
                      </label>
                      <label className="flex items-center">
                        <input type="checkbox" defaultChecked className="mr-3" />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Regular data backups</span>
                      </label>
                      <label className="flex items-center">
                        <input type="checkbox" defaultChecked className="mr-3" />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Audit log tracking</span>
                      </label>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">
                      Google Authenticator (2FA for Discount Authorization)
                    </h4>
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-4">
                      {totpStatus?.isConfigured && totpStatus.authenticators && totpStatus.authenticators.length > 0 ? (
                        <div className="space-y-3">
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <p className="text-sm font-medium text-green-600 dark:text-green-400">
                                ✓ {totpStatus.count} Authenticator{totpStatus.count > 1 ? 's' : ''} Configured
                              </p>
                            </div>
                          </div>
                          
                          {/* List of Authenticators */}
                          <div className="space-y-2">
                            {totpStatus.authenticators.map((auth) => (
                              <div key={auth.id} className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600">
                                <div>
                                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                                    {auth.name || 'Google Authenticator'}
                                  </p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    {auth.backup_codes?.length || 0} backup codes remaining
                                  </p>
                                  {auth.last_used_at && (
                                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                                      Last used: {new Date(auth.last_used_at).toLocaleString()}
                                    </p>
                                  )}
                                </div>
                                <button
                                  onClick={() => handleDeleteAuthenticator(auth.id)}
                                  className="px-3 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-xs"
                                >
                                  Delete
                                </button>
                              </div>
                            ))}
                          </div>
                          
                          {/* Add New Authenticator */}
                          <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              Add New Authenticator
                            </label>
                            <div className="flex gap-2">
                              <input
                                type="text"
                                value={newAuthenticatorName}
                                onChange={(e) => setNewAuthenticatorName(e.target.value)}
                                placeholder="e.g., Manager, Supervisor, Admin"
                                className="flex-1 border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                              />
                              <button
                                onClick={handleGenerateTOTP}
                                disabled={isGeneratingTOTP || !newAuthenticatorName.trim()}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
                              >
                                {isGeneratingTOTP ? 'Generating...' : 'Add'}
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <p className="text-sm text-gray-700 dark:text-gray-300">
                            No Google Authenticator configured. Add one to enable supervisor authorization for discounts.
                          </p>
                          <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                              Authenticator Name
                            </label>
                            <input
                              type="text"
                              value={newAuthenticatorName}
                              onChange={(e) => setNewAuthenticatorName(e.target.value)}
                              placeholder="e.g., Manager, Supervisor, Admin"
                              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                            />
                            <button
                              onClick={handleGenerateTOTP}
                              disabled={isGeneratingTOTP || !newAuthenticatorName.trim()}
                              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
                            >
                              {isGeneratingTOTP ? 'Generating...' : 'Setup Google Authenticator'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mt-6">
                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">
                      Google Authenticator (2FA for Refund Authorization)
                    </h4>
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-4">
                      {refundTotpStatus?.isConfigured && refundTotpStatus.authenticators && refundTotpStatus.authenticators.length > 0 ? (
                        <div className="space-y-3">
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <p className="text-sm font-medium text-green-600 dark:text-green-400">
                                ✓ {refundTotpStatus.count} Authenticator{refundTotpStatus.count > 1 ? 's' : ''} Configured
                              </p>
                            </div>
                          </div>
                          
                          {/* List of Authenticators */}
                          <div className="space-y-2">
                            {refundTotpStatus.authenticators.map((auth) => (
                              <div key={auth.id} className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600">
                                <div>
                                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                                    {auth.name || 'Google Authenticator'}
                                  </p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    {auth.backup_codes?.length || 0} backup codes remaining
                                  </p>
                                  {auth.last_used_at && (
                                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                                      Last used: {new Date(auth.last_used_at).toLocaleString()}
                                    </p>
                                  )}
                                </div>
                                <button
                                  onClick={() => handleDeleteRefundAuthenticator(auth.id)}
                                  className="px-3 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-xs"
                                >
                                  Delete
                                </button>
                              </div>
                            ))}
                          </div>
                          
                          {/* Add New Authenticator */}
                          <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              Add New Authenticator
                            </label>
                            <div className="flex gap-2">
                              <input
                                type="text"
                                value={newRefundAuthenticatorName}
                                onChange={(e) => setNewRefundAuthenticatorName(e.target.value)}
                                placeholder="e.g., Manager, Supervisor, Admin"
                                className="flex-1 border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                              />
                              <button
                                onClick={handleGenerateRefundTOTP}
                                disabled={isGeneratingRefundTOTP || !newRefundAuthenticatorName.trim()}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
                              >
                                {isGeneratingRefundTOTP ? 'Generating...' : 'Add'}
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <p className="text-sm text-gray-700 dark:text-gray-300">
                            No Google Authenticator configured. Add one to enable supervisor authorization for refunds.
                          </p>
                          <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                              Authenticator Name
                            </label>
                            <input
                              type="text"
                              value={newRefundAuthenticatorName}
                              onChange={(e) => setNewRefundAuthenticatorName(e.target.value)}
                              placeholder="e.g., Manager, Supervisor, Admin"
                              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                            />
                            <button
                              onClick={handleGenerateRefundTOTP}
                              disabled={isGeneratingRefundTOTP || !newRefundAuthenticatorName.trim()}
                              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
                            >
                              {isGeneratingRefundTOTP ? 'Generating...' : 'Setup Google Authenticator'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* TOTP Setup Modal */}
            {showTOTPSetup && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">Setup Google Authenticator</h2>
                    <button
                      onClick={() => {
                        setShowTOTPSetup(false)
                        setTotpQRCode('')
                        setTotpSecretId('')
                        setTotpBackupCodes([])
                        setTotpVerificationCode('')
                        setShowBackupCodes(false)
                        setNewAuthenticatorName('')
                        loadTOTPStatus()
                      }}
                      className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
                        1. Scan this QR code with your Google Authenticator app:
                      </p>
                      {totpQRCode && (
                        <div className="flex justify-center p-4 bg-white rounded-lg">
                          <img src={totpQRCode} alt="TOTP QR Code" className="w-48 h-48" />
                        </div>
                      )}
                    </div>

                    {showBackupCodes && totpBackupCodes.length > 0 && (
                      <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg">
                        <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-2">
                          ⚠️ Save these backup codes in a safe place:
                        </p>
                        <div className="grid grid-cols-2 gap-2">
                          {totpBackupCodes.map((code, index) => (
                            <div key={index} className="text-xs font-mono text-yellow-900 dark:text-yellow-100 p-2 bg-white dark:bg-gray-800 rounded">
                              {code}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div>
                      <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                        2. Enter the 6-digit code from Google Authenticator:
                      </p>
                      <input
                        type="text"
                        value={totpVerificationCode}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 6)
                          setTotpVerificationCode(value)
                        }}
                        placeholder="000000"
                        maxLength={6}
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-center text-2xl font-mono tracking-widest"
                      />
                    </div>

                    <div className="flex gap-3">
                      <button
                      onClick={() => {
                        setShowTOTPSetup(false)
                        setTotpQRCode('')
                        setTotpSecretId('')
                        setTotpBackupCodes([])
                        setTotpVerificationCode('')
                        setShowBackupCodes(false)
                        setNewAuthenticatorName('')
                        loadTOTPStatus()
                      }}
                        className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleVerifyTOTP}
                        disabled={isVerifyingTOTP || totpVerificationCode.length !== 6}
                        className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {isVerifyingTOTP ? 'Verifying...' : 'Verify & Activate'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Success Modal */}
            {showSuccessModal && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md">
                  <div className="flex items-center justify-center mb-4">
                    <div className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center">
                      <CheckIcon className="h-8 w-8 text-green-600 dark:text-green-400" />
                    </div>
                  </div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white text-center mb-2">
                    Google Authenticator Configured Successfully!
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400 text-center mb-6">
                    Your authenticator has been activated and is ready to use for discount authorization.
                  </p>
                  <button
                    onClick={() => setShowSuccessModal(false)}
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    OK
                  </button>
                </div>
              </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteConfirmModal && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md">
                  <div className="flex items-center justify-center mb-4">
                    <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
                      <XMarkIcon className="h-8 w-8 text-red-600 dark:text-red-400" />
                    </div>
                  </div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white text-center mb-2">
                    Delete Authenticator?
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400 text-center mb-6">
                    Are you sure you want to delete this authenticator? This action cannot be undone.
                  </p>
                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        setShowDeleteConfirmModal(false)
                        setDeletingSecretId(null)
                      }}
                      className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={confirmDeleteAuthenticator}
                      className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Delete Success Modal */}
            {showDeleteSuccessModal && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md">
                  <div className="flex items-center justify-center mb-4">
                    <div className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center">
                      <CheckIcon className="h-8 w-8 text-green-600 dark:text-green-400" />
                    </div>
                  </div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white text-center mb-2">
                    Authenticator Deleted Successfully
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400 text-center mb-6">
                    The authenticator has been removed and can no longer be used for discount authorization.
                  </p>
                  <button
                    onClick={() => setShowDeleteSuccessModal(false)}
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    OK
                  </button>
                </div>
              </div>
            )}

            {/* Refund TOTP Setup Modal */}
            {showRefundTOTPSetup && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">Setup Google Authenticator (Refund)</h2>
                    <button
                      onClick={() => {
                        setShowRefundTOTPSetup(false)
                        setRefundTotpQRCode('')
                        setRefundTotpSecretId('')
                        setRefundTotpBackupCodes([])
                        setRefundTotpVerificationCode('')
                        setShowRefundBackupCodes(false)
                        setNewRefundAuthenticatorName('')
                        loadRefundTOTPStatus()
                      }}
                      className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
                        1. Scan this QR code with your Google Authenticator app:
                      </p>
                      {refundTotpQRCode && (
                        <div className="flex justify-center p-4 bg-white rounded-lg">
                          <img src={refundTotpQRCode} alt="TOTP QR Code" className="w-48 h-48" />
                        </div>
                      )}
                    </div>

                    {showRefundBackupCodes && refundTotpBackupCodes.length > 0 && (
                      <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg">
                        <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-2">
                          ⚠️ Save these backup codes in a safe place:
                        </p>
                        <div className="grid grid-cols-2 gap-2">
                          {refundTotpBackupCodes.map((code, index) => (
                            <div key={index} className="text-xs font-mono text-yellow-900 dark:text-yellow-100 p-2 bg-white dark:bg-gray-800 rounded">
                              {code}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div>
                      <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                        2. Enter the 6-digit code from Google Authenticator:
                      </p>
                      <input
                        type="text"
                        value={refundTotpVerificationCode}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 6)
                          setRefundTotpVerificationCode(value)
                        }}
                        placeholder="000000"
                        maxLength={6}
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-center text-2xl font-mono tracking-widest"
                      />
                    </div>

                    <div className="flex gap-3">
                      <button
                        onClick={() => {
                          setShowRefundTOTPSetup(false)
                          setRefundTotpQRCode('')
                          setRefundTotpSecretId('')
                          setRefundTotpBackupCodes([])
                          setRefundTotpVerificationCode('')
                          setShowRefundBackupCodes(false)
                          setNewRefundAuthenticatorName('')
                          loadRefundTOTPStatus()
                        }}
                        className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleVerifyRefundTOTP}
                        disabled={isVerifyingRefundTOTP || refundTotpVerificationCode.length !== 6}
                        className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {isVerifyingRefundTOTP ? 'Verifying...' : 'Verify & Activate'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Refund Success Modal */}
            {showRefundSuccessModal && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md">
                  <div className="flex items-center justify-center mb-4">
                    <div className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center">
                      <CheckIcon className="h-8 w-8 text-green-600 dark:text-green-400" />
                    </div>
                  </div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white text-center mb-2">
                    Google Authenticator Configured Successfully!
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400 text-center mb-6">
                    Your authenticator has been activated and is ready to use for refund authorization.
                  </p>
                  <button
                    onClick={() => setShowRefundSuccessModal(false)}
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    OK
                  </button>
                </div>
              </div>
            )}

            {/* Refund Delete Confirmation Modal */}
            {showRefundDeleteConfirmModal && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md">
                  <div className="flex items-center justify-center mb-4">
                    <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
                      <XMarkIcon className="h-8 w-8 text-red-600 dark:text-red-400" />
                    </div>
                  </div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white text-center mb-2">
                    Delete Authenticator?
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400 text-center mb-6">
                    Are you sure you want to delete this authenticator? This action cannot be undone.
                  </p>
                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        setShowRefundDeleteConfirmModal(false)
                        setDeletingRefundSecretId(null)
                      }}
                      className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={confirmDeleteRefundAuthenticator}
                      className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Refund Delete Success Modal */}
            {showRefundDeleteSuccessModal && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 w-full max-w-md">
                  <div className="flex items-center justify-center mb-4">
                    <div className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center">
                      <CheckIcon className="h-8 w-8 text-green-600 dark:text-green-400" />
                    </div>
                  </div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white text-center mb-2">
                    Authenticator Deleted Successfully
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400 text-center mb-6">
                    The authenticator has been removed and can no longer be used for refund authorization.
                  </p>
                  <button
                    onClick={() => setShowRefundDeleteSuccessModal(false)}
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    OK
                  </button>
                </div>
              </div>
            )}

            {/* POS Settings */}
            {activeTab === 'pos' && (
              <div className="p-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-6">
                  POS Settings
                </h3>
                
                <div className="space-y-6">
                  <div>
                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">
                      Guest Customer Settings
                    </h4>
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Guest Customer Name
                          </label>
                          <input
                            type="text"
                            value={posSettings.guestCustomerName}
                            onChange={(e) => setPOSSettings({ ...posSettings, guestCustomerName: e.target.value })}
                            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Guest Starting Number
                          </label>
                          <input
                            type="text"
                            value={posSettings.guestCustomerStartingNumber}
                            onChange={(e) => setPOSSettings({ ...posSettings, guestCustomerStartingNumber: e.target.value })}
                            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />
                        </div>
                      </div>
                      
                      <div className="space-y-3">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={posSettings.enableGuestOrdering}
                            onChange={(e) => setPOSSettings({ ...posSettings, enableGuestOrdering: e.target.checked })}
                            className="mr-3"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">Enable guest ordering</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={posSettings.autoIncrementGuestNumber}
                            onChange={(e) => setPOSSettings({ ...posSettings, autoIncrementGuestNumber: e.target.checked })}
                            className="mr-3"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">Auto-increment guest numbers</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={posSettings.requireMobileNumber}
                            onChange={(e) => setPOSSettings({ ...posSettings, requireMobileNumber: e.target.checked })}
                            className="mr-3"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">Require mobile number for all customers</span>
                        </label>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">
                      Invoice Settings
                    </h4>
                    <div className="space-y-4">
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">
                              Dual Invoicing
                            </label>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              If enabled, generates a duplicate invoice first for customer payment, then a final invoice. If disabled, generates a single final invoice directly.
                            </p>
                          </div>
                          <div className="ml-4">
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={restaurantSettings.dual_invoicing_enabled ?? false}
                                onChange={(e) => setRestaurantSettings({ ...restaurantSettings, dual_invoicing_enabled: e.target.checked })}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                            </label>
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">
                              Enable ZATCA Phase 1
                            </label>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              Enable ZATCA Phase 1 compliance features including QR code generation, sequential invoice numbering, and tax invoice format for receipts and final invoices.
                            </p>
                          </div>
                          <div className="ml-4">
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                              checked={zatcaToggleValue}
                              onChange={(e) => {
                                const newValue = e.target.checked

                                // Update the tracked value immediately
                                setZatcaToggleValue(newValue)
                                // Update both POS settings (for display) and restaurant settings (for storage)
                                setPOSSettings(prev => ({ ...prev, zatcaPhase1Enabled: newValue }))
                                setRestaurantSettings(prev => ({ ...prev, zatca_phase1_enabled: newValue }))

                              }}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">
                      Waiter Module
                    </h4>
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">
                            Enable Waiter Module
                          </label>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            Turn the waiter module on or off for your staff application. When disabled, the waiter interface cannot be accessed.
                          </p>
                        </div>
                        <div className="ml-4">
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={posSettings.enableWaiterModule ?? true}
                              onChange={(e) => setPOSSettings({ ...posSettings, enableWaiterModule: e.target.checked })}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">
                      Discount Settings
                    </h4>
                    <div className="space-y-4">
                      <div className="space-y-3">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={posSettings.enableDiscountSystem}
                            onChange={(e) => setPOSSettings({ ...posSettings, enableDiscountSystem: e.target.checked })}
                            className="mr-3"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">Enable discount system</span>
                        </label>
                        <div className="flex items-center justify-between rounded-lg border border-gray-200 dark:border-gray-600 p-3">
                          <div>
                            <span className="text-sm font-medium text-gray-900 dark:text-white">Credit Dinner</span>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                              When enabled, a full (100%) discount requires C/o name and customer name; adds Credit Dinner totals to closing reports and a dedicated report tab.
                            </p>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={posSettings.creditDinnerEnabled ?? false}
                              onChange={(e) => setPOSSettings({ ...posSettings, creditDinnerEnabled: e.target.checked })}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600" />
                          </label>
                        </div>
                      </div>
                      
                      {posSettings.enableDiscountSystem && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              Maximum Discount Percentage
                            </label>
                            <input
                              type="number"
                              min="0"
                              max="100"
                              value={posSettings.maxDiscountPercentage}
                              onChange={(e) => {
                                const inputValue = e.target.value
                                const value = inputValue === '' ? 0 : parseInt(inputValue, 10)
                                const numValue = isNaN(value) ? 0 : value

                                setPOSSettings(prev => {
                                  const updated = { ...prev, maxDiscountPercentage: numValue }

                                  return updated
                                })
                              }}
                              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                              placeholder="50"
                            />
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Maximum percentage discount allowed (0-100%)</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              Maximum Discount Amount
                            </label>
                            <input
                              type="number"
                              min="0"
                              value={posSettings.maxDiscountAmount}
                              onChange={(e) => {
                                const inputValue = e.target.value
                                const value = inputValue === '' ? 0 : parseFloat(inputValue)
                                const numValue = isNaN(value) ? 0 : value

                                setPOSSettings(prev => {
                                  const updated = { ...prev, maxDiscountAmount: numValue }

                                  return updated
                                })
                              }}
                              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                              placeholder="100"
                            />
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Maximum discount amount in currency</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">
                      Return Settings
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Return Duration (Hours)
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={posSettings.returnDurationHours}
                          onChange={(e) => {
                            const inputValue = e.target.value
                            const value = inputValue === '' ? 0 : parseInt(inputValue, 10)
                            const numValue = isNaN(value) ? 0 : value

                            setPOSSettings(prev => {
                              const updated = { ...prev, returnDurationHours: numValue }

                              return updated
                            })
                          }}
                          className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          placeholder="24"
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Maximum number of hours after order placement within which returns are allowed. Set to 0 to disable time limit.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Inventory Integration Settings */}
                  <div>
                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">
                      Inventory Integration
                    </h4>
                    <div className="flex items-center mb-2">
                      <input
                        type="checkbox"
                        id="linkInventoryWithProducts"
                        checked={posSettings.linkInventoryWithProducts}
                        onChange={(e) => setPOSSettings({ ...posSettings, linkInventoryWithProducts: e.target.checked })}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="linkInventoryWithProducts" className="ml-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Link Inventory with Products
                      </label>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      When enabled, inventory will be automatically reduced when items are sold and can be increased when returns are processed. Recipe management will also be available in Menu Management.
                    </p>
                    <div className="flex items-center mt-4">
                      <input
                        type="checkbox"
                        id="enableQrMenu"
                        checked={posSettings.enableQrMenu}
                        onChange={(e) => setPOSSettings({ ...posSettings, enableQrMenu: e.target.checked })}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="enableQrMenu" className="ml-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Enable QR Menu
                      </label>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      When enabled, customers can access the menu via QR code, browse items, add to cart, and place orders using their mobile number. The customer-facing menu will be available at a separate URL.
                    </p>
                    <div className="flex items-center mt-4">
                      <input
                        type="checkbox"
                        id="qrMenuOrderPlacingEnabled"
                        checked={posSettings.qrMenuOrderPlacingEnabled ?? true}
                        onChange={(e) => setPOSSettings({ ...posSettings, qrMenuOrderPlacingEnabled: e.target.checked })}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="qrMenuOrderPlacingEnabled" className="ml-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Enable Order Placing in QR Menu
                      </label>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      When enabled, customers can add items to cart and place orders. When disabled, customers can only browse the menu. The cart icon and "Add to Cart" buttons will be hidden.
                    </p>
                  </div>
                  
                  {/* Cash Register Settings */}
                  <div>
                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">
                      Cash Register Settings
                    </h4>
                    <div className="flex items-center mb-2">
                      <input
                        type="checkbox"
                        id="allowZeroCashRegisterOpening"
                        checked={posSettings.allowZeroCashRegisterOpening ?? false}
                        onChange={(e) => setPOSSettings({ ...posSettings, allowZeroCashRegisterOpening: e.target.checked })}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="allowZeroCashRegisterOpening" className="ml-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Allow 0 Cash Register Opening
                      </label>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      When enabled, the cash register can be opened with 0 amount. By default, a positive amount is required.
                    </p>
                  </div>
                  
                  <div className="mt-6">
                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">
                      Order Type Settings
                    </h4>
                    <div className="flex items-center mb-2">
                      <input
                        type="checkbox"
                        id="allowDineInFromPOS"
                        checked={posSettings.allowDineInFromPOS ?? false}
                        onChange={(e) => setPOSSettings({ ...posSettings, allowDineInFromPOS: e.target.checked })}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="allowDineInFromPOS" className="ml-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Allow Dine-In Orders from POS
                      </label>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      When enabled, POS users can choose between dine-in and takeaway orders. When disabled, all POS orders will be takeaway only.
                    </p>
                    <div className="flex items-center mb-2 mt-4">
                      <input
                        type="checkbox"
                        id="allowCateringOrders"
                        checked={posSettings.allowCateringOrders ?? false}
                        onChange={(e) => setPOSSettings({ ...posSettings, allowCateringOrders: e.target.checked })}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="allowCateringOrders" className="ml-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Allow Catering Orders
                      </label>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      When enabled, POS users can select Catering as an order type alongside Dine In and Takeaway.
                    </p>
                  </div>

                  <div className="mt-6">
                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">
                      Order Cancellation Settings
                    </h4>
                    <div className="flex items-center mb-2">
                      <input
                        type="checkbox"
                        id="preventCancelWhenPreparing"
                        checked={posSettings.preventCancelWhenPreparing ?? true}
                        onChange={(e) => setPOSSettings({ ...posSettings, preventCancelWhenPreparing: e.target.checked })}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="preventCancelWhenPreparing" className="ml-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Prevent Cancellation When Items Are Being Prepared
                      </label>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      When enabled, orders cannot be cancelled once KDS starts preparing items (status: preparing or ready). This prevents food wastage and ensures kitchen efficiency. When disabled, orders can be cancelled at any time.
                    </p>
                  </div>

                  
                  <div className="mt-6">
                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">
                      Token Settings
                    </h4>
                    <div className="flex items-center mb-3">
                      <input
                        type="checkbox"
                        id="enableTokenSystem"
                        checked={posSettings.enableTokenSystem ?? false}
                        onChange={(e) => setPOSSettings({ ...posSettings, enableTokenSystem: e.target.checked })}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="enableTokenSystem" className="ml-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Enable Token System
                      </label>
                    </div>
                    {posSettings.enableTokenSystem && (
                      <div className="space-y-3">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Token Starting Number
                            </label>
                            <input
                              type="number"
                              min={1}
                              value={posSettings.tokenStartNumber ?? 1}
                              onChange={(e) => setPOSSettings({ ...posSettings, tokenStartNumber: parseInt(e.target.value, 10) || 1 })}
                              className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 shadow-sm py-2 px-3 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Reset Frequency
                            </label>
                            <select
                              value={posSettings.tokenResetFrequency || 'never'}
                              onChange={(e) => setPOSSettings({ ...posSettings, tokenResetFrequency: e.target.value as 'never' | 'daily' })}
                              className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-gray-900 dark:text-gray-100"
                            >
                              <option value="never">Never reset</option>
                              <option value="daily">Reset daily</option>
                            </select>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Token Mode
                          </label>
                          <select
                            value={posSettings.tokenMode || 'shared'}
                            onChange={(e) => setPOSSettings({ ...posSettings, tokenMode: e.target.value as POSSettings['tokenMode'] })}
                            className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-gray-900 dark:text-gray-100"
                          >
                            <option value="shared">Single sequence for POS and Waiter</option>
                            <option value="separate">Separate sequences for POS and Waiter</option>
                            <option value="pos_only">Tokens only for POS orders</option>
                            <option value="waiter_only">Tokens only for Waiter orders</option>
                          </select>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Tokens are sequential numbers printed on orders. You can choose whether POS and Waiter share the same sequence,
                          use separate sequences, or restrict tokens to POS or Waiter only.
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="mt-6">
                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">
                      Printer Settings
                    </h4>
                    <div className="flex items-center mb-3">
                      <input
                        type="checkbox"
                        id="enableAutoPrint"
                        checked={posSettings.enableAutoPrint ?? false}
                        onChange={(e) => setPOSSettings({ ...posSettings, enableAutoPrint: e.target.checked })}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="enableAutoPrint" className="ml-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Enable Auto Print
                      </label>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                      When enabled, receipts will automatically print after order creation without showing the print dialog.
                    </p>
                    
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Printer Type
                        </label>
                        <select
                          value={posSettings.printerType || 'tcp_ip'}
                          onChange={(e) => setPOSSettings({ ...posSettings, printerType: e.target.value as 'tcp_ip' | 'usb' | 'network' | 'system' })}
                          className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 py-2 px-3 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-gray-900 dark:text-gray-100"
                        >
                          <option value="system">System Printer (Silent Mode)</option>
                          <option value="tcp_ip">TCP/IP (Network Thermal Printer)</option>
                          <option value="usb">USB Printer</option>
                          <option value="network">Network Printer</option>
                        </select>
                      </div>

                      {posSettings.printerType === 'system' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            System Printer Name
                          </label>
                          <input
                            type="text"
                            value={posSettings.printerName || ''}
                            onChange={(e) => setPOSSettings({ ...posSettings, printerName: e.target.value })}
                            placeholder="e.g., HP LaserJet, Epson TM-T20"
                            className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 shadow-sm py-2 px-3 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          />
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            Enter the exact name of your system printer. The printer must be installed on your system. Silent mode will attempt to print without showing the print dialog (browser may still show dialog for security).
                          </p>
                        </div>
                      )}

                      {posSettings.printerType === 'tcp_ip' && (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Printer Name
                            </label>
                            <input
                              type="text"
                              value={posSettings.printerName || ''}
                              onChange={(e) => setPOSSettings({ ...posSettings, printerName: e.target.value })}
                              placeholder="e.g., Kitchen Printer, POS Printer"
                              className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 shadow-sm py-2 px-3 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            />
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Printer IP Address
                              </label>
                              <input
                                type="text"
                                value={posSettings.printerIp || ''}
                                onChange={(e) => setPOSSettings({ ...posSettings, printerIp: e.target.value })}
                                placeholder="192.168.1.100"
                                className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 shadow-sm py-2 px-3 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Printer Port
                              </label>
                              <input
                                type="number"
                                value={posSettings.printerPort || 9100}
                                onChange={(e) => setPOSSettings({ ...posSettings, printerPort: parseInt(e.target.value, 10) || 9100 })}
                                placeholder="9100"
                                className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 shadow-sm py-2 px-3 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                              />
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                Default port for TCP/IP thermal printers is 9100
                              </p>
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Printer Model (Optional)
                            </label>
                            <input
                              type="text"
                              value={posSettings.printerModel || ''}
                              onChange={(e) => setPOSSettings({ ...posSettings, printerModel: e.target.value })}
                              placeholder="e.g., Epson TM-T20, Star TSP100"
                              className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 shadow-sm py-2 px-3 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            />
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              Specify printer model for better compatibility
                            </p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Printer Width (mm)
                            </label>
                            <input
                              type="number"
                              min="50"
                              max="120"
                              value={posSettings.printerWidth || 80}
                              onChange={(e) => setPOSSettings({ ...posSettings, printerWidth: parseInt(e.target.value, 10) || 80 })}
                              placeholder="80"
                              className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 shadow-sm py-2 px-3 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            />
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              Thermal printer paper width in millimeters (default: 80mm)
                            </p>
                          </div>
                        </>
                      )}

                      {posSettings.printerType === 'usb' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Printer Name
                          </label>
                          <input
                            type="text"
                            value={posSettings.printerName || ''}
                            onChange={(e) => setPOSSettings({ ...posSettings, printerName: e.target.value })}
                            placeholder="Select from system printers"
                            className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 shadow-sm py-2 px-3 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          />
                        </div>
                      )}

                      {posSettings.printerType === 'network' && (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Printer Name
                            </label>
                            <input
                              type="text"
                              value={posSettings.printerName || ''}
                              onChange={(e) => setPOSSettings({ ...posSettings, printerName: e.target.value })}
                              placeholder="Network printer name or path"
                              className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 shadow-sm py-2 px-3 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Printer IP Address
                            </label>
                            <input
                              type="text"
                              value={posSettings.printerIp || ''}
                              onChange={(e) => setPOSSettings({ ...posSettings, printerIp: e.target.value })}
                              placeholder="192.168.1.100"
                              className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 shadow-sm py-2 px-3 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Invoice Printer Settings */}
                  <div className="mt-6">
                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">
                      Invoice Printer (TCP/IP)
                    </h4>
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-4">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={posSettings.invoiceEnableAutoPrint ?? false}
                          onChange={(e) => setPOSSettings({ ...posSettings, invoiceEnableAutoPrint: e.target.checked })}
                          className="mr-2"
                        />
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Enable Auto-Print for Invoices
                        </label>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Printer IP Address
                          </label>
                          <input
                            type="text"
                            value={posSettings.invoicePrinterIp || ''}
                            onChange={(e) => setPOSSettings({ ...posSettings, invoicePrinterIp: e.target.value })}
                            placeholder="192.168.1.100"
                            className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 shadow-sm py-2 px-3 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Printer Port
                          </label>
                          <input
                            type="number"
                            value={posSettings.invoicePrinterPort || 9100}
                            onChange={(e) => setPOSSettings({ ...posSettings, invoicePrinterPort: parseInt(e.target.value, 10) || 9100 })}
                            placeholder="9100"
                            className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 shadow-sm py-2 px-3 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Printer Name
                          </label>
                          <input
                            type="text"
                            value={posSettings.invoicePrinterName || ''}
                            onChange={(e) => setPOSSettings({ ...posSettings, invoicePrinterName: e.target.value })}
                            placeholder="e.g., Invoice Printer"
                            className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 shadow-sm py-2 px-3 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Printer Width (mm)
                          </label>
                          <input
                            type="number"
                            min="50"
                            max="120"
                            value={posSettings.invoicePrinterWidth || 80}
                            onChange={(e) => setPOSSettings({ ...posSettings, invoicePrinterWidth: parseInt(e.target.value, 10) || 80 })}
                            placeholder="80"
                            className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 shadow-sm py-2 px-3 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Duplicate Invoice Printer Settings */}
                  <div className="mt-6">
                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">
                      Duplicate Invoice Printer (TCP/IP)
                    </h4>
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-4">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={posSettings.duplicateInvoiceEnableAutoPrint ?? false}
                          onChange={(e) => setPOSSettings({ ...posSettings, duplicateInvoiceEnableAutoPrint: e.target.checked })}
                          className="mr-2"
                        />
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Enable Auto-Print for Duplicate Invoices
                        </label>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Printer IP Address
                          </label>
                          <input
                            type="text"
                            value={posSettings.duplicateInvoicePrinterIp || ''}
                            onChange={(e) => setPOSSettings({ ...posSettings, duplicateInvoicePrinterIp: e.target.value })}
                            placeholder="192.168.1.100"
                            className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 shadow-sm py-2 px-3 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Printer Port
                          </label>
                          <input
                            type="number"
                            value={posSettings.duplicateInvoicePrinterPort || 9100}
                            onChange={(e) => setPOSSettings({ ...posSettings, duplicateInvoicePrinterPort: parseInt(e.target.value, 10) || 9100 })}
                            placeholder="9100"
                            className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 shadow-sm py-2 px-3 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Printer Name
                          </label>
                          <input
                            type="text"
                            value={posSettings.duplicateInvoicePrinterName || ''}
                            onChange={(e) => setPOSSettings({ ...posSettings, duplicateInvoicePrinterName: e.target.value })}
                            placeholder="e.g., Duplicate Invoice Printer"
                            className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 shadow-sm py-2 px-3 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Printer Width (mm)
                          </label>
                          <input
                            type="number"
                            min="50"
                            max="120"
                            value={posSettings.duplicateInvoicePrinterWidth || 80}
                            onChange={(e) => setPOSSettings({ ...posSettings, duplicateInvoicePrinterWidth: parseInt(e.target.value, 10) || 80 })}
                            placeholder="80"
                            className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 shadow-sm py-2 px-3 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Final Invoice Printer Settings */}
                  <div className="mt-6">
                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">
                      Final Invoice Printer (TCP/IP)
                    </h4>
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-4">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={posSettings.finalInvoiceEnableAutoPrint ?? false}
                          onChange={(e) => setPOSSettings({ ...posSettings, finalInvoiceEnableAutoPrint: e.target.checked })}
                          className="mr-2"
                        />
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Enable Auto-Print for Final Invoices
                        </label>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Printer IP Address
                          </label>
                          <input
                            type="text"
                            value={posSettings.finalInvoicePrinterIp || ''}
                            onChange={(e) => setPOSSettings({ ...posSettings, finalInvoicePrinterIp: e.target.value })}
                            placeholder="192.168.1.100"
                            className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 shadow-sm py-2 px-3 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Printer Port
                          </label>
                          <input
                            type="number"
                            value={posSettings.finalInvoicePrinterPort || 9100}
                            onChange={(e) => setPOSSettings({ ...posSettings, finalInvoicePrinterPort: parseInt(e.target.value, 10) || 9100 })}
                            placeholder="9100"
                            className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 shadow-sm py-2 px-3 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Printer Name
                          </label>
                          <input
                            type="text"
                            value={posSettings.finalInvoicePrinterName || ''}
                            onChange={(e) => setPOSSettings({ ...posSettings, finalInvoicePrinterName: e.target.value })}
                            placeholder="e.g., Final Invoice Printer"
                            className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 shadow-sm py-2 px-3 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Printer Width (mm)
                          </label>
                          <input
                            type="number"
                            min="50"
                            max="120"
                            value={posSettings.finalInvoicePrinterWidth || 80}
                            onChange={(e) => setPOSSettings({ ...posSettings, finalInvoicePrinterWidth: parseInt(e.target.value, 10) || 80 })}
                            placeholder="80"
                            className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 shadow-sm py-2 px-3 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Bill Request Printer Settings */}
                  <div className="mt-6">
                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">
                      Bill Request Printer (TCP/IP) - Waiter to POS
                    </h4>
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-4">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={posSettings.billRequestEnableAutoPrint ?? false}
                          onChange={(e) => setPOSSettings({ ...posSettings, billRequestEnableAutoPrint: e.target.checked })}
                          className="mr-2"
                        />
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Enable Auto-Print for Bill Requests
                        </label>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Printer IP Address
                          </label>
                          <input
                            type="text"
                            value={posSettings.billRequestPrinterIp || ''}
                            onChange={(e) => setPOSSettings({ ...posSettings, billRequestPrinterIp: e.target.value })}
                            placeholder="192.168.1.100"
                            className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 shadow-sm py-2 px-3 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Printer Port
                          </label>
                          <input
                            type="number"
                            value={posSettings.billRequestPrinterPort || 9100}
                            onChange={(e) => setPOSSettings({ ...posSettings, billRequestPrinterPort: parseInt(e.target.value, 10) || 9100 })}
                            placeholder="9100"
                            className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 shadow-sm py-2 px-3 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Printer Name
                          </label>
                          <input
                            type="text"
                            value={posSettings.billRequestPrinterName || ''}
                            onChange={(e) => setPOSSettings({ ...posSettings, billRequestPrinterName: e.target.value })}
                            placeholder="e.g., Bill Request Printer"
                            className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 shadow-sm py-2 px-3 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Printer Width (mm)
                          </label>
                          <input
                            type="number"
                            min="50"
                            max="120"
                            value={posSettings.billRequestPrinterWidth || 80}
                            onChange={(e) => setPOSSettings({ ...posSettings, billRequestPrinterWidth: parseInt(e.target.value, 10) || 80 })}
                            placeholder="80"
                            className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 shadow-sm py-2 px-3 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6">
                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">
                      Android Print Service
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                      Configure the Android print service for POS devices running Android. The service runs on the Android device and forwards print requests to local printers.
                    </p>
                    <div className="flex items-center mb-4">
                      <input
                        type="checkbox"
                        id="androidPrintServiceEnabled"
                        checked={posSettings.androidPrintServiceEnabled ?? false}
                        onChange={(e) => setPOSSettings({ ...posSettings, androidPrintServiceEnabled: e.target.checked })}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="androidPrintServiceEnabled" className="ml-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Enable Android Print Service
                      </label>
                    </div>
                    {posSettings.androidPrintServiceEnabled && (
                      <div className="space-y-4 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Android Device IP Address
                            </label>
                            <input
                              type="text"
                              value={posSettings.androidPrintServiceIp || ''}
                              onChange={(e) => setPOSSettings({ ...posSettings, androidPrintServiceIp: e.target.value })}
                              placeholder="192.168.1.50"
                              className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 shadow-sm py-2 px-3 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            />
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              IP address of your Android POS device running the print service
                            </p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Service Port
                            </label>
                            <input
                              type="number"
                              value={posSettings.androidPrintServicePort || 3001}
                              onChange={(e) => setPOSSettings({ ...posSettings, androidPrintServicePort: parseInt(e.target.value, 10) || 3001 })}
                              placeholder="3001"
                              className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 shadow-sm py-2 px-3 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            />
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              Port number for the Android print service (default: 3001)
                            </p>
                          </div>
                        </div>
                        <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Download Print Service Files
                          </label>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                            Download the Android print service files to install on your Android POS device. No USB transfer needed!
                          </p>
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
                                
                                // First, test if the endpoint exists
                                try {
                                  const testResponse = await fetch(`${apiUrl}/android-print-service/test`, {
                                    method: 'GET',
                                  });
                                  if (!testResponse.ok) {
                                    throw new Error(`Endpoint test failed: ${testResponse.status}`);
                                  }
                                } catch (testError) {
                                  console.warn('Test endpoint check failed:', testError);
                                  // Continue anyway, might be a CORS issue
                                }
                                
                                const response = await fetch(`${apiUrl}/android-print-service/download`, {
                                  method: 'GET',
                                });
                                
                                if (!response.ok) {
                                  // Try to get error message from response
                                  let errorMessage = 'Download failed';
                                  try {
                                    const errorData = await response.json();
                                    errorMessage = errorData.error || errorMessage;
                                  } catch (e) {
                                    errorMessage = `HTTP ${response.status}: ${response.statusText}`;
                                  }
                                  
                                  if (response.status === 404) {
                                    errorMessage += '\n\n⚠️ The endpoint was not found. Please ensure:\n1. The backend server has been restarted\n2. The latest code has been deployed\n3. Check server logs for details';
                                  }
                                  
                                  throw new Error(errorMessage);
                                }
                                
                                const blob = await response.blob();
                                
                                // Check if blob is actually a zip file (not an error JSON)
                                if (blob.type === 'application/json' || blob.size < 100) {
                                  const text = await blob.text();
                                  try {
                                    const errorData = JSON.parse(text);
                                    throw new Error(errorData.error || 'Download failed');
                                  } catch (e) {
                                    throw new Error('Received invalid response from server');
                                  }
                                }
                                
                                const url = window.URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = 'android-print-service.zip';
                                document.body.appendChild(a);
                                a.click();
                                window.URL.revokeObjectURL(url);
                                document.body.removeChild(a);
                              } catch (error) {
                                console.error('Download error:', error);
                                const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
                                alert(`Failed to download print service files: ${errorMessage}\n\nPlease check:\n1. Backend server is running\n2. android-print-service directory exists\n3. Check browser console for details`);
                              }
                            }}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                          >
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            Download Android Print Service
                          </button>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                            After downloading, extract the ZIP file on your Android device and follow the installation instructions in the README.md file.
                          </p>
                          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                            <a
                              href={`${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/android-print-service/installer`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline"
                            >
                              📱 Open Quick Install Guide (Mobile-Friendly)
                            </a>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="mt-6">
                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">
                      Receipt Logo
                    </h4>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Upload Logo Image
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          if (file.size > 10 * 1024 * 1024) { alert('Image size must be less than 10MB'); return; }
                          if (!file.type.startsWith('image/')) { alert('Please select a valid image file'); return; }
                          const res = await apiService.uploadImage(file, 'logos');
                          if (res.success && res.url) {
                            setPOSSettings({ ...posSettings, receiptLogoUrl: res.url });
                          } else {
                            alert(res.error || 'Error uploading image');
                          }
                        }}
                        className="mt-1 block w-full rounded-md border border-gray-300 dark:border-gray-600 shadow-sm py-2 px-3 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:file:bg-blue-900 dark:file:text-blue-300"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Upload your logo image. The logo will appear at the top of receipts. Recommended size: 200-300px width, max 2MB. Supported formats: PNG, JPG, GIF, WebP.
                      </p>
                      {posSettings.receiptLogoUrl && (
                        <div className="mt-3">
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-xs text-gray-600 dark:text-gray-400">Preview:</p>
                            <button
                              type="button"
                              onClick={() => setPOSSettings({ ...posSettings, receiptLogoUrl: '' })}
                              className="text-xs text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                            >
                              Remove Logo
                            </button>
                          </div>
                          <img 
                            src={resolveImageUrl(posSettings.receiptLogoUrl)} 
                            alt="Receipt Logo Preview" 
                            className="max-w-xs max-h-24 object-contain border border-gray-300 dark:border-gray-600 rounded"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mt-6">
                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">
                      Receipt Layout Designer
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                      Customize the layout and order of elements on your receipts. Drag and drop to reorder, enable/disable elements, and customize their appearance.
                    </p>
                    {posSettings.receiptLayout && Array.isArray(posSettings.receiptLayout) && posSettings.receiptLayout.length > 0 && (
                      <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                        <p className="text-sm text-green-800 dark:text-green-200">
                          ✓ Custom layout configured ({posSettings.receiptLayout.filter((el: any) => el.enabled).length} enabled elements)
                        </p>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setShowLayoutDesigner(true)}
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
                      >
                        <PencilIcon className="h-5 w-5" />
                        {posSettings.receiptLayout && Array.isArray(posSettings.receiptLayout) && posSettings.receiptLayout.length > 0 
                          ? 'Edit Layout Designer' 
                          : 'Open Layout Designer'}
                      </button>
                      {posSettings.receiptLayout && Array.isArray(posSettings.receiptLayout) && posSettings.receiptLayout.length > 0 && (
                        <button
                          onClick={() => {
                            if (confirm('Are you sure you want to reset the layout to default?')) {
                              setPOSSettings({ ...posSettings, receiptLayout: [] });
                            }
                          }}
                          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
                        >
                          Reset to Default
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      ⚠️ Remember to click "Save POS Settings" after designing your layout to apply changes.
                    </p>
                  </div>
                  
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-md font-medium text-gray-900 dark:text-white">
                        Visit-Based Offers
                      </h4>
                      <button
                        onClick={handleAddVisitOffer}
                        className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors flex items-center gap-2"
                      >
                        <PlusIcon className="w-5 h-5" />
                        Add Offer
                      </button>
                    </div>
                    
                    <div className="space-y-3">
                      {visitBasedOffers.length === 0 ? (
                        <p className="text-sm text-gray-500 dark:text-gray-400">No visit-based offers configured</p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-800">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Name</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Visit Count</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Type</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Value</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                              {visitBasedOffers.map((offer) => (
                                <tr key={offer.id}>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">{offer.name}</td>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">{offer.visit_count}</td>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                    {offer.offer_type === 'free_item' ? 'Free Item' : 
                                     offer.offer_type === 'percentage_discount' ? 'Percentage Discount' : 
                                     'Fixed Discount'}
                                  </td>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                    {offer.offer_type === 'free_item' 
                                      ? (offer.free_item?.name || 'N/A')
                                      : offer.offer_type === 'percentage_discount'
                                      ? `${offer.discount_value}%`
                                      : `${offer.discount_value} ${restaurantSettings.currency_symbol || 'SAR'}`}
                                  </td>
                                  <td className="px-4 py-3 whitespace-nowrap">
                                    <span className={`px-2 py-1 text-xs rounded-full ${offer.is_active ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'}`}>
                                      {offer.is_active ? 'Active' : 'Inactive'}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                                    <button
                                      onClick={() => handleEditVisitOffer(offer)}
                                      className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 mr-3"
                                    >
                                      <PencilIcon className="w-5 h-5" />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteVisitOffer(offer.id)}
                                      className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                                    >
                                      <TrashIcon className="w-5 h-5" />
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex justify-end">
                    <button
                      onClick={handleSavePOSSettings}
                      disabled={saving}
                      className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium py-2 px-4 rounded-lg transition-colors"
                    >
                      {saving ? 'Saving...' : 'Save POS Settings'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Customer Management */}
            {activeTab === 'customers' && (
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    Customer Field Configuration
                  </h3>
                  <button
                    onClick={handleAddField}
                    className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                  >
                    <PlusIcon className="h-4 w-4 mr-2" />
                    Add Field
                  </button>
                </div>
                
                <div className="space-y-6">
                  {/* Customer Fields Configuration */}
                  <div>
                    <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">
                      Configure Customer Fields
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                      Manage the fields that appear in the customer form in the POS. Only mobile number is required by default.
                    </p>
                    <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Field Name
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Type
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Required
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Active
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                          {customerFields.map((field) => (
                            <tr key={field.id}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                                {field.fieldLabel}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                {field.fieldType}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                {field.isRequired ? (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                                    Required
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
                                    Optional
                                  </span>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                {field.isActive ? (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                    Active
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
                                    Inactive
                                  </span>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-500 dark:text-gray-400">
                                <div className="flex space-x-2">
                                  <button
                                    onClick={() => handleEditField(field)}
                                    className="text-blue-600 hover:text-blue-500"
                                  >
                                    <PencilIcon className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteField(field.id)}
                                    className="text-red-600 hover:text-red-500"
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* HR Management */}
            {activeTab === 'hr' && (
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                      HR Management
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Manage leave types and HR settings
                    </p>
                  </div>
                </div>

                {/* Leave Types Section */}
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-md font-medium text-gray-900 dark:text-white flex items-center gap-2">
                      <CalendarIcon className="h-5 w-5" />
                      Leave Types
                    </h4>
                    <button
                      onClick={handleAddLeaveType}
                      className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                    >
                      <PlusIcon className="h-4 w-4" />
                      Add Leave Type
                    </button>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Leave Type</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Max Days/Year</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Paid</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Requires Approval</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Carry Forward</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {leaveTypes.length === 0 ? (
                          <tr>
                            <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                              No leave types found
                            </td>
                          </tr>
                        ) : (
                          leaveTypes.map((leaveType) => (
                            <tr key={leaveType.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                                {leaveType.leave_type_name}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                {leaveType.max_days_per_year}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                  leaveType.is_paid
                                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                    : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                }`}>
                                  {leaveType.is_paid ? 'Paid' : 'Unpaid'}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                  leaveType.requires_approval
                                    ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                    : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                                }`}>
                                  {leaveType.requires_approval ? 'Yes' : 'No'}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                  leaveType.carry_forward
                                    ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                                    : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                                }`}>
                                  {leaveType.carry_forward ? 'Yes' : 'No'}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                  leaveType.is_active
                                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                    : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                                }`}>
                                  {leaveType.is_active ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <div className="flex justify-end gap-2">
                                  <button
                                    onClick={() => handleEditLeaveType(leaveType)}
                                    className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                                    title="Edit"
                                  >
                                    <PencilIcon className="h-5 w-5" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteLeaveType(leaveType)}
                                    className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                                    title="Delete"
                                  >
                                    <TrashIcon className="h-5 w-5" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Payroll Settings Section */}
                <div className="mt-8">
                  <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">
                    Payroll Settings
                  </h4>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">
                          Allowance Calculation Method
                        </label>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {restaurantSettings.allowance_calculation_based_on_attendance
                            ? 'Allowances are calculated based on attendance rate (proportional to days present).'
                            : 'Allowances are paid in full regardless of attendance.'}
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer ml-4">
                        <input
                          type="checkbox"
                          checked={restaurantSettings.allowance_calculation_based_on_attendance ?? true}
                          onChange={(e) => setRestaurantSettings({ ...restaurantSettings, allowance_calculation_based_on_attendance: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Leave Type Modal */}
      {showLeaveTypeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {editingLeaveType ? 'Edit Leave Type' : 'Add Leave Type'}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Leave Type Name *
                </label>
                <input
                  type="text"
                  value={leaveTypeFormData.leave_type_name || ''}
                  onChange={(e) => setLeaveTypeFormData({ ...leaveTypeFormData, leave_type_name: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="e.g., Annual Leave"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Max Days Per Year *
                </label>
                <input
                  type="number"
                  min="0"
                  value={leaveTypeFormData.max_days_per_year || 0}
                  onChange={(e) => setLeaveTypeFormData({ ...leaveTypeFormData, max_days_per_year: parseInt(e.target.value) || 0 })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="30"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  value={leaveTypeFormData.description || ''}
                  onChange={(e) => setLeaveTypeFormData({ ...leaveTypeFormData, description: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Brief description of this leave type"
                  rows={3}
                />
              </div>

              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={leaveTypeFormData.is_paid !== false}
                    onChange={(e) => setLeaveTypeFormData({ ...leaveTypeFormData, is_paid: e.target.checked })}
                    className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Paid Leave</span>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      If unchecked, this leave type will deduct from salary (unpaid leave)
                    </p>
                  </div>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={leaveTypeFormData.requires_approval !== false}
                    onChange={(e) => setLeaveTypeFormData({ ...leaveTypeFormData, requires_approval: e.target.checked })}
                    className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Requires Approval</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={leaveTypeFormData.carry_forward || false}
                    onChange={(e) => setLeaveTypeFormData({ ...leaveTypeFormData, carry_forward: e.target.checked })}
                    className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Allow Carry Forward</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={leaveTypeFormData.is_active !== false}
                    onChange={(e) => setLeaveTypeFormData({ ...leaveTypeFormData, is_active: e.target.checked })}
                    className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Active</span>
                </label>
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => setShowLeaveTypeModal(false)}
                className="flex-1 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveLeaveType}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                {editingLeaveType ? 'Update Leave Type' : 'Add Leave Type'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Customer Field Modal */}
      {showFieldModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {editingField ? 'Edit Field' : 'Add Field'}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Field Name *
                </label>
                <input
                  type="text"
                  value={fieldFormData.fieldName || ''}
                  onChange={(e) => setFieldFormData({ ...fieldFormData, fieldName: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="e.g., first_name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Field Label *
                </label>
                <input
                  type="text"
                  value={fieldFormData.fieldLabel || ''}
                  onChange={(e) => setFieldFormData({ ...fieldFormData, fieldLabel: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="e.g., First Name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Field Type *
                </label>
                <select
                  value={fieldFormData.fieldType || 'text'}
                  onChange={(e) => setFieldFormData({ ...fieldFormData, fieldType: e.target.value as any })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="text">Text</option>
                  <option value="email">Email</option>
                  <option value="number">Number</option>
                  <option value="date">Date</option>
                  <option value="select">Select</option>
                  <option value="textarea">Textarea</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Sort Order
                </label>
                <input
                  type="number"
                  value={fieldFormData.sortOrder || 0}
                  onChange={(e) => setFieldFormData({ ...fieldFormData, sortOrder: parseInt(e.target.value) || 0 })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={fieldFormData.isRequired || false}
                    onChange={(e) => setFieldFormData({ ...fieldFormData, isRequired: e.target.checked })}
                    className="mr-3"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Required field</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={fieldFormData.isActive !== false}
                    onChange={(e) => setFieldFormData({ ...fieldFormData, isActive: e.target.checked })}
                    className="mr-3"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Active</span>
                </label>
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowFieldModal(false)
                  setEditingField(null)
                  setFieldFormData({})
                }}
                className="flex-1 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveField}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                {editingField ? 'Update Field' : 'Add Field'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* KDS Station Modal */}
      {showKDSModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4 my-8">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {editingKDS ? 'Edit KDS Station' : 'Add KDS Station'}
            </h3>
            
            <div className="space-y-4 max-h-[calc(100vh-200px)] overflow-y-auto pr-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Station Name
                </label>
                <input
                  type="text"
                  value={kdsFormData.name || ''}
                  onChange={(e) => setKdsFormData({ ...kdsFormData, name: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="e.g., Main Kitchen"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  value={kdsFormData.description || ''}
                  onChange={(e) => setKdsFormData({ ...kdsFormData, description: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Brief description of this station"
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Station Color
                </label>
                <div className="flex items-center space-x-3">
                  <input
                    type="color"
                    value={kdsFormData.color || '#3B82F6'}
                    onChange={(e) => setKdsFormData({ ...kdsFormData, color: e.target.value })}
                    className="w-12 h-10 border border-gray-300 dark:border-gray-600 rounded-md"
                  />
                  <input
                    type="text"
                    value={kdsFormData.color || '#3B82F6'}
                    onChange={(e) => setKdsFormData({ ...kdsFormData, color: e.target.value })}
                    className="flex-1 border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="#3B82F6"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Sort Order
                </label>
                <input
                  type="number"
                  value={kdsFormData.sort_order || 0}
                  onChange={(e) => setKdsFormData({ ...kdsFormData, sort_order: parseInt(e.target.value) || 0 })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="0"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={kdsFormData.is_active || false}
                  onChange={(e) => setKdsFormData({ ...kdsFormData, is_active: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                  Active Station
                </label>
              </div>

              <div className="border-t border-gray-300 dark:border-gray-600 pt-4 mt-4">
                <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3">
                  Printer Configuration (TCP/IP)
                </h4>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Printer IP Address
                    </label>
                    <input
                      type="text"
                      value={kdsFormData.printer_ip || ''}
                      onChange={(e) => setKdsFormData({ ...kdsFormData, printer_ip: e.target.value })}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="192.168.1.100"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Printer Port
                    </label>
                    <input
                      type="number"
                      value={kdsFormData.printer_port || 9100}
                      onChange={(e) => setKdsFormData({ ...kdsFormData, printer_port: parseInt(e.target.value) || 9100 })}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="9100"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Printer Name (Optional)
                    </label>
                    <input
                      type="text"
                      value={kdsFormData.printer_name || ''}
                      onChange={(e) => setKdsFormData({ ...kdsFormData, printer_name: e.target.value })}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="Kitchen Printer"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Printer Width (mm)
                    </label>
                    <input
                      type="number"
                      value={kdsFormData.printer_width || 80}
                      onChange={(e) => setKdsFormData({ ...kdsFormData, printer_width: parseInt(e.target.value) || 80 })}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="80"
                    />
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="enable_auto_print"
                      checked={kdsFormData.enable_auto_print || false}
                      onChange={(e) => setKdsFormData({ ...kdsFormData, enable_auto_print: e.target.checked })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="enable_auto_print" className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                      Enable Auto-Print (Automatically print tickets when items arrive at this station)
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex space-x-3 pt-4">
              <button
                onClick={() => setShowKDSModal(false)}
                className="flex-1 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveKDS}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                {editingKDS ? 'Update Station' : 'Add Station'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Visit-Based Offer Modal */}
      {showVisitOfferModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl mx-4 my-8">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {editingVisitOffer ? 'Edit Visit-Based Offer' : 'Add Visit-Based Offer'}
            </h3>
            
            <div className="space-y-4 max-h-[70vh] overflow-y-auto">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Offer Name *
                </label>
                <input
                  type="text"
                  value={visitOfferFormData.name || ''}
                  onChange={(e) => setVisitOfferFormData({ ...visitOfferFormData, name: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="e.g., 5th Visit Reward"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  value={visitOfferFormData.description || ''}
                  onChange={(e) => setVisitOfferFormData({ ...visitOfferFormData, description: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Offer description"
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Visit Count Required *
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={visitOfferFormData.visit_count || 1}
                    onChange={(e) => setVisitOfferFormData({ ...visitOfferFormData, visit_count: parseInt(e.target.value) || 1 })}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Minimum Order Amount
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={visitOfferFormData.min_order_amount || 0}
                    onChange={(e) => setVisitOfferFormData({ ...visitOfferFormData, min_order_amount: parseFloat(e.target.value) || 0 })}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Offer Type *
                </label>
                <select
                  value={visitOfferFormData.offer_type || 'free_item'}
                  onChange={(e) => setVisitOfferFormData({ ...visitOfferFormData, offer_type: e.target.value, free_item_id: e.target.value !== 'free_item' ? null : visitOfferFormData.free_item_id, discount_value: e.target.value === 'free_item' ? 0 : visitOfferFormData.discount_value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="free_item">Free Item</option>
                  <option value="percentage_discount">Percentage Discount</option>
                  <option value="fixed_discount">Fixed Discount</option>
                </select>
              </div>

              {visitOfferFormData.offer_type === 'free_item' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Free Item *
                  </label>
                  <select
                    value={visitOfferFormData.free_item_id || ''}
                    onChange={(e) => setVisitOfferFormData({ ...visitOfferFormData, free_item_id: e.target.value || null })}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">Select a product</option>
                    {products.map((product) => (
                      <option key={product.id} value={product.id}>
                        {product.name} - {restaurantSettings.currency_symbol || 'SAR'} {product.basePrice || 0}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {(visitOfferFormData.offer_type === 'percentage_discount' || visitOfferFormData.offer_type === 'fixed_discount') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {visitOfferFormData.offer_type === 'percentage_discount' ? 'Discount Percentage *' : 'Discount Amount *'}
                  </label>
                  <input
                    type="number"
                    min="0"
                    step={visitOfferFormData.offer_type === 'percentage_discount' ? '1' : '0.01'}
                    max={visitOfferFormData.offer_type === 'percentage_discount' ? '100' : undefined}
                    value={visitOfferFormData.discount_value || 0}
                    onChange={(e) => setVisitOfferFormData({ ...visitOfferFormData, discount_value: parseFloat(e.target.value) || 0 })}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                  {visitOfferFormData.offer_type === 'percentage_discount' && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Enter percentage (0-100)</p>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={visitOfferFormData.start_date || ''}
                    onChange={(e) => setVisitOfferFormData({ ...visitOfferFormData, start_date: e.target.value })}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={visitOfferFormData.end_date || ''}
                    onChange={(e) => setVisitOfferFormData({ ...visitOfferFormData, end_date: e.target.value })}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Max Uses Per Customer (leave empty for unlimited)
                </label>
                <input
                  type="number"
                  min="1"
                  value={visitOfferFormData.max_uses_per_customer || ''}
                  onChange={(e) => setVisitOfferFormData({ ...visitOfferFormData, max_uses_per_customer: e.target.value ? parseInt(e.target.value) : null })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Unlimited"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="visit_offer_is_active"
                  checked={visitOfferFormData.is_active !== false}
                  onChange={(e) => setVisitOfferFormData({ ...visitOfferFormData, is_active: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="visit_offer_is_active" className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                  Active Offer
                </label>
              </div>
            </div>

            <div className="flex space-x-3 pt-4">
              <button
                onClick={() => setShowVisitOfferModal(false)}
                className="flex-1 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveVisitOffer}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                {editingVisitOffer ? 'Update Offer' : 'Create Offer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Expense Category Modal */}
      {showExpenseCategoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {editingExpenseCategory ? 'Edit Expense Category' : 'Add Expense Category'}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Category Name *
                </label>
                <input
                  type="text"
                  value={expenseCategoryFormData.category_name || ''}
                  onChange={(e) => setExpenseCategoryFormData({ ...expenseCategoryFormData, category_name: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="e.g., Utilities, Rent, Marketing"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Chart of Account (Optional)
                </label>
                <select
                  value={expenseCategoryFormData.account_id || ''}
                  onChange={(e) => setExpenseCategoryFormData({ ...expenseCategoryFormData, account_id: e.target.value || null })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Select an account</option>
                  {chartOfAccounts.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.account_code} - {account.account_name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  value={expenseCategoryFormData.description || ''}
                  onChange={(e) => setExpenseCategoryFormData({ ...expenseCategoryFormData, description: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Category description"
                  rows={3}
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="expense_category_is_active"
                  checked={expenseCategoryFormData.is_active !== false}
                  onChange={(e) => setExpenseCategoryFormData({ ...expenseCategoryFormData, is_active: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="expense_category_is_active" className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                  Active Category
                </label>
              </div>
            </div>

            <div className="flex space-x-3 pt-4">
              <button
                onClick={() => setShowExpenseCategoryModal(false)}
                className="flex-1 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveExpenseCategory}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                {editingExpenseCategory ? 'Update Category' : 'Create Category'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Location Modal */}
      {showLocationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {editingLocation ? 'Edit Location' : 'Add Location'}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Location Name *
                </label>
                <input
                  type="text"
                  value={locationFormData.name || ''}
                  onChange={(e) => setLocationFormData({ ...locationFormData, name: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="e.g., Kitchen, Storage Room, Cabinet 1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Parent Location (Optional)
                </label>
                <select
                  value={locationFormData.parent_id || ''}
                  onChange={(e) => setLocationFormData({ ...locationFormData, parent_id: e.target.value || null })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Main Location (No Parent)</option>
                  {getRootLocations(inventoryLocations, editingLocation?.id).map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.name}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Select a parent location to create a sub-location (e.g., "Cabinet 1" under "Kitchen")
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  value={locationFormData.description || ''}
                  onChange={(e) => setLocationFormData({ ...locationFormData, description: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Location description"
                  rows={3}
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="location_is_active"
                  checked={locationFormData.is_active !== false}
                  onChange={(e) => setLocationFormData({ ...locationFormData, is_active: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="location_is_active" className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                  Active Location
                </label>
              </div>
            </div>

            <div className="flex space-x-3 pt-4">
              <button
                onClick={() => {
                  setShowLocationModal(false)
                  setEditingLocation(null)
                }}
                className="flex-1 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveLocation}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                {editingLocation ? 'Update Location' : 'Create Location'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Inventory Category Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {editingCategory ? 'Edit Category' : 'Add Category'}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Category Name *
                </label>
                <input
                  type="text"
                  value={categoryFormData.name || ''}
                  onChange={(e) => setCategoryFormData({ ...categoryFormData, name: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="e.g., Meat, Vegetables, Pantry"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  value={categoryFormData.description || ''}
                  onChange={(e) => setCategoryFormData({ ...categoryFormData, description: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Category description"
                  rows={3}
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="category_is_active"
                  checked={categoryFormData.is_active !== false}
                  onChange={(e) => setCategoryFormData({ ...categoryFormData, is_active: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="category_is_active" className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                  Active Category
                </label>
              </div>
            </div>

            <div className="flex space-x-3 pt-4">
              <button
                onClick={() => {
                  setShowCategoryModal(false)
                  setEditingCategory(null)
                }}
                className="flex-1 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveCategory}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                {editingCategory ? 'Update Category' : 'Create Category'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table Location Modal */}
      {showTableLocationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {selectedTableLocation ? 'Edit Location' : 'Add Location'}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  value={newTableLocation.name || ''}
                  onChange={(e) => setNewTableLocation({ ...newTableLocation, name: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="e.g., Indoor, Outdoor, Bar"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  value={newTableLocation.description || ''}
                  onChange={(e) => setNewTableLocation({ ...newTableLocation, description: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  rows={3}
                  placeholder="Optional description"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Color
                  </label>
                  <input
                    type="color"
                    value={newTableLocation.color || '#3B82F6'}
                    onChange={(e) => setNewTableLocation({ ...newTableLocation, color: e.target.value })}
                    className="w-full h-10 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Sort Order
                  </label>
                  <input
                    type="number"
                    value={newTableLocation.sort_order || 0}
                    onChange={(e) => setNewTableLocation({ ...newTableLocation, sort_order: Number(e.target.value) })}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="table_location_is_active"
                  checked={newTableLocation.is_active !== false}
                  onChange={(e) => setNewTableLocation({ ...newTableLocation, is_active: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="table_location_is_active" className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                  Active
                </label>
              </div>
            </div>

            <div className="flex space-x-3 pt-4">
              <button
                onClick={() => {
                  setShowTableLocationModal(false)
                  setSelectedTableLocation(null)
                  setNewTableLocation({ name: '', description: '', color: '#3B82F6', sort_order: 0, is_active: true })
                }}
                className="flex-1 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTableLocation}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                {selectedTableLocation ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Allergen Add/Edit Modal */}
      {showAllergenModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {editingAllergen ? 'Edit Allergen' : 'Add Allergen'}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Name *</label>
                <input
                  type="text"
                  value={allergenForm.name}
                  onChange={(e) => setAllergenForm({ ...allergenForm, name: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="e.g. Gluten, Dairy"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Icon</label>
                {allergenForm.icon && (
                  <div className="mb-2">
                    <img src={resolveImageUrl(allergenForm.icon)} alt="" className="w-12 h-12 object-contain rounded border border-gray-200 dark:border-gray-600" />
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={async (e) => {
                    const file = e.target.files?.[0]
                    if (!file) return
                    const res = await apiService.uploadImage(file, 'allergens')
                    if (res.success && res.url) setAllergenForm({ ...allergenForm, icon: res.url })
                  }}
                  className="w-full text-sm text-gray-900 dark:text-gray-100"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Small icon shown in POS, Waiter, and customer menu</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Sort order</label>
                <input
                  type="number"
                  value={allergenForm.sort_order}
                  onChange={(e) => setAllergenForm({ ...allergenForm, sort_order: Number(e.target.value) || 0 })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowAllergenModal(false)}
                className="flex-1 bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 text-gray-800 dark:text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveAllergen}
                disabled={allergenSaving}
                className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                {allergenSaving ? 'Saving...' : editingAllergen ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floor Plan Modal */}
      {showFloorPlanModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {selectedFloorPlan ? 'Edit Floor Plan' : 'Add Floor Plan'}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  value={newFloorPlan.name || ''}
                  onChange={(e) => setNewFloorPlan({ ...newFloorPlan, name: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="e.g., Ground Floor, First Floor, Mezzanine"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  value={newFloorPlan.description || ''}
                  onChange={(e) => setNewFloorPlan({ ...newFloorPlan, description: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  rows={2}
                  placeholder="Optional description"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Level
                  </label>
                  <input
                    type="number"
                    value={newFloorPlan.level || 0}
                    onChange={(e) => setNewFloorPlan({ ...newFloorPlan, level: Number(e.target.value) })}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="0 = ground floor"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">0 = ground, +1 = first floor, -1 = basement</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Sort Order
                  </label>
                  <input
                    type="number"
                    value={newFloorPlan.sort_order || 0}
                    onChange={(e) => setNewFloorPlan({ ...newFloorPlan, sort_order: Number(e.target.value) })}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Width (px)
                  </label>
                  <input
                    type="number"
                    value={newFloorPlan.width || 2000}
                    onChange={(e) => setNewFloorPlan({ ...newFloorPlan, width: Number(e.target.value) })}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Height (px)
                  </label>
                  <input
                    type="number"
                    value={newFloorPlan.height || 1500}
                    onChange={(e) => setNewFloorPlan({ ...newFloorPlan, height: Number(e.target.value) })}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="floor_plan_is_active"
                  checked={newFloorPlan.is_active !== false}
                  onChange={(e) => setNewFloorPlan({ ...newFloorPlan, is_active: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="floor_plan_is_active" className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                  Active
                </label>
              </div>
            </div>

            <div className="flex space-x-3 pt-4">
              <button
                onClick={() => {
                  setShowFloorPlanModal(false)
                  setSelectedFloorPlan(null)
                  setNewFloorPlan({ name: '', level: 0, description: '', width: 2000, height: 1500, sort_order: 0, is_active: true })
                }}
                className="flex-1 bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveFloorPlan}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                {selectedFloorPlan ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Receipt Layout Designer Modal */}
      {showLayoutDesigner && (
        <ReceiptLayoutDesigner
          layout={posSettings.receiptLayout || []}
          onLayoutChange={(layout) => {

            setPOSSettings({ ...posSettings, receiptLayout: layout })
          }}
          onClose={() => {

            if (posSettings.receiptLayout && Array.isArray(posSettings.receiptLayout) && posSettings.receiptLayout.length > 0) {
              // Receipt layout is configured
            }
            setShowLayoutDesigner(false);
          }}
        />
      )}

      {/* Notification Modal */}
      {showNotificationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-start">
              <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                notificationMessage.type === 'success' 
                  ? 'bg-green-100 dark:bg-green-900' 
                  : 'bg-red-100 dark:bg-red-900'
              }`}>
                {notificationMessage.type === 'success' ? (
                  <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
              </div>
              <div className="ml-4 flex-1">
                <h3 className={`text-lg font-medium ${
                  notificationMessage.type === 'success'
                    ? 'text-green-800 dark:text-green-200'
                    : 'text-red-800 dark:text-red-200'
                }`}>
                  {notificationMessage.type === 'success' ? 'Success' : 'Error'}
                </h3>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  {notificationMessage.message}
                </p>
              </div>
              <button
                onClick={() => setShowNotificationModal(false)}
                className="ml-4 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowNotificationModal(false)}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  notificationMessage.type === 'success'
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'bg-red-600 hover:bg-red-700 text-white'
                }`}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      {showConfirmModal && confirmModalConfig && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-start">
              <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-yellow-100 dark:bg-yellow-900">
                <svg className="w-6 h-6 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="ml-4 flex-1">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Confirm Action
                </h3>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  {confirmModalConfig.message}
                </p>
              </div>
              <button
                onClick={() => setShowConfirmModal(false)}
                className="ml-4 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  confirmModalConfig.onConfirm()
                  setConfirmModalConfig(null)
                }}
                className={`px-4 py-2 text-white rounded-md text-sm font-medium ${
                  confirmModalConfig.confirmButtonColor === 'green'
                    ? 'bg-green-600 hover:bg-green-700'
                    : confirmModalConfig.confirmButtonColor === 'blue'
                    ? 'bg-blue-600 hover:bg-blue-700'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {confirmModalConfig.confirmButtonText || 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Settings
